import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { onAuthStateChanged } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { deleteObject, getDownloadURL, getStorage, listAll, ref as storageRef } from "firebase/storage";
import { auth, db } from "../../firebase";
import jobSingleIcon from "../../assets/tallarge_job_single_256.png";
import jobInspectionIcon from "../../assets/tallarge_job_inspection_256.png";
import jobMultiIcon from "../../assets/tallarge_job_multi_256.png";
import CreateInternalFleetJob from "./CreateInternalFleetJob";
import CreateInspectionFleetJob from "./CreateInspectionFleetJob";
import CreateFleetLogisticsJob from "./CreateFleetLogisticsJob";
import QuickCreateFleetLogisticsJob from "./QuickCreateFleetLogisticsJob";
import QuickCreateFleetVehicleStep from "./QuickCreateFleetVehicleStep";
import QuickCreateFleetClusteringStep from "./QuickCreateFleetClusteringStep";
import DriverManagerSetting from "./DriverManagerSetting";
import DriverManagerDrivers from "./DriverManagerDrivers";
import DriverManagerReports from "./DriverManagerReports";
import DriverManagerCargoManagement, { makeCargoCode } from "./DriverManagerCargoManagement";
import DriverManagerCompanyAccounts from "./DriverManagerCompanyAccounts";
import DriverManagerDeliveryDetails from "./DriverManagerDeliveryDetails";
import DriverManagerJobList from "./DriverManagerJobList";
import DriverManagerJobDetail from "./DriverManagerJobDetail";
import DriverManagerLogisticsJobDetail from "./DriverManagerLogisticsJobDetail";
import DriverManagerChatList from "./DriverManagerChatList";
import DriverManagerChatRoom from "./DriverManagerChatRoom";
import { subscribeToWebChatPushMessages } from "./DriverManagerChatPush";
import DriverManagerCommonInfo from "./DriverManagerCommonInfo";
import DriverManagerCars from "./DriverManagerCars";
import {
  DRIVER_MANAGER_SIDEBAR_EXPANDED_WIDTH,
  DriverManagerSidebar,
} from "./DriverManagerSidebar";
import {
  INTERNAL_ENTRY_BASE,
  INTERNAL_ROUTES,
  internalPathForFeature,
} from "../../internalRoutes";

const TOKENS = {
  pageBg: "#F3F4F8",
  white: "#FFFFFF",
  text: "#111827",
  textStrong: "#0F172A",
  textSoft: "#6B7280",
  textMuted: "#94A3B8",

  line: "rgba(15,23,42,0.08)",
  lineSoft: "rgba(15,23,42,0.06)",
  shadow: "0 14px 34px rgba(15,23,42,0.08)",
  shadowSoft: "0 10px 24px rgba(15,23,42,0.06)",
  shadowCard: "0 8px 20px rgba(15,23,42,0.05)",

  orange: "#F68B2C",
  orangeDeep: "#E9781E",
  orangeSoft: "rgba(246,139,44,0.12)",

  green: "#31B66A",
  greenSoft: "rgba(49,182,106,0.14)",

  blue: "#3B82F6",
  blueSoft: "rgba(59,130,246,0.14)",

  red: "#EF4444",
  redSoft: "rgba(239,68,68,0.14)",

  yellow: "#F4B400",
  yellowSoft: "rgba(244,180,0,0.16)",

};

const MENU_ROUTE_KEYS = {
  overview: null,
  internalJob: "job",
  drivers: "driver",
  chats: "chat",
  commonInfo: "common",
  fleet: "vehicle",
  cargoManagement: null,
  reports: "report",
  companyAccounts: "company",
  settings: "settings",
};

const HISTORY_VIEW_KEY = "__tallargeDriverManagerView";
const TRACKING_HISTORY_API_URL = "https://us-central1-chat3test-59045.cloudfunctions.net/api/tracking/history";

function collectJobAssetIds(value, ids = new Set()) {
  if (!value || typeof value !== "object") return ids;
  if (Array.isArray(value)) {
    value.forEach((item) => collectJobAssetIds(item, ids));
    return ids;
  }
  if (value.assetId) ids.add(mapText(value.assetId));
  Object.values(value).forEach((item) => collectJobAssetIds(item, ids));
  return ids;
}

async function deleteStorageTree(storage, path) {
  let result;
  try {
    result = await listAll(storageRef(storage, path));
  } catch (error) {
    if (error?.code === "storage/object-not-found") return;
    throw error;
  }
  await Promise.allSettled(result.items.map((item) => deleteObject(item)));
  await Promise.all(result.prefixes.map((prefix) => deleteStorageTree(storage, prefix.fullPath)));
}

async function commitDeleteBatch(refs, operation) {
  for (let index = 0; index < refs.length; index += 450) {
    const batch = writeBatch(db);
    refs.slice(index, index + 450).forEach((ref) => operation(batch, ref));
    await batch.commit();
  }
}

const CARGO_INVENTORY_TARGET_BYTES = 700000;

function cargoInventoryMetaRef(orgId) {
  return doc(db, "orgs", orgId, "cargoInventory", "meta");
}

function cargoInventoryChunkRef(orgId, chunkId) {
  return doc(db, "orgs", orgId, "cargoInventory", "meta", "chunks", chunkId);
}

function cargoInventoryCodeRef(orgId, code) {
  return doc(db, "orgs", orgId, "cargoInventory", "meta", "codes", code);
}

function cargoInventoryChunkSequence(chunkId) {
  const match = String(chunkId || "").match(/chunk_(\d+)/i);
  return match ? Number(match[1]) : 0;
}

function nextCargoInventorySequence(meta, chunkIds) {
  const highest = chunkIds.reduce(
    (value, chunkId) => Math.max(value, cargoInventoryChunkSequence(chunkId)),
    0,
  );
  return Math.max(Number(meta?.nextChunkSequence) || 1, highest + 1);
}

function cargoInventoryItemsBytes(items) {
  const json = JSON.stringify(items, (key, value) =>
    ["createdAt", "updatedAt", "assignedAt", "contentIdUpdatedAt"].includes(key) ? null : value,
  );
  return new TextEncoder().encode(json).length;
}

function splitCargoInventoryItems(items) {
  const chunks = [];
  let current = {};
  Object.entries(items).forEach(([cargoId, item]) => {
    const candidate = { ...current, [cargoId]: item };
    if (Object.keys(current).length && cargoInventoryItemsBytes(candidate) > CARGO_INVENTORY_TARGET_BYTES) {
      chunks.push(current);
      current = { [cargoId]: item };
    } else {
      current = candidate;
    }
    if (cargoInventoryItemsBytes(current) > CARGO_INVENTORY_TARGET_BYTES) {
      throw new Error("更新後的單筆貨物資料超過分片安全容量，請先縮短備註或地址。");
    }
  });
  if (Object.keys(current).length) chunks.push(current);
  return chunks;
}

async function markCargoInventoryAssigned({ orgId, cargoRefs, applicationId, dateKey }) {
  const safeOrgId = mapText(orgId);
  const safeApplicationId = mapText(applicationId);
  const safeDateKey = mapText(dateKey);
  const selectedByChunk = new Map();
  (Array.isArray(cargoRefs) ? cargoRefs : []).forEach((source) => {
    const cargoId = mapText(source?.cargoId);
    const chunkId = mapText(source?.chunkId);
    if (!cargoId || !chunkId) return;
    const expectedContentId = mapText(source?.expectedContentId || source?.contentId) || cargoId;
    const workContentId = mapText(source?.workContentId || source?.contentId) || expectedContentId;
    if (!selectedByChunk.has(chunkId)) selectedByChunk.set(chunkId, new Map());
    const byCargoId = selectedByChunk.get(chunkId);
    const existingRef = byCargoId.get(cargoId);
    if (existingRef && (
      existingRef.expectedContentId !== expectedContentId ||
      existingRef.workContentId !== workContentId
    )) {
      throw new Error(`貨物 ${cargoId} 的工作編號資料不一致，請重新建立工作。`);
    }
    byCargoId.set(cargoId, { expectedContentId, workContentId });
  });
  if (!safeOrgId || !safeApplicationId || !safeDateKey || !selectedByChunk.size) {
    throw new Error("缺少組織、工作或貨物識別資料，無法更新派單狀態。");
  }

  const requestedWorkContentIds = new Map();
  selectedByChunk.forEach((byCargoId) => byCargoId.forEach((source, cargoId) => {
    const existingCargoId = requestedWorkContentIds.get(source.workContentId);
    if (existingCargoId && existingCargoId !== cargoId) {
      throw new Error(`本次工作有重複的 contentId：${source.workContentId}。`);
    }
    requestedWorkContentIds.set(source.workContentId, cargoId);
  }));

  await runTransaction(db, async (transaction) => {
    const metaRef = cargoInventoryMetaRef(safeOrgId);
    const metaSnapshot = await transaction.get(metaRef);
    if (!metaSnapshot.exists()) throw new Error("貨物管理分片索引不存在，請重新載入後再試。");
    const meta = metaSnapshot.data() || {};
    const rawChunkIds = Array.isArray(meta.chunkIds) ? meta.chunkIds : [];
    let chunkIds = [...new Set(rawChunkIds.map(mapText).filter(Boolean))];
    const sourceSnapshots = {};
    for (const chunkId of selectedByChunk.keys()) {
      if (!chunkIds.includes(chunkId)) throw new Error("部分貨物分片已更新，工作已建立但貨物狀態尚未同步。");
      const snapshot = await transaction.get(cargoInventoryChunkRef(safeOrgId, chunkId));
      if (!snapshot.exists()) throw new Error("部分貨物分片已不存在，工作已建立但貨物狀態尚未同步。");
      sourceSnapshots[chunkId] = snapshot;
    }

    let nextSequence = nextCargoInventorySequence(meta, chunkIds);
    const preparedChunks = [];
    const contentCodeWrites = new Map();
    const reservedContentIds = new Set();
    selectedByChunk.forEach((byCargoId) => byCargoId.forEach((source) => {
      reservedContentIds.add(source.expectedContentId);
      reservedContentIds.add(source.workContentId);
    }));

    for (const [chunkId, cargoRefsById] of selectedByChunk.entries()) {
      const snapshot = sourceSnapshots[chunkId];
      const sourceChunk = snapshot.data() || {};
      const items = { ...(sourceChunk.items || {}) };
      for (const [cargoId, source] of cargoRefsById.entries()) {
        const item = items[cargoId];
        if (!item) throw new Error(`找不到貨物 ${cargoId}，工作已建立但貨物狀態尚未同步。`);
        const currentContentId = mapText(item.contentId) || cargoId;
        const expectedContentId = mapText(source.expectedContentId) || cargoId;
        const workContentId = mapText(source.workContentId) || expectedContentId;

        if (
          mapText(item.lastDispatchedApplicationId) === safeApplicationId &&
          mapText(item.lastDispatchedDateKey) === safeDateKey &&
          mapText(item.lastDispatchedContentId) === workContentId
        ) continue;

        if (currentContentId !== expectedContentId) {
          throw new Error(`貨物 ${cargoId} 的 contentId 已更新，工作已建立但貨物狀態尚未同步。請重新整理貨物管理確認。`);
        }
        const assignmentStatus = mapText(item.assignmentStatus || "UNASSIGNED");
        if (assignmentStatus !== "UNASSIGNED" && currentContentId === cargoId && workContentId === currentContentId) {
          throw new Error(`已派單貨物 ${cargoId} 必須使用新的 contentId，避免與舊工作重複。`);
        }

        const workCodeSnapshot = await transaction.get(cargoInventoryCodeRef(safeOrgId, workContentId));
        if (workContentId !== currentContentId && workCodeSnapshot.exists()) {
          throw new Error(`本次工作 contentId ${workContentId} 已被使用，請重新建立工作。`);
        }
        if (workCodeSnapshot.exists()) {
          const codeOwner = mapText(workCodeSnapshot.data()?.cargoId);
          if (codeOwner && codeOwner !== cargoId) {
            throw new Error(`本次工作 contentId ${workContentId} 已被其他貨物使用。`);
          }
        } else {
          const priorReservation = contentCodeWrites.get(workContentId);
          if (priorReservation && mapText(priorReservation.cargoId) !== cargoId) {
            throw new Error(`本次工作 contentId ${workContentId} 重複，請重新建立工作。`);
          }
          contentCodeWrites.set(workContentId, {
            cargoId,
            contentId: workContentId,
            codeType: "CONTENT_ID",
            source: "cargoInventory",
            applicationId: safeApplicationId,
            dateKey: safeDateKey,
            createdAt: serverTimestamp(),
          });
        }

        let nextContentId = "";
        for (let attempt = 0; attempt < 20 && !nextContentId; attempt += 1) {
          const candidate = makeCargoCode();
          if (reservedContentIds.has(candidate) || contentCodeWrites.has(candidate)) continue;
          const candidateSnapshot = await transaction.get(cargoInventoryCodeRef(safeOrgId, candidate));
          if (candidateSnapshot.exists()) continue;
          nextContentId = candidate;
          reservedContentIds.add(candidate);
          contentCodeWrites.set(candidate, {
            cargoId,
            contentId: candidate,
            codeType: "CONTENT_ID",
            source: "cargoInventory",
            createdAt: serverTimestamp(),
          });
        }
        if (!nextContentId) throw new Error("無法產生唯一 contentId，請重新建立工作。");

        items[cargoId] = {
          ...item,
          contentId: nextContentId,
          contentIdUpdatedAt: serverTimestamp(),
          lastDispatchedContentId: workContentId,
          lastDispatchedApplicationId: safeApplicationId,
          lastDispatchedDateKey: safeDateKey,
          assignmentStatus: "ASSIGNED",
          assignedApplicationId: safeApplicationId,
          assignedDateKey: safeDateKey,
          assignedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
      }

      const itemChunks = splitCargoInventoryItems(items);
      const ids = [chunkId];
      for (let index = 1; index < itemChunks.length; index += 1) {
        let newChunkId = "";
        while (!newChunkId) {
          const candidateId = `chunk_${String(nextSequence++).padStart(6, "0")}`;
          const candidateSnapshot = await transaction.get(cargoInventoryChunkRef(safeOrgId, candidateId));
          if (!candidateSnapshot.exists()) newChunkId = candidateId;
        }
        ids.push(newChunkId);
      }
      preparedChunks.push({ chunkId, sourceChunk, itemChunks, ids });
    }

    const preparedById = new Map(preparedChunks.map((chunk) => [chunk.chunkId, chunk]));
    const nextChunkIds = [];
    chunkIds.forEach((chunkId) => {
      const prepared = preparedById.get(chunkId);
      nextChunkIds.push(...(prepared ? prepared.ids : [chunkId]));
    });
    for (const [contentId, codeData] of contentCodeWrites.entries()) {
      transaction.set(cargoInventoryCodeRef(safeOrgId, contentId), codeData, { merge: true });
    }
    for (const prepared of preparedChunks) {
      prepared.itemChunks.forEach((items, index) => {
        const chunkId = prepared.ids[index];
        const sequence = cargoInventoryChunkSequence(chunkId) || nextCargoInventorySequence(meta, nextChunkIds);
        transaction.set(cargoInventoryChunkRef(safeOrgId, chunkId), {
          chunkId,
          sequence,
          itemCount: Object.keys(items).length,
          estimatedBytes: cargoInventoryItemsBytes(items),
          targetBytes: CARGO_INVENTORY_TARGET_BYTES,
          items,
          createdAt: prepared.sourceChunk.createdAt || serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });
    }
    const currentActiveChunkId = mapText(meta.activeChunkId);
    const activePreparedChunk = preparedById.get(currentActiveChunkId);
    const nextActiveChunkId = activePreparedChunk?.ids.length > 1
      ? activePreparedChunk.ids[activePreparedChunk.ids.length - 1]
      : nextChunkIds.includes(currentActiveChunkId)
        ? currentActiveChunkId
        : nextChunkIds[nextChunkIds.length - 1] || null;
    transaction.set(metaRef, {
      chunkIds: nextChunkIds,
      chunkCount: nextChunkIds.length,
      activeChunkId: nextActiveChunkId,
      targetBytes: CARGO_INVENTORY_TARGET_BYTES,
      nextChunkSequence: nextSequence,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  });
}
function reportDayFromStartAt(value, fallback) {
  if (!value) return fallback;
  const date = typeof value?.toDate === "function"
    ? value.toDate()
    : value instanceof Date
      ? value
      : new Date(value);
  return Number.isNaN(date.getTime()) ? fallback : mapDateKey(date);
}

async function deleteJobWithDependencies({ orgId, dateKey, applicationId }) {
  const storage = getStorage();
  const applicationRef = doc(db, "orgs", orgId, "applications", dateKey, "jobs", applicationId);
  const applicationSnapshot = await getDoc(applicationRef);
  if (!applicationSnapshot.exists()) throw new Error("找不到要刪除的工作資料。");
  const application = applicationSnapshot.data() || {};

  const applicationChunkIds = Array.isArray(application.stopStorage?.chunkIds)
    ? application.stopStorage.chunkIds.map(mapText).filter(Boolean)
    : [];
  const applicationChunkRefs = applicationChunkIds.map((chunkId) =>
    doc(db, "orgs", orgId, "applications", dateKey, "jobs", applicationId, "chunks", chunkId),
  );
  const applicationChunkSnapshots = await Promise.all(
    applicationChunkRefs.map((ref) => getDoc(ref)),
  );
  const assetIdsSet = collectJobAssetIds(application);
  applicationChunkSnapshots.forEach((snapshot) => {
    if (snapshot.exists()) collectJobAssetIds(snapshot.data(), assetIdsSet);
  });
  const assetIds = [...assetIdsSet].filter(Boolean);

  const mediaSnapshots = await Promise.all(
    assetIds.map((assetId) => getDoc(doc(db, "mediaAssets", assetId))),
  );
  const mediaPaths = mediaSnapshots.flatMap((snapshot) => {
    const data = snapshot.exists() ? snapshot.data() || {} : {};
    return [data.previewPath, data.originalPath].filter(Boolean);
  });

  const reportDay = reportDayFromStartAt(application?.schedule?.startAt, dateKey);
  const cargoRef = doc(db, "orgs", orgId, "reportDays", reportDay, "types", "cargo");
  const cargoSnapshot = await getDoc(cargoRef);
  const cargoMeta = cargoSnapshot.exists() ? cargoSnapshot.data() || {} : {};
  const cargoChunkIds = Array.isArray(cargoMeta.chunkIds)
    ? cargoMeta.chunkIds.map(mapText).filter(Boolean)
    : [];
  const cargoChunkRefs = cargoChunkIds.map((chunkId) => doc(cargoRef, "chunks", chunkId));
  const cargoChunkSnapshots = await Promise.all(
    cargoChunkRefs.map((ref) => getDoc(ref)),
  );
  const remainingCargo = cargoChunkSnapshots.flatMap((snapshot) => {
    const items = snapshot.exists() ? snapshot.data()?.items || {} : {};
    return Object.entries(items)
      .filter(([, item]) => mapText(item?.applicationId) !== applicationId)
      .map(([id, item]) => ({ id, item }));
  });

  await commitDeleteBatch(
    [...applicationChunkRefs, ...cargoChunkRefs],
    (batch, ref) => batch.delete(ref),
  );

  const nextCargoChunks = [];
  let currentItems = {};
  const pushCargoChunk = () => {
    if (!Object.keys(currentItems).length) return;
    nextCargoChunks.push(currentItems);
    currentItems = {};
  };
  remainingCargo.forEach(({ id, item }) => {
    const candidate = { ...currentItems, [id]: item };
    const bytes = new TextEncoder().encode(JSON.stringify(candidate)).length;
    if (Object.keys(currentItems).length && bytes > 700000) {
      pushCargoChunk();
      currentItems = { [id]: item };
    } else {
      currentItems = candidate;
    }
  });
  pushCargoChunk();

  if (nextCargoChunks.length) {
    for (let index = 0; index < nextCargoChunks.length; index += 1) {
      const chunkId = `chunk_${String(index + 1).padStart(6, "0")}`;
      const items = nextCargoChunks[index];
      const batch = writeBatch(db);
      batch.set(doc(cargoRef, "chunks", chunkId), {
        chunkId,
        sequence: index + 1,
        itemCount: Object.keys(items).length,
        estimatedBytes: new TextEncoder().encode(JSON.stringify(items)).length,
        items,
        updatedAt: new Date(),
      });
      await batch.commit();
    }
    const metaBatch = writeBatch(db);
    metaBatch.set(cargoRef, {
      reportType: "cargo",
      chunkCount: nextCargoChunks.length,
      chunkIds: nextCargoChunks.map((_, index) =>
        `chunk_${String(index + 1).padStart(6, "0")}`,
      ),
      totalItemCount: remainingCargo.length,
      activeChunkId: `chunk_${String(nextCargoChunks.length).padStart(6, "0")}`,
      targetBytes: 700000,
      updatedAt: new Date(),
    }, { merge: true });
    await metaBatch.commit();
  } else if (cargoSnapshot.exists()) {
    const batch = writeBatch(db);
    batch.delete(cargoRef);
    await batch.commit();
  }

  await deleteDoc(applicationRef);
  await commitDeleteBatch(
    assetIds.map((assetId) => doc(db, "mediaAssets", assetId)),
    (batch, ref) => batch.delete(ref),
  );
  await Promise.allSettled([
    ...mediaPaths.map((path) => deleteObject(storageRef(storage, path))),
    ...assetIds.map((assetId) => deleteStorageTree(storage, `assets/${assetId}`)),
    deleteStorageTree(storage, `orgs/${orgId}/applications/${dateKey}/jobs/${applicationId}/proof`),
  ]);
  return { applicationId, reportDay, deletedAssetCount: assetIds.length };
}

function isLocalDeliveryDetailsEnabled() {
  return typeof window !== "undefined" &&
    ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);
}

function menuKeyFromPath(pathname, search = "") {
  if (typeof pathname !== "string") return "overview";
  if (pathname === INTERNAL_ENTRY_BASE) {
    const view = new URLSearchParams(search).get("view");
    if (view === "cargoManagement") return "cargoManagement";
    if (view === "deliveryDetails" && isLocalDeliveryDetailsEnabled()) return "deliveryDetails";
    return "overview";
  }

  const routeEntries = Object.entries(MENU_ROUTE_KEYS);
  const matched = routeEntries.find(([, routeKey]) => {
    if (!routeKey) return false;
    const route = internalPathForFeature(routeKey);
    return pathname === route || pathname.startsWith(`${route}/`);
  });

  return matched?.[0] || "overview";
}

const DEFAULT_MAP_CENTER = [23.6978, 120.9605];
const HISTORY_MARKER_THRESHOLD_SECONDS = 5 * 60;
const HISTORY_STATION_CLUSTER_RADIUS_METERS = 50;
const STAY_AREA_RADIUS_METERS = 100;
const STAY_AREA_MAX_GAP_SECONDS = 10 * 60;

function mapText(value) {
  return String(value ?? "").trim();
}

function isInspectionApplication(application) {
  return (
    mapText(application?.workType).toUpperCase() === "INSPECTION" ||
    mapText(application?.workMode).toUpperCase() === "ROUTE_RECORDING"
  );
}

function imageUrl(source = {}) {
  const photos = Array.isArray(source.photos) ? source.photos : [];
  const values = [
    source.photo?.previewURL,
    source.photo?.previewUrl,
    ...photos.map((photo) => photo?.previewURL || photo?.previewUrl),
    source.previewURL,
    source.previewUrl,
    ...photos,
    source.photo,
    source.photoUrl,
    source.photoURL,
    source.avatarUrl,
    source.avatarURL,
    source.imageUrl,
    source.imageURL,
    source.photoUrls?.[0],
    source.photoURLs?.[0],
  ];
  for (const value of values) {
    const url = typeof value === "string"
      ? value
      : value?.previewURL || value?.previewUrl || value?.url || value?.downloadURL || value?.downloadUrl || value?.originalURL || value?.originalUrl || "";
    if (/^(https?:|data:|blob:)/i.test(mapText(url))) return mapText(url);
  }
  return "";
}

function markerImageSrc(value) {
  return mapText(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function mapNumber(value) {
  const result = Number(value);
  return Number.isFinite(result) ? result : null;
}

function formatSpeedKmh(value) {
  const speed = mapNumber(value);
  return speed === null ? "—" : String(Math.max(0, Math.round(speed * 3.6)));
}

function mapTimestamp(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  const result = new Date(value).getTime();
  return Number.isFinite(result) ? result : 0;
}

function mapDateKey(value) {
  const date = value instanceof Date ? value : new Date(value || Date.now());
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}

function historyDayRange(value) {
  const date = value instanceof Date ? new Date(value) : new Date(value || Date.now());
  date.setHours(0, 0, 0, 0);
  return { startAt: date.getTime(), endAt: date.getTime() + 24 * 60 * 60 * 1000 };
}

function historyDateKeyOffset(value, offset) {
  const date = value instanceof Date ? new Date(value) : new Date(value || Date.now());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offset);
  return mapDateKey(date);
}

function historyDateTimeFromInput(dateKey, timeValue, inclusiveEnd = false) {
  if (!dateKey || !timeValue) return 0;
  const hasSeconds = /^\d{2}:\d{2}:\d{2}$/.test(timeValue);
  const normalizedTime = hasSeconds ? timeValue : `${timeValue}:00`;
  const value = new Date(`${dateKey}T${normalizedTime}`).getTime();
  const inclusiveEndMs = hasSeconds ? 999 : 59999;
  return Number.isFinite(value) ? value + (inclusiveEnd ? inclusiveEndMs : 0) : 0;
}

function historyRecordOverlapsDate(startAt, endAt, range) {
  const start = mapTimestamp(startAt);
  const end = mapTimestamp(endAt);
  if (!start || !range) return false;
  return start < range.endAt && (!end || end >= range.startAt);
}

function mapDateTime(value) {
  const millis = mapTimestamp(value);
  if (!millis) return "—";
  const date = new Date(millis);
  return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, "0")}/${String(
    date.getDate(),
  ).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes(),
  ).padStart(2, "0")}`;
}

function mapTrackPoint(raw) {
  const location =
    raw?.locationSnapshot || raw?.location || raw?.coordinate || raw || {};
  const lat = mapNumber(
    raw?.lat ?? raw?.latitude ?? location?.lat ?? location?.latitude,
  );
  const lng = mapNumber(
    raw?.lng ?? raw?.longitude ?? location?.lng ?? location?.longitude,
  );
  const observedAt = mapTimestamp(
    raw?.observedAt ??
      raw?.timestamp ??
      raw?.createdAt ??
      raw?.receivedAt ??
      location?.observedAt ??
      location?.timestamp,
  );
  if (
    lat === null ||
    lng === null ||
    Math.abs(lat) > 90 ||
    Math.abs(lng) > 180
  ) {
    return null;
  }
  return {
    id: mapText(raw?.eventId || raw?.id),
    lat,
    lng,
    observedAt,
    speed: mapNumber(raw?.speed ?? location?.speed),
    locationState: mapText(raw?.locationState ?? location?.locationState),
    status: mapText(raw?.status ?? location?.status),
    applicationId: mapText(raw?.applicationId),
    stopId: mapText(raw?.stopId),
    sessionId: mapText(raw?.sessionId),
    deviceId: mapText(raw?.deviceId),
    segmentId: mapText(raw?.segmentId) || `${mapText(raw?.sessionId) || "legacy"}:${mapText(raw?.deviceId) || "legacy"}`,
  };
}

function formatDurationSeconds(value) {
  const seconds = Math.max(0, Math.round(Number(value) || 0));
  if (!seconds) return "—";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours) return minutes ? `${hours} 小時 ${minutes} 分` : `${hours} 小時`;
  return minutes ? `${minutes} 分鐘` : `${seconds} 秒`;
}

function formatSummaryDuration(value) {
  return formatDurationSeconds(value) === "—"
    ? "0 分鐘"
    : formatDurationSeconds(value);
}

function formatElapsedDuration(value) {
  const seconds = Math.max(0, Math.round(Number(value) || 0));
  if (seconds < 60) return "不到 1 分鐘";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} 分鐘`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) {
    return remainingMinutes
      ? `${hours} 小時 ${remainingMinutes} 分`
      : `${hours} 小時`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours ? `${days} 天 ${remainingHours} 小時` : `${days} 天`;
}

function formatRelativeTime(value) {
  const millis = mapTimestamp(value);
  if (!millis) return "時間不明";
  const elapsedSeconds = Math.max(0, Math.round((Date.now() - millis) / 1000));
  if (elapsedSeconds >= 24 * 60 * 60) {
    return `${Math.floor(elapsedSeconds / (24 * 60 * 60))} 天前`;
  }
  if (elapsedSeconds >= 60 * 60) {
    return `${Math.floor(elapsedSeconds / (60 * 60))} 小時前`;
  }
  if (elapsedSeconds >= 60) return `${Math.floor(elapsedSeconds / 60)} 分鐘前`;
  return "剛剛";
}

function recordDurationSeconds(record) {
  const start = mapTimestamp(record?.restStartTime);
  const end = mapTimestamp(record?.restEndTime);
  if (!start) return 0;
  return Math.max(0, Math.round(((end || Date.now()) - start) / 1000));
}

function mapTrackStop(raw) {
  const stopStartTime = mapTimestamp(raw?.startedAt);
  const endedAt = mapTimestamp(raw?.endedAt);
  const explicitDuration =
    raw?.durationSeconds === null || raw?.durationSeconds === undefined
      ? NaN
      : Number(raw.durationSeconds);
  const durationSeconds = Number.isFinite(explicitDuration) &&
    (endedAt > 0 || explicitDuration > 0)
    ? Math.max(0, explicitDuration)
    : stopStartTime > 0
      ? Math.round(((endedAt || Date.now()) - stopStartTime) / 1000)
      : 0;
  const lat = mapNumber(raw?.lat);
  const lng = mapNumber(raw?.lng);

  return {
    id: mapText(raw?.eventId || raw?.id),
    eventType: mapText(raw?.eventType),
    applicationId: mapText(raw?.applicationId),
    applicationTitle: mapText(raw?.applicationTitle),
    stopId: mapText(raw?.stopId),
    stopName: mapText(raw?.stopName),
    lat,
    lng,
    stopStartTime,
    endedAt,
    durationSeconds,
  };
}

function historyStopLabel(stop, jobsById = {}) {
  const job = jobsById[mapText(stop?.applicationId)];
  const jobTitle = job
    ? mapApplicationTitle(job)
    : mapText(stop?.applicationTitle);
  const jobStops = Array.isArray(job?.stops) ? job.stops : [];
  const stopIndex = jobStops.findIndex((jobStop) =>
    mapText(jobStop?.id || jobStop?.stopId) === mapText(stop?.stopId),
  );
  if (jobTitle && stopIndex >= 0) {
    return `${jobTitle} · 第 ${stopIndex + 1} 站`;
  }
  if (jobTitle) return `${jobTitle} · 站點`;
  return mapText(stop?.stopName) || "一般停留";
}

function shouldKeepLatestStopOpen(stop, allStops = [], trackPoints = [], statusEvents = []) {
  if (!stop?.stopStartTime) return false;
  const latestStop = [...allStops].sort(
    (left, right) => left.stopStartTime - right.stopStartTime,
  )[allStops.length - 1];
  if (!latestStop || latestStop.id !== stop.id) return false;

  const stopEndAt = stop.endedAt ||
    stop.stopStartTime + (Number(stop.durationSeconds) || 0) * 1000;
  const closingEvent = statusEvents
    .filter((event) =>
      ["tracking_paused", "tracking_stopped", "resume_work", "end_work", "work_completed", "work_cancelled", "go_offline"].includes(mapText(event?.eventType)) &&
      mapTimestamp(event?.observedAt) >= stop.stopStartTime,
    )
    .sort((left, right) => mapTimestamp(right?.observedAt) - mapTimestamp(left?.observedAt))[0];
  if (closingEvent && mapTimestamp(closingEvent.observedAt) >= stopEndAt) {
    return false;
  }

  const pointAfterStop = [...trackPoints]
    .filter((point) => mapTimestamp(point?.observedAt) > stopEndAt)
    .sort((left, right) => mapTimestamp(left?.observedAt) - mapTimestamp(right?.observedAt))[0];
  if (pointAfterStop && distanceKm(stop, pointAfterStop) > STAY_AREA_RADIUS_METERS / 1000) {
    return false;
  }

  return true;
}

function playbackStopEndAt(stop, allStops = [], trackPoints = [], statusEvents = []) {
  const explicitEndAt = mapTimestamp(stop?.endedAt);
  if (explicitEndAt) return explicitEndAt;
  const startAt = mapTimestamp(stop?.stopStartTime);
  if (!startAt) return 0;
  if (shouldKeepLatestStopOpen(stop, allStops, trackPoints, statusEvents)) return Date.now();

  const laterStopAt = allStops
    .map((item) => mapTimestamp(item?.stopStartTime))
    .filter((value) => value > startAt)
    .sort((left, right) => left - right)[0] || 0;
  const pointLeavingArea = trackPoints
    .filter((point) => mapTimestamp(point?.observedAt) > startAt &&
      distanceKm(stop, point) > STAY_AREA_RADIUS_METERS / 1000)
    .sort((left, right) => mapTimestamp(left?.observedAt) - mapTimestamp(right?.observedAt))[0];
  const closingEventAt = statusEvents
    .filter((event) =>
      ["tracking_paused", "tracking_stopped", "resume_work", "end_work", "work_completed", "work_cancelled", "go_offline"].includes(mapText(event?.eventType)) &&
      mapTimestamp(event?.observedAt) > startAt,
    )
    .map((event) => mapTimestamp(event?.observedAt))
    .sort((left, right) => left - right)[0] || 0;
  const inferredEndAt = [laterStopAt, pointLeavingArea ? mapTimestamp(pointLeavingArea.observedAt) : 0, closingEventAt]
    .filter((value) => value > startAt)
    .sort((left, right) => left - right)[0];
  return inferredEndAt || startAt;
}

function mergeTrackStops(stops = []) {
  const ordered = Array.isArray(stops)
    ? [...stops].sort((left, right) => left.stopStartTime - right.stopStartTime)
    : [];

  return ordered.reduce((areas, stop) => {
    const previous = areas[areas.length - 1];
    const previousEnd = previous
      ? previous.endedAt || previous.stopStartTime + previous.durationSeconds * 1000
      : 0;
    const timeGapSeconds = previous
      ? Math.max(0, (stop.stopStartTime - previousEnd) / 1000)
      : Infinity;
    const distanceMeters = previous && stop.lat !== null && stop.lng !== null
      ? distanceKm(previous, stop) * 1000
      : Infinity;
    const canMerge = Boolean(
      previous &&
        stop.lat !== null &&
        stop.lng !== null &&
        previous.lat !== null &&
        previous.lng !== null &&
        distanceMeters <= STAY_AREA_RADIUS_METERS &&
        timeGapSeconds <= STAY_AREA_MAX_GAP_SECONDS,
    );

    if (!canMerge) {
      areas.push({ ...stop, sourceIds: [stop.id], mergedCount: 1 });
      return areas;
    }

    const mergedCount = previous.mergedCount + 1;
    const mergedEndedAt = previous.endedAt && stop.endedAt
      ? Math.max(previous.endedAt, stop.endedAt)
      : 0;
    areas[areas.length - 1] = {
      ...previous,
      id: `${previous.id}_area`,
      lat: (previous.lat * previous.mergedCount + stop.lat) / mergedCount,
      lng: (previous.lng * previous.mergedCount + stop.lng) / mergedCount,
      endedAt: mergedEndedAt,
      durationSeconds: mergedEndedAt
        ? Math.max(0, Math.round((mergedEndedAt - previous.stopStartTime) / 1000))
        : Math.max(0, Math.round((Date.now() - previous.stopStartTime) / 1000)),
      stopId: previous.stopId || stop.stopId,
      sourceIds: [...previous.sourceIds, stop.id],
      mergedCount,
    };
    return areas;
  }, []);
}

function buildRestRecords(events = []) {
  const ordered = (Array.isArray(events) ? [...events] : [])
    .filter((event) => {
      const eventType = mapText(event?.eventType);
      const isAttendanceState = mapText(event?.category).toLowerCase() === "attendance" &&
        Boolean(mapText(event?.attendanceStatus));
      return ["take_break", "resume_work", "end_work", "go_online", "go_offline"].includes(eventType) ||
        isAttendanceState;
    })
    .sort((left, right) =>
      mapTimestamp(left?.observedAt) - mapTimestamp(right?.observedAt) ||
      mapText(left?.eventId || left?.id).localeCompare(mapText(right?.eventId || right?.id)),
    );
  const records = [];
  let open = null;

  ordered.forEach((event) => {
    const eventType = mapText(event?.eventType);
    const eventAt = mapTimestamp(event?.observedAt);
    if (!eventAt) return;
    const category = mapText(event?.category).toLowerCase();
    const attendanceStatus = mapText(event?.attendanceStatus).toLowerCase();
    const isAttendanceState = category === "attendance" && Boolean(attendanceStatus);
    const isBreakStatus = ["break", "rest"].includes(attendanceStatus);
    const isStart = eventType === "take_break" || (isAttendanceState && isBreakStatus);
    const isEnd =
      (eventType === "resume_work" &&
        (category === "attendance" || attendanceStatus === "online")) ||
      (eventType === "go_online" &&
        (category === "attendance" || attendanceStatus === "online")) ||
      (eventType === "go_offline" &&
        (category === "attendance" || attendanceStatus === "offline")) ||
      (eventType === "end_work" &&
        (category === "attendance" || attendanceStatus === "offline")) ||
      (isAttendanceState && !isBreakStatus);

    if (isStart) {
      if (!open) {
        open = {
          id: mapText(event?.eventId || event?.id) || `rest_${eventAt}`,
          restStartTime: eventAt,
          restEndTime: 0,
          lat: mapNumber(event?.lat),
          lng: mapNumber(event?.lng),
          applicationId: mapText(event?.applicationId),
        };
      }
    } else if (isEnd && open) {
      records.push({
        ...open,
        restEndTime: eventAt,
        endedBy: isAttendanceState ? attendanceStatus : eventType,
        endAttendanceStatus: attendanceStatus || null,
        durationSeconds: Math.max(0, Math.round((eventAt - open.restStartTime) / 1000)),
        applicationId: open.applicationId || mapText(event?.applicationId),
      });
      open = null;
    }
  });

  if (open) records.push(open);
  return records.sort((left, right) => left.restStartTime - right.restStartTime);
}

function excludeRestOverlapsFromStops(stops = [], rests = []) {
  return (Array.isArray(stops) ? stops : []).flatMap((stop) => {
    const startAt = mapTimestamp(stop?.stopStartTime);
    const sourceEndAt = mapTimestamp(stop?.endedAt) || Date.now();
    if (!startAt || sourceEndAt <= startAt) return [];

    let visibleIntervals = [{ startAt, endAt: sourceEndAt }];
    (Array.isArray(rests) ? rests : [])
      .map((rest) => ({
        startAt: mapTimestamp(rest?.restStartTime),
        endAt: mapTimestamp(rest?.restEndTime) || Date.now(),
      }))
      .filter((rest) => rest.startAt && rest.endAt > rest.startAt)
      .sort((left, right) => left.startAt - right.startAt)
      .forEach((rest) => {
        visibleIntervals = visibleIntervals.flatMap((interval) => {
          if (rest.endAt <= interval.startAt || rest.startAt >= interval.endAt) {
            return [interval];
          }
          const pieces = [];
          if (rest.startAt > interval.startAt) {
            pieces.push({ startAt: interval.startAt, endAt: rest.startAt });
          }
          if (rest.endAt < interval.endAt) {
            pieces.push({ startAt: rest.endAt, endAt: interval.endAt });
          }
          return pieces;
        });
      });

    return visibleIntervals
      .filter((interval) => interval.endAt > interval.startAt)
      .map((interval, index) => {
        const isOpenTail = !stop?.endedAt && interval.endAt >= Date.now() - 1000;
        const split = visibleIntervals.length > 1;
        return {
          ...stop,
          id: split ? `${stop.id}_active_${index}` : stop.id,
          stopStartTime: interval.startAt,
          endedAt: isOpenTail ? 0 : interval.endAt,
          durationSeconds: Math.max(0, Math.round((interval.endAt - interval.startAt) / 1000)),
        };
      });
  });
}

function escapeTrackMarkerHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function historyWorkLabelWidth(groups = []) {
  const groupWidths = groups.map(({ numbers = [] }) =>
    10 + numbers.length * 22,
  );
  const dividerWidth = groups.length > 1 ? 20 : 0;
  const contentWidth = groupWidths.reduce((total, width) => total + width, 0) + dividerWidth;
  return Math.min(220, Math.max(46, Math.ceil(contentWidth + 14)));
}

function stationEventCoordinate(value) {
  const location = value?.locationSnapshot || value?.completedLocationSnapshot ||
    value?.location || value?.coordinate || value?.position || value || {};
  const lat = mapNumber(value?.lat ?? value?.latitude ?? location?.lat ?? location?.latitude);
  const lng = mapNumber(value?.lng ?? value?.longitude ?? location?.lng ?? location?.longitude);
  return lat !== null && lng !== null && Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180 && (lat !== 0 || lng !== 0) ? {lat, lng} : null;
}

const HISTORY_STATION_COLORS = [
  { fill: "#3284A7", ring: "rgba(50,132,167,.28)" },
  { fill: "#C77655", ring: "rgba(199,118,85,.3)" },
  { fill: "#C28A43", ring: "rgba(194,138,67,.28)" },
  { fill: "#4E9875", ring: "rgba(78,152,117,.28)" },
  { fill: "#B46F79", ring: "rgba(180,111,121,.28)" },
];

function historyStationTheme(stationNumber) {
  const number = Number(stationNumber);
  const colorIndex = Number.isFinite(number) && number > 0
    ? (Math.floor(number) - 1) % HISTORY_STATION_COLORS.length
    : 0;
  return HISTORY_STATION_COLORS[colorIndex];
}

function buildHistoryMapWorkMarkers(events = [], jobsById = {}) {
  const actionsById = new Map();
  (Array.isArray(events) ? events : []).forEach((event) => {
    const eventType = mapText(event?.eventType).toLowerCase();
    const applicationId = mapText(event?.applicationId);
    const stopId = mapText(event?.stopId);
    const observedAt = mapTimestamp(event?.observedAt);
    const coordinate = stationEventCoordinate(event);
    if (
      !applicationId || !stopId || !observedAt || !coordinate ||
      !["station_started", "station_completed"].includes(eventType)
    ) return;

    const eventId = mapText(event?.actionId) || mapText(event?.id) ||
      `${applicationId}|${stopId}|${eventType}|${observedAt}|${coordinate.lat}|${coordinate.lng}`;
    const current = actionsById.get(eventId);
    const normalizedEvent = {...(current?.event || {}), ...event, eventType};
    actionsById.set(eventId, {
      event: normalizedEvent,
      coordinate: current?.coordinate || coordinate,
    });
  });

  const actionItems = [...actionsById.entries()]
    .sort((left, right) => {
      const timeDifference =
        mapTimestamp(left[1].event.observedAt) - mapTimestamp(right[1].event.observedAt);
      return timeDifference || left[0].localeCompare(right[0]);
    })
    .map(([eventId, {event, coordinate}]) => {
      const applicationId = mapText(event.applicationId);
      const stopId = mapText(event.stopId);
      const job = jobsById[applicationId];
      const jobStops = Array.isArray(job?.stops) ? job.stops : [];
      const stopIndex = jobStops.findIndex((stop) =>
        mapText(stop?.id || stop?.stopId) === stopId,
      );
      const explicitNumber = mapNumber(
        event?.stationNumber ?? event?.stopNumber ?? event?.sequence,
      );
      const zeroBasedIndex = mapNumber(event?.stopIndex ?? event?.stationIndex);
      const stationNumber = explicitNumber !== null && explicitNumber > 0
        ? explicitNumber
        : stopIndex >= 0
          ? stopIndex + 1
          : zeroBasedIndex !== null && zeroBasedIndex >= 0
            ? zeroBasedIndex + 1
            : "";
      const prefix = event.eventType === "station_started" ? "出" : "完";
      const title = mapText(event.applicationTitle) ||
        (job ? mapApplicationTitle(job) : "") || "工作";
      const stop = jobStops.find((item) =>
        mapText(item?.id || item?.stopId) === stopId,
      );
      return {
        eventId,
        event,
        applicationId,
        stopId,
        stationNumber: mapText(stationNumber),
        title,
        stopName: mapText(event.stopName || stop?.text || stop?.name || stop?.address),
        actionLabel: `${prefix}${stationNumber} · ${historyClockTime(mapTimestamp(event.observedAt))}`,
        prefix,
        observedAt: mapTimestamp(event.observedAt),
        locationObservedAt: mapTimestamp(event.locationObservedAt),
        accuracy: mapNumber(event.accuracy),
        lat: coordinate.lat,
        lng: coordinate.lng,
      };
    });

  const locationGroups = [];
  actionItems.forEach((action) => {
    const nearestGroup = locationGroups
      .map((group) => ({
        group,
        distanceMeters: Math.max(
          ...group.actions.map((existing) => distanceKm(existing, action) * 1000),
        ),
      }))
      .filter(({ distanceMeters }) => distanceMeters <= HISTORY_STATION_CLUSTER_RADIUS_METERS)
      .sort((left, right) => left.distanceMeters - right.distanceMeters)[0]?.group;

    if (!nearestGroup) {
      locationGroups.push({
        actions: [action],
        lat: action.lat,
        lng: action.lng,
      });
      return;
    }

    const previousCount = nearestGroup.actions.length;
    nearestGroup.actions.push(action);
    nearestGroup.lat = (nearestGroup.lat * previousCount + action.lat) / (previousCount + 1);
    nearestGroup.lng = (nearestGroup.lng * previousCount + action.lng) / (previousCount + 1);
  });

  const stationMarkers = locationGroups.map(({ actions, lat, lng }) => {
    const orderedActions = [...actions].sort((left, right) =>
      left.observedAt - right.observedAt || left.eventId.localeCompare(right.eventId),
    );
    const locationKey = orderedActions
      .map((action) => action.eventId)
      .sort()
      .join("|");
    const listNumbers = (prefix) => [...new Set(
      orderedActions
        .filter((action) => action.prefix === prefix)
        .map((action) => action.stationNumber || "?"),
    )].sort((left, right) => {
      const leftNumber = Number(left);
      const rightNumber = Number(right);
      if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
        return leftNumber - rightNumber;
      }
      return left.localeCompare(right, "zh-Hant");
    });
    const departureNumbers = listNumbers("出");
    const completionNumbers = listNumbers("完");
    const first = orderedActions[0];
    const compactLabel = [
      departureNumbers.length ? `出${departureNumbers.join("、")}` : "",
      completionNumbers.length ? `完${completionNumbers.join("、")}` : "",
    ].filter(Boolean).join(" / ");

    return {
      id: `station_${locationKey}`,
      type: "station",
      applicationId: first.applicationId,
      stopId: first.stopId,
      title: first.title,
      stopName: [...new Set(orderedActions.map((action) => action.stopName).filter(Boolean))].join("、"),
      subtitle: orderedActions.map((action) => `${action.title} · ${action.actionLabel}`).join(" / "),
      compactLabel,
      departureNumbers,
      completionNumbers,
      actions: orderedActions,
      coreLabel: departureNumbers.length && completionNumbers.length
        ? "出/完"
        : departureNumbers.length ? "出" : "完",
      observedAt: first.observedAt,
      locationObservedAt: first.locationObservedAt,
      accuracy: first.accuracy,
      lat,
      lng,
      events: orderedActions.map((action) => action.event),
    };
  });
  const inspectionEventRows = (Array.isArray(events) ? events : [])
    .filter((event) => ["inspection_record_added", "inspection_exception_reported"].includes(mapText(event?.eventType)))
    .map((event) => {
      const coordinate = stationEventCoordinate(event);
      const observedAt = mapTimestamp(event?.observedAt);
      const applicationId = mapText(event?.applicationId);
      if (!coordinate || !observedAt || !applicationId) return null;
      const eventType = mapText(event?.eventType);
      return {
        event,
        eventId: mapText(event?.eventId || event?.actionId || event?.id),
        eventType,
        applicationId,
        observedAt,
        lat: coordinate.lat,
        lng: coordinate.lng,
        workDateKey: mapText(event?.workDateKey),
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.observedAt - right.observedAt || left.eventId.localeCompare(right.eventId));
  const inspectionNumbers = new Map();
  const inspectionMarkers = inspectionEventRows.map(({event, eventId, eventType, applicationId, observedAt, lat, lng, workDateKey}) => {
    const numberKey = `${workDateKey}:${applicationId}`;
    const markerNumber = (inspectionNumbers.get(numberKey) || 0) + 1;
    inspectionNumbers.set(numberKey, markerNumber);
    const job = jobsById[applicationId];
    const title = mapText(event?.applicationTitle) || (job ? mapApplicationTitle(job) : "巡檢工作");
    const eventTitle = mapText(event?.eventTitle || event?.title) || historyEventLabel(eventType, event);
    return {
      id: `inspection_${eventId || `${applicationId}_${observedAt}`}`,
      eventId,
      workDateKey,
      type: "inspection",
      applicationId,
      title,
      eventTitle,
      eventType,
      markerLabel: `記${markerNumber}`,
      notePreview: mapText(event?.notePreview || event?.reason),
      observedAt,
      locationObservedAt: mapTimestamp(event?.locationObservedAt),
      accuracy: mapNumber(event?.accuracy),
      recordType: mapText(event?.recordType),
      lat,
      lng,
    };
  });
  const stationMarkersWithInspection = stationMarkers.map((marker) => ({
    ...marker,
    inspectionRecords: [],
  }));
  const standaloneInspectionMarkers = [];
  inspectionMarkers.forEach((inspectionMarker) => {
    const nearestStation = stationMarkersWithInspection
      .map((marker) => ({
        marker,
        distanceMeters: distanceKm(marker, inspectionMarker) * 1000,
      }))
      .filter(({distanceMeters}) => distanceMeters <= HISTORY_STATION_CLUSTER_RADIUS_METERS)
      .sort((left, right) => left.distanceMeters - right.distanceMeters)[0]?.marker;
    if (nearestStation) nearestStation.inspectionRecords.push(inspectionMarker);
    else standaloneInspectionMarkers.push(inspectionMarker);
  });

  const inspectionGroups = [];
  standaloneInspectionMarkers.forEach((inspectionMarker) => {
    const nearestGroup = inspectionGroups
      .map((group) => ({
        group,
        distanceMeters: Math.max(...group.records.map((record) => distanceKm(record, inspectionMarker) * 1000)),
      }))
      .filter(({distanceMeters}) => distanceMeters <= HISTORY_STATION_CLUSTER_RADIUS_METERS)
      .sort((left, right) => left.distanceMeters - right.distanceMeters)[0]?.group;
    if (!nearestGroup) {
      inspectionGroups.push({records: [inspectionMarker], lat: inspectionMarker.lat, lng: inspectionMarker.lng});
      return;
    }
    const previousCount = nearestGroup.records.length;
    nearestGroup.records.push(inspectionMarker);
    nearestGroup.lat = (nearestGroup.lat * previousCount + inspectionMarker.lat) / (previousCount + 1);
    nearestGroup.lng = (nearestGroup.lng * previousCount + inspectionMarker.lng) / (previousCount + 1);
  });

  const stationMarkersWithLabels = stationMarkersWithInspection.map((marker) => {
    const inspectionLabel = marker.inspectionRecords.length
      ? `記${marker.inspectionRecords.map((record) => mapText(record.markerLabel).replace(/^記/, "")).join("、")}`
      : "";
    return {
      ...marker,
      inspectionNumbers: marker.inspectionRecords.map((record) => mapText(record.markerLabel).replace(/^記/, "")),
      compactLabel: [marker.compactLabel, inspectionLabel].filter(Boolean).join(" / "),
      subtitle: [
        marker.subtitle,
        ...marker.inspectionRecords.map((record) => `${record.markerLabel} · ${record.eventTitle} · ${historyClockTime(record.observedAt)}`),
      ].filter(Boolean).join(" / "),
    };
  });
  const standaloneGroups = inspectionGroups.flatMap((group) => {
    const records = [...group.records].sort((left, right) =>
      left.observedAt - right.observedAt || left.eventId.localeCompare(right.eventId),
    );
    if (records.length === 1) return records;
    const first = records[0];
    const inspectionNumbers = records.map((record) => mapText(record.markerLabel).replace(/^記/, ""));
    return [{
      id: `inspection_group_${records.map((record) => record.eventId).sort().join("|")}`,
      type: "workCluster",
      applicationId: first.applicationId,
      title: [...new Set(records.map((record) => record.title).filter(Boolean))].join("、") || "巡檢紀錄",
      subtitle: records.map((record) => `${record.markerLabel} · ${record.eventTitle} · ${historyClockTime(record.observedAt)}`).join(" / "),
      compactLabel: `記${inspectionNumbers.join("、")}`,
      departureNumbers: [],
      completionNumbers: [],
      inspectionNumbers,
      inspectionRecords: records,
      actions: [],
      coreLabel: "記",
      observedAt: first.observedAt,
      lat: group.lat,
      lng: group.lng,
    }];
  });

  return [...stationMarkersWithLabels, ...standaloneGroups];
}

function historyJobStops(job) {
  const stops = Array.isArray(job?.stops)
    ? job.stops
    : Array.isArray(job?.routeSnapshot?.stops)
      ? job.routeSnapshot.stops
      : [];
  return stops.map((stop, index) => ({ stop, index })).sort((left, right) => {
    const leftOrder = Number(left.stop?.order ?? left.stop?.sequence);
    const rightOrder = Number(right.stop?.order ?? right.stop?.sequence);
    return Number.isFinite(leftOrder) && Number.isFinite(rightOrder) && leftOrder !== rightOrder
      ? leftOrder - rightOrder
      : left.index - right.index;
  }).map(({ stop, index }) => ({ stop, index }));
}

function historyCargoItemsForStop(stop, job, stopIndex, totalStops) {
  const directItems = [stop?.cargoItems, stop?.items, stop?.contents].find(
    (items) => Array.isArray(items) && items.length,
  );
  if (directItems) return directItems;

  const recipients = Array.isArray(stop?.recipients) ? stop.recipients : [];
  const recipientItems = recipients.flatMap((recipient) => {
    const items = [recipient?.cargoItems, recipient?.contents, recipient?.items].find(
      (value) => Array.isArray(value) && value.length,
    );
    return items || [];
  });
  if (recipientItems.length) return recipientItems;

  const allItems = Array.isArray(job?.cargoItems)
    ? job.cargoItems
    : Array.isArray(job?.items)
      ? job.items
      : [];
  if (!allItems.length) return [];
  const stopId = mapText(stop?.id || stop?.stopId).toUpperCase();
  const stopOrder = Number(stop?.order) || stopIndex + 1;
  const matched = allItems.filter((item) => {
    const itemStopId = mapText(item?.stopId || item?.stop?.id).toUpperCase();
    const itemOrder = Number(item?.stopOrder);
    return (itemStopId && itemStopId === stopId) ||
      (!itemStopId && itemOrder > 0 && itemOrder === stopOrder);
  });
  if (matched.length) return matched;
  return totalStops === 1 ? allItems : [];
}

function historyStopRoleLabel(stop, stopIndex) {
  const operationType = mapText(stop?.operationType).toUpperCase();
  if (operationType === "PICKUP") return "寄件人";
  if (operationType === "DELIVERY") return "收件人";
  if (operationType === "PICKUP_DELIVERY") return "寄件人／收件人";
  const role = mapText(stop?.role || stop?.type).toLowerCase();
  return ["start", "pickup", "sender"].includes(role) || (!role && stopIndex === 0)
    ? "寄件人"
    : "收件人";
}

function historyStopAddress(stop, fallback = "") {
  return mapText(stop?.text || stop?.address || stop?.fullAddress || stop?.locationText) ||
    [stop?.city, stop?.district || stop?.suburb, stop?.road, stop?.lane, stop?.alley, stop?.houseNumber, stop?.detail]
      .map(mapText)
      .filter(Boolean)
      .join("") || mapText(fallback);
}

function historyMediaFileKey(file) {
  return mapText(file?.assetId || file?.previewUrl || file?.previewURL || file?.url || file?.downloadURL || file?.downloadUrl || file?.originalUrl || file?.originalURL);
}

function buildSelectedHistoryWorkStations(selection, jobsById = {}) {
  if (selection?.type !== "station") return [];
  const marker = selection.record || {};
  const stationGroups = new Map();
  (Array.isArray(marker.actions) ? marker.actions : []).forEach((action) => {
    const applicationId = mapText(action?.applicationId || action?.event?.applicationId);
    const stopId = mapText(action?.stopId || action?.event?.stopId);
    if (!applicationId || !stopId) return;
    const key = `${applicationId}:${stopId}`;
    const group = stationGroups.get(key) || { applicationId, stopId, actions: [] };
    group.actions.push(action);
    stationGroups.set(key, group);
  });

  return [...stationGroups.values()].map((group, groupIndex) => {
    const job = jobsById[group.applicationId] || {};
    const orderedStops = historyJobStops(job);
    const matchedIndex = orderedStops.findIndex(({ stop }) =>
      mapText(stop?.id || stop?.stopId) === group.stopId,
    );
    const stopIndex = matchedIndex >= 0 ? orderedStops[matchedIndex].index : groupIndex;
    const stop = matchedIndex >= 0
      ? orderedStops[matchedIndex].stop
      : group.actions[0]?.event?.stop && typeof group.actions[0].event.stop === "object"
        ? group.actions[0].event.stop
        : { id: group.stopId };
    const recipients = Array.isArray(stop?.recipients) ? stop.recipients : [];
    const firstAction = group.actions[0] || {};
    const stopName = mapText(
      stop?.name || stop?.stopName || firstAction.stopName || firstAction.event?.stopName,
    );
    const address = historyStopAddress(stop, firstAction.event?.address || firstAction.event?.fullAddress);
    const stationNumber = mapText(
      firstAction.stationNumber || stop?.order || stop?.sequence || stopIndex + 1,
    );
    return {
      key: `${group.applicationId}:${group.stopId}`,
      applicationId: group.applicationId,
      title: mapApplicationTitle(job) || mapText(firstAction.title || firstAction.event?.applicationTitle || marker.title) || "工作",
      stopId: group.stopId,
      stop,
      stopIndex,
      stationNumber,
      stopName: stopName || "未提供名稱",
      address,
      roleLabel: historyStopRoleLabel(stop, stopIndex),
      recipients,
      cargoItems: historyCargoItemsForStop(stop, job, stopIndex, orderedStops.length),
      actions: [...group.actions].sort((left, right) =>
        mapTimestamp(left?.observedAt) - mapTimestamp(right?.observedAt),
      ),
    };
  });
}

function buildHistoryAuxMarkers(stops = [], rests = []) {
  const records = [
    ...(Array.isArray(stops) ? stops : []).map((record) => ({ type: "stop", record })),
    ...(Array.isArray(rests) ? rests : []).map((record) => ({ type: "rest", record })),
  ].filter(({ record }) =>
    mapNumber(record?.lat) !== null && mapNumber(record?.lng) !== null,
  ).sort((left, right) => {
    const leftAt = mapTimestamp(left.type === "stop" ? left.record.stopStartTime : left.record.restStartTime);
    const rightAt = mapTimestamp(right.type === "stop" ? right.record.stopStartTime : right.record.restStartTime);
    return leftAt - rightAt;
  });

  const groups = [];
  records.forEach((item) => {
    const startAt = mapTimestamp(item.type === "stop" ? item.record.stopStartTime : item.record.restStartTime);
    const endAt = mapTimestamp(item.type === "stop" ? item.record.endedAt : item.record.restEndTime) || Date.now();
    const group = groups.find((candidate) => candidate.items.some((existing) => {
      const otherStart = mapTimestamp(existing.type === "stop" ? existing.record.stopStartTime : existing.record.restStartTime);
      const otherEnd = mapTimestamp(existing.type === "stop" ? existing.record.endedAt : existing.record.restEndTime) || Date.now();
      const gap = Math.max(0, Math.max(startAt, otherStart) - Math.min(endAt, otherEnd));
      return distanceKm(existing.record, item.record) <= 0.05 && gap <= 10 * 60 * 1000;
    }));
    if (group) group.items.push(item);
    else groups.push({ items: [item] });
  });

  return groups.map((group, index) => {
    const hasStop = group.items.some((item) => item.type === "stop");
    const hasRest = group.items.some((item) => item.type === "rest");
    const labels = group.items.map(({ type, record }) =>
      `${type === "stop" ? "停" : "休"} ${formatTrackStopMarkerDuration(record.durationSeconds)}`,
    );
    const first = group.items[0];
    const lat = group.items.reduce((sum, item) => sum + Number(item.record.lat), 0) / group.items.length;
    const lng = group.items.reduce((sum, item) => sum + Number(item.record.lng), 0) / group.items.length;
    return {
      id: `aux_${group.items.map((item) => `${item.type}_${item.record.id || index}`).join("_")}`,
      type: "aux",
      coreLabel: hasStop && hasRest ? "停休" : hasRest ? "休" : "停",
      subtitle: [...new Set(labels)].join(" / "),
      observedAt: mapTimestamp(first.type === "stop" ? first.record.stopStartTime : first.record.restStartTime),
      lat,
      lng,
      records: group.items,
    };
  });
}

function formatTrackStopMarkerDuration(value) {
  const totalMinutes = Math.max(1, Math.round((Number(value) || 0) / 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours) return `${hours} 小時${minutes ? ` ${minutes} 分` : ""}`;
  return `${totalMinutes} 分`;
}

function historyAuxRecordsAtPlayback(stops = [], rests = [], playbackAt) {
  const targetAt = mapTimestamp(playbackAt);
  if (!targetAt) return { stops: [], rests: [] };

  const clipRecord = (record, type) => {
    const isStop = type === "stop";
    const startAt = mapTimestamp(isStop ? record?.stopStartTime : record?.restStartTime);
    if (!startAt || startAt > targetAt || mapNumber(record?.lat) === null || mapNumber(record?.lng) === null) {
      return null;
    }

    const actualEndAt = mapTimestamp(isStop ? record?.endedAt : record?.restEndTime);
    const hasEnded = actualEndAt > 0 && targetAt >= actualEndAt;
    const durationEndAt = hasEnded ? actualEndAt : targetAt;
    const durationSeconds = Math.max(0, Math.round((durationEndAt - startAt) / 1000));
    if (durationSeconds < HISTORY_MARKER_THRESHOLD_SECONDS) return null;

    return isStop
      ? { ...record, endedAt: hasEnded ? actualEndAt : 0, durationSeconds }
      : { ...record, restEndTime: hasEnded ? actualEndAt : 0, durationSeconds };
  };

  return {
    stops: (Array.isArray(stops) ? stops : [])
      .map((record) => clipRecord(record, "stop"))
      .filter(Boolean),
    rests: (Array.isArray(rests) ? rests : [])
      .map((record) => clipRecord(record, "rest"))
      .filter(Boolean),
  };
}

function createTrackAuxIcon(marker, selected = false, placement = {}) {
  const coreLabel = escapeTrackMarkerHtml(marker?.coreLabel || "停");
  const subtitle = escapeTrackMarkerHtml(marker?.subtitle || "");
  const side = placement.side === "left" ? "left" : "right";
  const iconWidth = 48 + Math.min(28, subtitle.length) * 5;
  const pinLeft = side === "left" ? iconWidth - 30 : 2;
  const shiftX = Number(placement.shiftX) || 0;
  const shiftY = Number(placement.shiftY) || 0;
  const anchorX = side === "left" ? iconWidth - 16 : 16;
  const leaderLength = Math.hypot(shiftX, shiftY);
  const leader = leaderLength > 2
    ? `<i class="dmh-track-aux-leader" style="left:${anchorX}px;top:23px;width:${leaderLength}px;transform:translateY(-50%) rotate(${Math.atan2(shiftY, shiftX)}rad)"></i>`
    : "";
  const labelLeft = (side === "left" ? 0 : 20) + shiftX;
  const labelTop = 12 + shiftY;
  return L.divIcon({
    className: "dmh-track-aux-marker-host",
    iconSize: [iconWidth, 46],
    iconAnchor: [anchorX, 23],
    popupAnchor: [0, -23],
    html: `<span class="dmh-track-aux-marker${selected ? " is-selected" : ""} is-label-${side}">${leader}<span class="dmh-track-aux-pin" style="left:${pinLeft + shiftX}px;top:${9 + shiftY}px"><i class="dmh-track-aux-ring"></i><span class="dmh-track-aux-core">${coreLabel}</span></span><span class="dmh-track-aux-label" style="left:${labelLeft}px;top:${labelTop}px;width:${iconWidth - 20}px">${subtitle}</span></span>`,
  });
}

function createHistoryWorkIcon(marker, selected = false, placement = {}) {
  const side = placement.side === "left" ? "left" : "right";
  const groups = [
    { label: "出", numbers: marker?.departureNumbers || [] },
    { label: "完", numbers: marker?.completionNumbers || [] },
    {
      label: "記",
      numbers: (marker?.inspectionRecords || []).map((record) => mapText(record.markerLabel).replace(/^記/, "")),
      items: marker?.inspectionRecords || [],
    },
  ].filter((group) => group.numbers.length);
  const labelWidth = historyWorkLabelWidth(groups);
  const iconWidth = labelWidth;
  const iconHeight = 36;
  const anchorX = side === "left" ? iconWidth - 12 : 12;
  const anchorY = iconHeight / 2;
  const groupsHtml = groups.map((group, groupIndex) => {
    const numberBadges = group.numbers.map((number, index) => {
      const isException = group.items?.[index]?.eventType === "inspection_exception_reported";
      const theme = isException
        ? {fill: "#C7473A", ring: "#A93228"}
        : historyStationTheme(number);
      const safeNumber = escapeTrackMarkerHtml(number);
      return `<span class="dmh-track-work-number" style="--dmh-work-number-color:${theme.fill};--dmh-work-number-ring:${theme.ring}" title="${group.label}${safeNumber}">${safeNumber}</span>`;
    }).join("");
    const divider = groupIndex > 0 ? '<i class="dmh-track-work-divider" aria-hidden="true">/</i>' : "";
    return `${divider}<span class="dmh-track-work-group"><b class="dmh-track-work-kind">${group.label}</b>${numberBadges}</span>`;
  }).join("");
  const accessibleLabel = escapeTrackMarkerHtml(marker?.compactLabel || "站點");
  return L.divIcon({
    className: "dmh-track-work-marker-host",
    iconSize: [iconWidth, iconHeight],
    iconAnchor: [anchorX, anchorY],
    popupAnchor: [0, -anchorY],
    html: `<span class="dmh-track-work-marker is-label-${side}${selected ? " is-selected" : ""}" aria-label="${accessibleLabel}"><span class="dmh-track-work-summary" style="--dmh-work-label-width:${labelWidth}px">${groupsHtml}</span></span>`,
  });
}

function createHistoryInspectionIcon(marker, selected = false) {
  const isException = marker?.eventType === "inspection_exception_reported";
  const color = isException ? "#D94A3A" : "#287EAA";
  const label = escapeTrackMarkerHtml(marker?.markerLabel || "記");
  const shadow = selected ? "0 0 0 4px rgba(40,126,170,.22),0 4px 12px rgba(25,45,60,.25)" : "0 3px 9px rgba(25,45,60,.25)";
  const width = Math.max(42, [...label].length * 9 + 18);
  return L.divIcon({
    className: "dmh-track-inspection-marker-host",
    iconSize: [width, 34],
    iconAnchor: [width / 2, 17],
    popupAnchor: [0, -17],
    html: `<span aria-label="${escapeTrackMarkerHtml(marker?.eventTitle || "巡檢紀錄")}" style="display:grid;place-items:center;width:${width - 4}px;height:30px;padding:0 6px;border:2px solid #fff;border-radius:16px;background:${color};box-shadow:${shadow};color:#fff;white-space:nowrap;font:800 11px/1 Inter,'Noto Sans TC',sans-serif">${label}</span>`,
  });
}

function buildHistoryMarkerPlacements(stationMarkers = [], auxiliaryMarkers = [], map) {
  const stations = stationMarkers.map((marker) => ({
    marker,
    point: map.latLngToContainerPoint([marker.lat, marker.lng]),
  }));
  const auxiliaries = auxiliaryMarkers.map((marker) => ({
    marker,
    point: map.latLngToContainerPoint([marker.lat, marker.lng]),
  }));
  const placements = {};
  const mapWidth = map.getSize().x;
  const markerHalfWidth = 217;
  [...stations]
    .sort((left, right) =>
      mapTimestamp(left.marker.observedAt) - mapTimestamp(right.marker.observedAt) ||
      mapText(left.marker.id).localeCompare(mapText(right.marker.id)),
    )
    .forEach(({marker, point}, index) => {
      const nearest = stations
        .filter((entry) => entry.marker.id !== marker.id)
        .map((entry) => ({
          dx: entry.point.x - point.x,
          dy: entry.point.y - point.y,
          distance: Math.hypot(entry.point.x - point.x, entry.point.y - point.y),
        }))
        .sort((left, right) => left.distance - right.distance)[0];
      const preferredSide = nearest && Math.abs(nearest.dx) >= 12
        ? nearest.dx > 0 ? "left" : "right"
        : index % 2 ? "left" : "right";
      const leftFits = point.x >= markerHalfWidth + 8;
      const rightFits = point.x + markerHalfWidth <= mapWidth - 8;
      let side = preferredSide;
      if (side === "right" && !rightFits && leftFits) side = "left";
      else if (side === "left" && !leftFits && rightFits) side = "right";
      else if (!leftFits && !rightFits) side = point.x > mapWidth / 2 ? "left" : "right";
      placements[marker.id] = {side};
    });

  auxiliaries.forEach(({ marker, point }) => {
    const neighbors = stations
      .map((station) => ({
        station,
        dx: point.x - station.point.x,
        dy: point.y - station.point.y,
      }))
      .filter(({ dx, dy }) => Math.abs(dy) < 78 && Math.abs(dx) < 230)
      .sort((left, right) => Math.hypot(left.dx, left.dy) - Math.hypot(right.dx, right.dy));
    if (!neighbors.length) return;
    const nearest = neighbors[0];
    const distance = Math.hypot(nearest.dx, nearest.dy);
    let shiftX = 0;
    let shiftY = 0;
    if (distance < 62) {
      if (distance < 1) {
        shiftX = -30;
        shiftY = 25;
      } else {
        shiftX = nearest.dx / distance * 34;
        shiftY = nearest.dy / distance * 34;
      }
    }
    placements[marker.id] = {
      side: nearest.dx <= 0 ? "left" : "right",
      shiftX: Math.round(shiftX),
      shiftY: Math.round(shiftY),
    };
  });
  return placements;
}

function HistoryMarkerLayer({ stationMarkers = [], auxiliaryMarkers = [], selectedMarker, onSelect }) {
  const map = useMap();
  const [placements, setPlacements] = useState({});
  const selectedInspectionIsGrouped = stationMarkers.some((marker) =>
    marker.id === selectedMarker?.record?.id || marker.inspectionRecords?.some((record) =>
      record.eventId === selectedMarker?.record?.eventId,
    ),
  );
  const selectedInspectionMarker = selectedMarker?.type === "inspection" &&
    !selectedInspectionIsGrouped
    ? selectedMarker.record
    : null;
  const selectedInspectionCoordinate = stationEventCoordinate(selectedInspectionMarker);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!map.getSize().x || !map.getSize().y) return;
        const next = buildHistoryMarkerPlacements(stationMarkers, auxiliaryMarkers, map);
        setPlacements((current) =>
          JSON.stringify(current) === JSON.stringify(next) ? current : next,
        );
      });
    };
    map.on("moveend zoomend resize", update);
    update();
    return () => {
      map.off("moveend zoomend resize", update);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [auxiliaryMarkers, map, stationMarkers]);

  return (
    <>
      {auxiliaryMarkers.map((marker) => (
        <Marker
          key={marker.id}
          position={[marker.lat, marker.lng]}
          icon={createTrackAuxIcon(
            marker,
            selectedMarker?.record.id === marker.id,
            placements[marker.id],
          )}
          eventHandlers={{ click: () => onSelect({ type: "aux", record: marker }) }}
        >
          <Popup>
            <div className="dmh-popup dmh-track-stop-popup">
              <strong>{marker.coreLabel === "停休" ? "停留與休息" : marker.coreLabel === "休" ? "手動休息" : "自動停留"}</strong>
              {marker.records.map(({ type, record }, index) => {
                const startAt = type === "stop" ? record.stopStartTime : record.restStartTime;
                const endAt = type === "stop" ? record.endedAt : record.restEndTime;
                return (
                  <div key={`${type}_${record.id || index}`}>
                    <span>{type === "stop" ? "停留" : "休息"}・{formatTrackStopMarkerDuration(record.durationSeconds)}</span>
                    <small>
                      {mapDateTime(startAt)}
                      {endAt ? ` ～ ${mapDateTime(endAt)}` : type === "stop" ? " ～ 目前仍在停留" : " ～ 目前仍在休息"}
                    </small>
                  </div>
                );
              })}
            </div>
          </Popup>
        </Marker>
      ))}
      {stationMarkers.map((marker) => (
        <Marker
          key={marker.id}
          position={[marker.lat, marker.lng]}
          zIndexOffset={1000 + (mapTimestamp(marker.observedAt) || 0) % 1000}
          icon={marker.type === "inspection"
            ? createHistoryInspectionIcon(marker, selectedMarker?.record.id === marker.id)
            : createHistoryWorkIcon(
                marker,
                selectedMarker?.record.id === marker.id || marker.inspectionRecords?.some((record) =>
                  record.eventId === selectedMarker?.record?.eventId,
                ),
                placements[marker.id] || {},
              )}
          eventHandlers={{ click: () => onSelect({ type: marker.type, record: marker }) }}
        >
          {marker.type === "workCluster" ? (
            <Popup>
              <div className="dmh-popup dmh-track-work-popup">
                <strong>巡檢紀錄</strong>
                {(marker.inspectionRecords || []).map((record) => (
                  <button
                    key={record.eventId || record.id}
                    type="button"
                    onClick={() => onSelect({ type: "inspection", record })}
                    style={{ display: "grid", gap: 2, width: "100%", marginTop: 5, padding: "7px 8px", border: "1px solid #E6EDF2", borderRadius: 9, background: "#F8FAFC", color: "#334155", textAlign: "left", cursor: "pointer" }}
                  >
                    <strong style={{ fontSize: 11 }}>{record.markerLabel} · {record.eventTitle}</strong>
                    <span style={{ fontSize: 10, color: "#64748B" }}>
                      {record.title} · {historyClockTime(record.observedAt)}{record.notePreview ? ` · ${record.notePreview}` : ""}
                    </span>
                  </button>
                ))}
              </div>
            </Popup>
          ) : null}
        </Marker>
      ))}
      {selectedInspectionMarker && selectedInspectionCoordinate ? (
        <Marker
          key={`selected_${selectedInspectionMarker.id}`}
          position={[selectedInspectionCoordinate.lat, selectedInspectionCoordinate.lng]}
          zIndexOffset={2000}
          icon={createHistoryInspectionIcon(selectedInspectionMarker, true)}
          eventHandlers={{ click: () => onSelect({ type: "inspection", record: selectedInspectionMarker }) }}
        />
      ) : null}
    </>
  );
}

function nearestTrackPoint(points, targetAt) {
  return (Array.isArray(points) ? points : []).reduce((nearest, point) => {
    if (!nearest) return point;
    return Math.abs(point.observedAt - targetAt) <
      Math.abs(nearest.observedAt - targetAt)
      ? point
      : nearest;
  }, null);
}

function isPlaybackInStayOrRest(playbackAt, stops = [], rests = []) {
  const targetAt = Number(playbackAt);
  if (!Number.isFinite(targetAt)) return false;
  const inRange = (startAt, endAt) => {
    const start = Number(startAt);
    const end = Number(endAt);
    return Number.isFinite(start) && targetAt >= start && targetAt <= (Number.isFinite(end) ? end : Date.now());
  };
  return (
    stops.some((stop) =>
      inRange(
        stop.stopStartTime,
        stop.endedAt || stop.stopStartTime + (Number(stop.durationSeconds) || 0) * 1000,
      ),
    ) ||
    rests.some((rest) =>
      inRange(
        rest.restStartTime,
        rest.restEndTime || rest.restStartTime + (Number(rest.durationSeconds) || 0) * 1000,
      ),
    )
  );
}

function getPlaybackPoint(points, progress) {
  if (!Array.isArray(points) || points.length === 0) return null;
  if (points.length === 1) return points[0];

  const safeProgress = Math.max(0, Math.min(1, Number(progress) || 0));
  if (safeProgress <= 0) return points[0];
  if (safeProgress >= 1) return points[points.length - 1];

  const pointPosition = safeProgress * (points.length - 1);
  const leftIndex = Math.floor(pointPosition);
  const left = points[leftIndex];
  const right = points[Math.min(points.length - 1, leftIndex + 1)];
  const segmentProgress = pointPosition - leftIndex;
  const leftAt = Number(left.observedAt);
  const rightAt = Number(right.observedAt);

  return {
    ...right,
    observedAt: leftAt + (rightAt - leftAt) * segmentProgress,
    lat: left.lat + (right.lat - left.lat) * segmentProgress,
    lng: left.lng + (right.lng - left.lng) * segmentProgress,
  };
}

function getPlaybackProgressAtTime(points, targetAt) {
  if (!Array.isArray(points) || points.length < 2) return 0;
  const firstAt = Number(points[0].observedAt);
  const lastAt = Number(points[points.length - 1].observedAt);
  if (targetAt <= firstAt) return 0;
  if (targetAt >= lastAt) return 1;

  for (let index = 1; index < points.length; index += 1) {
    const leftAt = Number(points[index - 1].observedAt);
    const rightAt = Number(points[index].observedAt);
    if (targetAt > rightAt) continue;
    const segmentProgress = rightAt > leftAt
      ? Math.max(0, Math.min(1, (targetAt - leftAt) / (rightAt - leftAt)))
      : 1;
    return (index - 1 + segmentProgress) / (points.length - 1);
  }

  return 1;
}

function getPlaybackTrackPoints(points, playbackPoint) {
  if (!Array.isArray(points) || !playbackPoint) return [];
  const targetAt = Number(playbackPoint.observedAt);
  return [
    ...points.filter((point) => Number(point.observedAt) < targetAt),
    playbackPoint,
  ];
}

const TRACK_SEGMENT_PALETTE = [
  [37, 99, 235],
  [220, 38, 38],
  [124, 58, 237],
  [5, 150, 105],
  [217, 119, 6],
  [8, 145, 178],
  [219, 39, 119],
];

function trackGradientColor(segmentIndex, progress) {
  const safeProgress = Math.max(0, Math.min(1, Number(progress) || 0));
  const paletteIndex = ((segmentIndex % TRACK_SEGMENT_PALETTE.length) +
    TRACK_SEGMENT_PALETTE.length) % TRACK_SEGMENT_PALETTE.length;
  const base = TRACK_SEGMENT_PALETTE[paletteIndex];
  const whiteRatio = 0.62 - (0.5 * safeProgress);
  const color = base.map((channel) =>
    Math.round((255 * whiteRatio) + (channel * (1 - whiteRatio))),
  );
  return `rgb(${color.join(",")})`;
}

function buildTrackGradientSegments(
  points = [],
  statusEvents = [],
  stops = [],
  rests = [],
  baseAt = 0,
) {
  const orderedPoints = (Array.isArray(points) ? points : [])
    .filter((point) => Number.isFinite(Number(point?.observedAt)) &&
      Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng)))
    .sort((left, right) => Number(left.observedAt) - Number(right.observedAt));
  if (orderedPoints.length < 2) return [];

  const boundaryTypes = new Set([
    "tracking_started", "tracking_resumed", "tracking_paused", "tracking_stopped",
    "start_job", "job_paused", "end_work", "work_completed", "work_cancelled",
    "go_offline",
    "switch_job", "stop_changed", "device_switched",
  ]);
  const colorStartTypes = new Set([
    "start_job", "resume_work", "stop_changed",
  ]);
  const boundaryTimes = [];
  const colorStartTimes = [];
  (Array.isArray(statusEvents) ? statusEvents : []).forEach((event) => {
    const eventType = mapText(event?.eventType).toLowerCase();
    const observedAt = mapTimestamp(event?.observedAt);
    if (!observedAt) return;
    if (boundaryTypes.has(eventType)) boundaryTimes.push(observedAt);
    if (colorStartTypes.has(eventType)) colorStartTimes.push(observedAt);
  });

  (Array.isArray(stops) ? stops : []).forEach((stop) => {
    const startAt = mapTimestamp(stop?.stopStartTime ?? stop?.startedAt);
    const endedAt = mapTimestamp(stop?.endedAt);
    const durationMs = Math.max(0, Number(stop?.durationSeconds) || 0) * 1000;
    const endAt = endedAt || (durationMs ? startAt + durationMs : Date.now());
    if (endAt > startAt) colorStartTimes.push(endAt);
  });
  (Array.isArray(rests) ? rests : []).forEach((record) => {
    const startAt = mapTimestamp(record?.restStartTime);
    const endAt = mapTimestamp(record?.restEndTime) || Date.now();
    if (endAt > startAt) colorStartTimes.push(endAt);
  });

  const uniqueSortedTimes = (values) => [...new Set(values)].sort((a, b) => a - b);
  const boundaries = uniqueSortedTimes(boundaryTimes);
  const colorStarts = uniqueSortedTimes(colorStartTimes);
  const firstAt = mapTimestamp(baseAt) || Number(orderedPoints[0].observedAt);
  const colorStartOffset = colorStarts.filter((time) => time <= firstAt).length;
  const maximumRouteGapMs = 2 * 60 * 1000;
  const runs = [];
  let currentRun = [];
  const finishRun = () => {
    if (currentRun.length) runs.push(currentRun);
    currentRun = [];
  };

  for (let index = 1; index < orderedPoints.length; index += 1) {
    const left = orderedPoints[index - 1];
    const right = orderedPoints[index];
    const leftAt = Number(left.observedAt);
    const rightAt = Number(right.observedAt);
    const edgeDurationMs = rightAt - leftAt;
    const leftSegment = mapText(left.segmentId);
    const rightSegment = mapText(right.segmentId);
    const crossesBoundary = boundaries.some((time) => time > leftAt && time <= rightAt);
    const startsAtBoundary = boundaries.includes(leftAt);
    if (
      edgeDurationMs <= 0 ||
      edgeDurationMs > maximumRouteGapMs ||
      (leftSegment && rightSegment && leftSegment !== rightSegment) ||
      crossesBoundary
    ) {
      finishRun();
      continue;
    }
    if (startsAtBoundary) finishRun();
    currentRun.push({
      leftAt,
      positions: [[left.lat, left.lng], [right.lat, right.lng]],
    });
    if (colorStarts.some((time) => time > leftAt && time <= rightAt)) finishRun();
  }
  finishRun();

  return runs.flatMap((run) => {
    const runStartAt = run[0].leftAt;
    const colorIndex = Math.max(
      0,
      colorStarts.filter((time) => time <= runStartAt).length - colorStartOffset,
    );
    return run.map((edge, index) => ({
      positions: edge.positions,
      color: trackGradientColor(
        colorIndex,
        run.length > 1 ? index / (run.length - 1) : 0.5,
      ),
    }));
  });
}

function distanceKm(left, right) {
  if (!left || !right) return 0;

  const toRadians = (value) => (value * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRadians(right.lat - left.lat);
  const dLng = toRadians(right.lng - left.lng);
  const lat1 = toRadians(left.lat);
  const lat2 = toRadians(right.lat);
  const value =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return (
    earthRadiusKm * 2 * Math.asin(Math.sqrt(Math.min(1, Math.max(0, value))))
  );
}

function trackDistanceKm(points) {
  return (Array.isArray(points) ? points : []).reduce(
    (total, point, index, list) =>
      index === 0 ? total : total + distanceKm(list[index - 1], point),
    0,
  );
}

function historyEventLabel(eventType, event = {}) {
  const labels = {
    go_online: "上線",
    go_offline: "下線",
    start_job: "開始工作",
    job_paused: "暫停工作",
    take_break: "開始休息",
    resume_work: "返回工作",
    work_completed: "完成工作",
    work_cancelled: "取消工作",
    station_started: "開始站點作業",
    station_approaching: "前往站點",
    arrived: "抵達站點",
    station_completed: "完成站點",
    exception: "回報異常",
    stop_changed: "切換站點",
    inspection_started: "開始巡檢",
    inspection_resumed: "恢復巡檢",
    inspection_record_added: "新增巡檢紀錄",
    inspection_exception_reported: "回報巡檢異常",
    inspection_completed: "結束巡檢",
  };
  if (mapText(eventType) === "end_work") {
    return mapText(event?.category).toLowerCase() === "attendance" ||
      mapText(event?.attendanceStatus).toLowerCase() === "offline"
      ? "下線"
      : "完成工作";
  }
  return labels[mapText(eventType)] || mapText(eventType) || "狀態更新";
}

function historyClockTime(value) {
  const formatted = mapDateTime(value);
  return formatted === "—" ? formatted : formatted.slice(-5);
}

function dedupeHistoryEvents(events = []) {
  const unique = new Map();
  (Array.isArray(events) ? events : []).forEach((event) => {
    const eventType = mapText(event?.eventType);
    const observedAt = mapTimestamp(event?.observedAt);
    if (!eventType || !observedAt) return;
    const actionId = mapText(event?.actionId);
    const key = actionId || [
      mapText(event?.workDateKey),
      mapText(event?.applicationId),
      mapText(event?.stopId),
      eventType,
      observedAt,
    ].join("|");
    const previous = unique.get(key);
    if (!previous) {
      unique.set(key, event);
      return;
    }
    const preferred = event.source === "application" && previous.source !== "application"
      ? event
      : previous;
    const supplementary = preferred === previous ? event : previous;
    unique.set(key, {
      ...supplementary,
      ...preferred,
      actionId: preferred.actionId || supplementary.actionId,
      applicationTitle: preferred.applicationTitle || supplementary.applicationTitle,
      stopName: preferred.stopName || supplementary.stopName,
      stationNumber: preferred.stationNumber ?? supplementary.stationNumber,
      lat: preferred.lat ?? supplementary.lat,
      lng: preferred.lng ?? supplementary.lng,
      accuracy: preferred.accuracy ?? supplementary.accuracy,
      locationObservedAt: preferred.locationObservedAt ?? supplementary.locationObservedAt,
    });
  });
  return [...unique.values()].sort(
    (left, right) => mapTimestamp(left?.observedAt) - mapTimestamp(right?.observedAt),
  );
}

function buildHistoryTimelineItems(events = [], jobsById = {}, fallbackDateKey = "") {
  const workGroups = new Map();
  const standaloneEvents = [];
  const lifecycleTypes = new Set([
    "start_job", "job_paused", "end_work", "work_completed", "work_cancelled",
  ]);
  const stationTypes = new Set([
    "station_started", "station_approaching", "arrived", "station_completed",
    "exception", "stop_changed",
  ]);

  (Array.isArray(events) ? events : []).forEach((event) => {
    const applicationId = mapText(event?.applicationId);
    if (!applicationId || mapText(event?.category).toLowerCase() === "attendance") {
      standaloneEvents.push({
        type: "event",
        key: `event:${mapText(event?.id) || mapTimestamp(event?.observedAt)}`,
        event,
        sortAt: mapTimestamp(event?.observedAt),
      });
      return;
    }
    const workDateKey = mapText(event?.workDateKey) || fallbackDateKey;
    const key = `${workDateKey}:${applicationId}`;
    if (!workGroups.has(key)) {
      workGroups.set(key, {key, workDateKey, applicationId, events: []});
    }
    workGroups.get(key).events.push(event);
  });

  const items = [...standaloneEvents];
  workGroups.forEach((group) => {
    const orderedEvents = dedupeHistoryEvents(group.events);
    const job = jobsById[group.applicationId] || null;
    const stationGroups = new Map();
    const otherEvents = [];
    orderedEvents.forEach((event) => {
      const eventType = mapText(event?.eventType);
      if (lifecycleTypes.has(eventType)) return;
      const stopId = mapText(event?.stopId);
      const stopName = mapText(event?.stopName);
      if (!stationTypes.has(eventType) && !stopId && !stopName) {
        otherEvents.push(event);
        return;
      }
      const stationKey = stopId || stopName || `station:${eventType}`;
      if (!stationGroups.has(stationKey)) {
        stationGroups.set(stationKey, {key: stationKey, stopId, stopName, events: []});
      }
      stationGroups.get(stationKey).events.push(event);
    });

    const startEvent = orderedEvents.find((event) => ["start_job", "inspection_started"].includes(mapText(event?.eventType)));
    const lifecycleEnd = [...orderedEvents].reverse().find((event) =>
      ["job_paused", "end_work", "work_completed", "work_cancelled", "inspection_completed"].includes(mapText(event?.eventType)),
    );
    const firstEvent = orderedEvents[0];
    const lastEvent = orderedEvents[orderedEvents.length - 1];
    const workTitle = mapText(
      job?.applicationName || job?.Application_name || job?.jobTitle ||
      job?.cargoSnapshot?.name,
    ) || orderedEvents.map((event) => mapText(event?.applicationTitle)).find(Boolean) ||
      (job ? mapApplicationTitle(job) : "工作紀錄");
    const routeLabel = job ? mapApplicationRoute(job) : "";
    const isInspection = mapText(job?.workType).toUpperCase() === "INSPECTION" || mapText(job?.workMode).toUpperCase() === "ROUTE_RECORDING";
    const statusLabel = mapText(lifecycleEnd?.eventType) === "work_cancelled"
      ? "已取消"
      : ["end_work", "work_completed", "inspection_completed"].includes(mapText(lifecycleEnd?.eventType)) || job?.execution?.completedAt
        ? "已結束"
        : lifecycleEnd?.eventType === "job_paused"
          ? "已暫停"
          : "進行中";
    const stations = [...stationGroups.values()]
      .map((station) => {
        const firstStationEvent = station.events[0];
        const jobStop = (Array.isArray(job?.stops) ? job.stops : []).find((stop) =>
          mapText(stop?.id || stop?.stopId) === station.stopId,
        );
        const stopLabel = station.stopName || mapText(
          jobStop?.text || jobStop?.name || jobStop?.address || jobStop?.formattedAddress,
        ) || (station.stopId ? `站點 ${station.stopId}` : "站點作業");
        return {
          ...station,
          label: stopLabel,
          sortAt: mapTimestamp(firstStationEvent?.observedAt),
        };
      })
      .sort((left, right) => left.sortAt - right.sortAt);
    items.push({
      type: "work",
      key: `work:${group.key}`,
      applicationId: group.applicationId,
      workDateKey: group.workDateKey,
      job,
      title: workTitle,
      routeLabel: routeLabel === "" ? "" : routeLabel,
      statusLabel,
      isInspection,
      inspectionEventCount: isInspection ? orderedEvents.filter((event) => mapText(event?.eventType).startsWith("inspection_")).length : 0,
      startAt: mapTimestamp(startEvent?.observedAt) || mapTimestamp(firstEvent?.observedAt),
      endAt: mapTimestamp(lifecycleEnd?.observedAt) || mapTimestamp(lastEvent?.observedAt),
      stations,
      otherEvents,
      sortAt: mapTimestamp(firstEvent?.observedAt),
    });
  });

  return items.sort((left, right) => left.sortAt - right.sortAt);
}

function createTrackEndpointIcon(kind) {
  const isStart = kind === "start";
  return L.divIcon({
    className: "dmh-track-endpoint-shell",
    html: `<span class="dmh-track-endpoint ${isStart ? "is-start" : "is-end"}">${isStart ? "起" : "終"}</span>`,
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  });
}

function formatMapTime(value) {
  const millis = mapTimestamp(value);
  if (!millis) return "尚無更新";

  const minutes = Math.max(0, Math.floor((Date.now() - millis) / 60000));
  if (minutes < 1) return "剛剛更新";
  if (minutes < 60) return `${minutes} 分鐘前更新`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)} 小時前更新`;

  const date = new Date(millis);
  return `${date.getMonth() + 1}/${date.getDate()} ${String(
    date.getHours(),
  ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function formatDriverListTime(value) {
  const millis = mapTimestamp(value);
  if (!millis) return "尚無更新";

  const date = new Date(millis);
  return `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes(),
  ).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
}

function mapStatusKey(driver) {
  const attendanceStatus = mapText(driver?.attendanceStatus).toLowerCase();
  const workStatus = mapText(driver?.workStatus).toLowerCase();
  const tracking = driver?.currentTracking || {};
  const applicationId = mapText(
    tracking?.applicationId || driver?.activeApplicationId,
  );
  const trackingState = mapText(
    tracking?.locationState,
  ).toLowerCase();
  const hasWork =
    Boolean(applicationId) ||
    ["busy", "working", "on_job", "in_progress", "active"].includes(
      workStatus,
    );
  const hasActiveTrackingSession = Boolean(
    mapText(driver?.activeTrackingSessionId || tracking?.sessionId),
  );
  const isExplicitlyStopped =
    ["stopped", "stop", "offline", "ended", "end_work", "not_working"].includes(
      attendanceStatus,
    ) ||
    ["stopped", "stop", "offline", "ended", "end_work", "not_working"].includes(
      workStatus,
    ) ||
    ["stopped", "stop", "offline", "ended", "end_work"].includes(
      trackingState,
    );

  if (attendanceStatus === "break") return "break";
  if (attendanceStatus === "online" || attendanceStatus === "on") {
    return hasWork ? "working" : "online";
  }
  if (isExplicitlyStopped) return "offline";
  if (hasWork) return "working";
  if (hasActiveTrackingSession && trackingState !== "stopped") return "online";
  if (["available", "idle"].includes(attendanceStatus)) return "online";
  return "offline";
}

function mapStatusTheme(status) {
  if (status === "working") {
    return { label: "工作中", color: "#31B66A", soft: "#E8F8EE" };
  }
  if (status === "online") {
    return { label: "上線中", color: "#3B82F6", soft: "#EAF2FF" };
  }
  if (status === "break") {
    return { label: "休息中", color: "#D98B13", soft: "#FFF6DF" };
  }
  return { label: "離線", color: "#94A3B8", soft: "#F1F4F7" };
}

function mapCoordinate(raw) {
  const current =
    raw?.currentTracking ||
    (mapText(raw?.attendanceStatus).toLowerCase() === "break"
      ? raw?.lastKnownLocation
      : null) ||
    {};
  const latitude = mapNumber(current.lat);
  const longitude = mapNumber(current.lng);

  if (
    latitude === null ||
    longitude === null ||
    Math.abs(latitude) > 90 ||
    Math.abs(longitude) > 180 ||
    (latitude === 0 && longitude === 0)
  ) {
    return null;
  }

  return [latitude, longitude];
}

function liveStopPosition(driver) {
  const coordinate = driver?.coordinate;
  const lat = mapNumber(coordinate?.[0]);
  const lng = mapNumber(coordinate?.[1]);
  return lat === null || lng === null ? null : { lat, lng };
}

function liveStopHasMoved(previousStop, position) {
  if (!previousStop?.position || !position) return false;
  return distanceKm(previousStop.position, position) > 0.08;
}

function mapApplicationTitle(application) {
  return (
    mapText(application?.applicationName) ||
    mapText(application?.Application_name) ||
    mapText(application?.jobTitle) ||
    mapText(application?.cargoSnapshot?.name) ||
    (mapText(application?.jobCategory).toLowerCase() === "logistics"
      ? "物流配送"
      : "運輸工作")
  );
}

function mapApplicationRoute(application) {
  const stops = Array.isArray(application?.stops) ? application.stops : [];
  if (stops.length >= 2) {
    const getStop = (stop) =>
      mapText(stop?.text) ||
      mapText(stop?.address) ||
      mapText(stop?.formattedAddress) ||
      "未提供地址";
    return `${getStop(stops[0])} → ${getStop(stops[stops.length - 1])}`;
  }
  return mapText(application?.routeLabel) || "";
}

function mapVehicleLabel(application) {
  const vehicle = application?.vehicle || {};
  const trailer = application?.trailer || {};
  const plate = mapText(vehicle.plateNumber || vehicle.plateNo);
  const trailerPlate = mapText(trailer.plateNumber || trailer.plateNo);
  return [plate, trailerPlate ? `拖車 ${trailerPlate}` : ""]
    .filter(Boolean)
    .join("・");
}

function mapDriverFromDocument(id, raw, member = null) {
  const driver = raw || {};
  const latestUpdate = driver?.currentTracking?.observedAt || null;

  return {
    uid: mapText(driver?.uid) || id,
    displayName:
      mapText(driver?.displayName || driver?.name) ||
      mapText(member?.displayName || member?.name) ||
      "未命名成員",
    phone: mapText(driver?.phone),
    avatarUrl: imageUrl({ ...driver, ...(member || {}) }),
    statusKey: mapStatusKey(driver),
    coordinate: mapCoordinate(driver),
    latestUpdate,
    applicationId: mapText(driver?.currentTracking?.applicationId),
    workDateKey: mapText(driver?.currentTracking?.workDateKey || driver?.workDateKey),
    stopId: mapText(driver?.currentTracking?.stopId),
    speed: mapNumber(driver?.currentTracking?.speed),
    observedAt: driver?.currentTracking?.observedAt || null,
    raw: driver,
  };
}

function mapCurrentDriverWorkLookup(driver, orgId) {
  const raw = driver?.raw || {};
  const tracking = raw.currentTracking || {};
  const applicationId = mapText(
    driver?.applicationId || tracking.applicationId || raw.activeApplicationId,
  );
  const observedAt = mapTimestamp(driver?.observedAt || tracking.observedAt);
  const dateKeys = [...new Set([
    mapText(driver?.workDateKey || tracking.workDateKey || raw.workDateKey),
    observedAt ? mapDateKey(observedAt) : "",
  ].filter(Boolean))];
  return {
    applicationId,
    dateKeys,
    cacheKey: `${mapText(orgId)}:${applicationId}:${dateKeys.join("|")}`,
    inlineTitle: mapText(
      tracking.applicationTitle || tracking.jobTitle || raw.activeApplicationTitle || raw.applicationTitle,
    ),
  };
}

function markerHtmlEscape(value) {
  return mapText(value).replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[character]);
}

function createDriverMarkerIcon(driver, selected, opacity = 1, options = {}) {
  const theme = mapStatusTheme(driver.statusKey);
  const initial = mapText(driver.displayName).slice(0, 1) || "司";
  const avatarUrl = mapText(driver.avatarUrl);
  const avatarSize = avatarUrl ? (selected ? 58 : 50) : (selected ? 54 : 46);
  const speedValue = mapNumber(driver.speed);
  const currentTracking = driver.raw?.currentTracking || {};
  const isRestOrStopped =
    driver.statusKey === "break" ||
    ["stopped", "paused"].includes(mapText(currentTracking.locationState).toLowerCase());
  const showSpeed = driver.showSpeed ?? (driver.statusKey === "working" && !isRestOrStopped && speedValue > 0);
  const workName = options.showWorkName === false || !["working", "break"].includes(driver.statusKey)
    ? ""
    : mapText(driver.currentWorkName);
  const workLabelExtraHeight = workName ? 23 : 0;
  const safeWorkName = markerHtmlEscape(workName);
  const workNameMarkup = workName
    ? `<span class="dmh-driver-work-label" title="${safeWorkName}"><i aria-hidden="true"></i><span>${safeWorkName}</span></span>`
    : "";
  const markerWidth = showSpeed ? avatarSize + 18 : avatarSize;
  const speedRowExtraHeight = showSpeed ? 27 : 0;
  const markerHeight = avatarSize + speedRowExtraHeight + workLabelExtraHeight;
  const markerAnchorY = avatarSize / 2 + speedRowExtraHeight + workLabelExtraHeight;
  const markerOpacity = Math.min(1, Math.max(0, Number(opacity) || 0));
  const speedMarkup = showSpeed
    ? `<span class="dmh-speed-badge"><b>${formatSpeedKmh(speedValue)}</b></span>`
    : "";

  if (avatarUrl) {
    return L.divIcon({
      className: "dmh-photo-marker-host",
      iconSize: [markerWidth, markerHeight],
      iconAnchor: [markerWidth / 2, markerAnchorY],
      popupAnchor: [0, -markerAnchorY],
      html: `<span class="dmh-speed-marker" style="opacity:${markerOpacity};--marker-color:${theme.color}">${workNameMarkup}${speedMarkup}<img class="dmh-photo-marker${selected ? " is-selected" : ""}" style="--marker-color:${theme.color}" src="${markerImageSrc(avatarUrl)}" alt="" /></span>`,
    });
  }

  return L.divIcon({
      className: "dmh-driver-marker-shell",
      iconSize: [markerWidth, markerHeight],
      iconAnchor: [markerWidth / 2, markerAnchorY],
      html: `<span class="dmh-speed-marker" style="opacity:${markerOpacity};--marker-color:${theme.color}">${workNameMarkup}${speedMarkup}<span class="dmh-driver-marker${selected ? " is-selected" : ""}" style="--marker-color:${theme.color};--marker-soft:${theme.soft}"><span class="dmh-driver-avatar-fallback">${initial}</span><i></i></span></span>`,
    });
}

function MapViewport({ selectedDriver, points, fitAllRequestKey = 0 }) {
  const map = useMap();
  const focusedItemKeyRef = useRef("");
  const lastFitAllRequestRef = useRef(null);
  const selectedItemPresent = Boolean(selectedDriver);
  const selectedItemKey = mapText(selectedDriver?.uid || selectedDriver?.assetId || selectedDriver?.id);
  const selectedLatitude = selectedDriver?.coordinate?.[0];
  const selectedLongitude = selectedDriver?.coordinate?.[1];
  const selectedFocusKey = selectedItemKey || (
    selectedLatitude !== undefined && selectedLongitude !== undefined
      ? `${selectedLatitude}:${selectedLongitude}`
      : ""
  );

  useEffect(() => {
    if (selectedItemPresent) {
      if (selectedLatitude === undefined || selectedLongitude === undefined || !selectedFocusKey || focusedItemKeyRef.current === selectedFocusKey) return;
      focusedItemKeyRef.current = selectedFocusKey;
      map.flyTo([selectedLatitude, selectedLongitude], 18, {
        animate: true,
        duration: 0.45,
      });
      return;
    }

    if (!points.length) return;
    const needsFitAll = Boolean(focusedItemKeyRef.current) ||
      lastFitAllRequestRef.current !== fitAllRequestKey;
    if (!needsFitAll) return;
    if (points.length === 1) {
      map.setView(points[0], Math.min(13, map.getMaxZoom()), { animate: false });
    } else {
      map.fitBounds(points, { padding: [35, 35], maxZoom: 13, animate: false });
    }
    focusedItemKeyRef.current = "";
    lastFitAllRequestRef.current = fitAllRequestKey;
  }, [fitAllRequestKey, map, points, selectedItemPresent, selectedFocusKey, selectedLatitude, selectedLongitude]);

  return null;
}

function MapCenterWheelZoom() {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    let accumulatedDelta = 0;
    let zoomTimer = null;

    const handleWheel = (event) => {
      if (!event.deltaY) return;
      event.preventDefault();
      const deltaY = event.deltaMode === 1
        ? event.deltaY * 16
        : event.deltaMode === 2
          ? event.deltaY * container.clientHeight
          : event.deltaY;
      accumulatedDelta += deltaY;
      if (zoomTimer) window.clearTimeout(zoomTimer);
      zoomTimer = window.setTimeout(() => {
        const delta = accumulatedDelta;
        accumulatedDelta = 0;
        zoomTimer = null;
        if (!delta) return;
        const zoomSteps = Math.max(1, Math.round(Math.abs(delta) / 120));
        const direction = delta > 0 ? -1 : 1;
        const nextZoom = Math.max(
          map.getMinZoom(),
          Math.min(map.getMaxZoom(), map.getZoom() + direction * zoomSteps),
        );
        if (nextZoom !== map.getZoom()) {
          map.setZoom(nextZoom, { animate: false });
        }
      }, 70);
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      container.removeEventListener("wheel", handleWheel);
      if (zoomTimer) window.clearTimeout(zoomTimer);
    };
  }, [map]);

  return null;
}

function MapMarkerProjectionSync() {
  const map = useMap();

  useEffect(() => {
    let frameId = 0;
    const syncMarkerProjection = () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        map.invalidateSize({ animate: false, pan: false });
        map.eachLayer((layer) => {
          if (layer instanceof L.Marker) layer.update();
        });
      });
    };

    map.on("zoomend", syncMarkerProjection);
    return () => {
      map.off("zoomend", syncMarkerProjection);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [map]);

  return null;
}

function MapSizeSync({ layoutKey }) {
  const map = useMap();

  useEffect(() => {
    let debounceId = null;
    const invalidateSize = () => {
      map.invalidateSize({ animate: false, pan: false });
    };
    const scheduleInvalidate = () => {
      if (debounceId) window.clearTimeout(debounceId);
      debounceId = window.setTimeout(invalidateSize, 80);
    };
    const container = map.getContainer();
    const observer = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(scheduleInvalidate)
      : null;
    observer?.observe(container);
    window.addEventListener("resize", scheduleInvalidate);
    const transitionTimers = [0, 180, 420].map((delay) =>
      window.setTimeout(invalidateSize, delay),
    );

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", scheduleInvalidate);
      if (debounceId) window.clearTimeout(debounceId);
      transitionTimers.forEach((timerId) => window.clearTimeout(timerId));
    };
  }, [layoutKey, map]);

  return null;
}

function HistoryMapFocus({ target }) {
  const map = useMap();

  useEffect(() => {
    if (!target?.lat || !target?.lng) return;
    map.flyTo([target.lat, target.lng], Math.max(map.getZoom(), 15), {
      animate: true,
      duration: 0.55,
    });
  }, [map, target]);

  return null;
}

function PlaybackFollowController({ point, enabled, onDisable }) {
  const map = useMap();

  useEffect(() => {
    if (!enabled || !point) return;
    const target = L.latLng(point.lat, point.lng);
    if (map.getCenter().distanceTo(target) < 0.5) return;
    map.panTo(target, { animate: false });
  }, [enabled, map, point?.lat, point?.lng]);

  useEffect(() => {
    const handleMapClick = (event) => {
      const target = event.originalEvent?.target;
      if (target?.closest?.(".dmh-photo-marker-host, .dmh-driver-marker-shell")) return;
      onDisable(false);
    };
    map.on("click", handleMapClick);
    return () => map.off("click", handleMapClick);
  }, [map, onDisable]);

  return null;
}

function vehicleStatusTheme(statusKey) {
  if (statusKey === "working") {
    return { label: "使用中", color: "#31B66A", soft: "#E8F8EE" };
  }
  if (statusKey === "parked") {
    return { label: "停放中", color: "#3B82F6", soft: "#EAF2FF" };
  }
  if (statusKey === "maintenance") {
    return { label: "維修中", color: "#F68B2C", soft: "#FFF1E5" };
  }
  if (statusKey === "retired") {
    return { label: "已報廢", color: "#64748B", soft: "#F1F5F9" };
  }
  return { label: "可使用", color: "#94A3B8", soft: "#F1F4F7" };
}

function vehicleTypeLabel(assetType, assetKind = "") {
  const kind = mapText(assetKind).toLowerCase();
  if (kind === "vehicle") return "車輛";
  if (kind === "trailer") return "托架";
  if (kind === "container") return "容器";
  if (kind === "equipment") return "設備";
  return {
    INTEGRATED_VEHICLE: "一體式車輛",
    TRACTOR: "曳引車",
    SEMI_TRAILER: "半拖車",
  }[mapText(assetType).toUpperCase()] || "車隊資產";
}

function normalizeVehicleAssetKind(asset = {}) {
  const kind = mapText(asset.assetKind).toLowerCase();
  if (["vehicle", "trailer", "container", "equipment"].includes(kind)) return kind;
  const type = mapText(asset.assetType).toUpperCase().replace(/[\s_\/-]/g, "");
  if (type.includes("TRAILER")) return "trailer";
  if (["FUELTANK", "CHEMICALTANK", "WATERTANK", "WASTETANK", "CARGOCONTAINER"].includes(type)) return "container";
  if (["PALLET", "CAGECART", "PUMPEQUIPMENT", "GENERATOR", "IBCCONTAINER"].includes(type)) return "equipment";
  return "vehicle";
}

function normalizeVehicleMasterStatus(value) {
  const status = mapText(value).toUpperCase();
  if (["PARKED", "IN_USE", "MAINTENANCE", "RETIRED"].includes(status)) return status;
  if (["ATTACHED", "INUSE", "ASSIGNED"].includes(status)) return "IN_USE";
  return "PARKED";
}

function normalizeVehicleAssets(documents = []) {
  return documents
    .map((document) => {
      const data = typeof document?.data === "function" ? document.data() || {} : document || {};
      const assetId = mapText(document?.id || data.assetId || data.id);
      const status = normalizeVehicleMasterStatus(data.status || data.currentState);
      const parkedLocation = data.parkedLocation && typeof data.parkedLocation === "object"
        ? data.parkedLocation
        : {};
      return {
        ...data,
        id: assetId,
        assetId,
        assetKind: normalizeVehicleAssetKind(data),
        assetType: mapText(data.assetType).toUpperCase(),
        assetCode: mapText(data.assetCode),
        displayName: mapText(data.displayName),
        plateNumber: mapText(data.plateNumber || data.plateNo),
        photoUrl: imageUrl(data),
        status,
        currentState: status,
        currentDriverId: mapText(data.currentDriverId || data.driverId),
        currentDriverName: mapText(data.currentDriverName),
        currentVehicleId: mapText(data.currentVehicleId),
        parkedLocation,
        parkedLocationName: mapText(parkedLocation.name || data.parkedLocationName),
        trailerBindingCount: Number(data.trailerBindingCount) || 0,
        hasBoundTrailer: Boolean(data.hasBoundTrailer) || Number(data.trailerBindingCount) > 0,
      };
    })
    .filter((asset) => asset.assetId);
}

function vehicleCoordinateFromValue(value) {
  const candidates = [
    value?.mapPosition,
    value?.locationSnapshot,
    value?.coordinate,
    value?.location,
    value?.lastKnownLocation,
    value?.parkedLocation,
    value,
  ];
  for (const location of candidates) {
    const lat = mapNumber(location?.lat ?? location?.latitude);
    const lng = mapNumber(location?.lng ?? location?.longitude);
    if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) continue;
    return [lat, lng];
  }
  return null;
}

function vehicleDisplayIdentifier(vehicle) {
  const plate = mapText(vehicle?.plateNumber || vehicle?.plateNo);
  if (plate) return plate;
  const codes = [vehicle?.assetCode, vehicle?.assetId, vehicle?.id]
    .map((value) => mapText(value).toUpperCase())
    .filter((value) => /^[A-Z]{6}$/.test(value));
  return codes[0] || "未設定六碼編號";
}

function vehicleParkingDetails(vehicle, now = Date.now()) {
  if (vehicle?.statusKey !== "parked") return "";
  const startedAtMs = mapTimestamp(vehicle?.parkedLocation?.startedAt) || mapTimestamp(vehicle?.locationUpdatedAt);
  if (!startedAtMs) return { duration: "停留時間未知", parkedAt: "停放時間未記錄" };
  const durationSeconds = Math.max(0, Math.floor((now - startedAtMs) / 1000));
  const elapsedDays = Math.floor(durationSeconds / (24 * 60 * 60));
  const duration = elapsedDays >= 1
    ? `停留 ${elapsedDays === 1 ? "一天前" : elapsedDays === 2 ? "兩天前" : `${elapsedDays} 天前`}`
    : `停留 ${formatElapsedDuration(durationSeconds)}`;
  return {
    duration,
    parkedAt: `停放 ${mapDateTime(startedAtMs)}`,
  };
}

function vehicleParkingSummary(vehicle, now = Date.now()) {
  const details = vehicleParkingDetails(vehicle, now);
  return details ? `${details.duration} · ${details.parkedAt}` : "";
}

function vehicleCoordinatesLabel(vehicle) {
  const lat = mapNumber(vehicle?.coordinate?.[0]);
  const lng = mapNumber(vehicle?.coordinate?.[1]);
  if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180) return "";
  return `座標 ${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

function normalizeVehicleState(
  asset,
  state = {},
  driverById = {},
  driverByVehicleId = {},
  vehicleStatesMap = {},
  assetsById = {},
) {
  const parentVehicleId = mapText(asset?.currentVehicleId);
  const parentAsset = parentVehicleId ? assetsById[parentVehicleId] : null;
  const parentState = parentVehicleId ? vehicleStatesMap[parentVehicleId] || {} : {};
  const ownVehicleDriver = driverByVehicleId[asset?.assetId];
  const parentVehicleDriver = parentVehicleId ? driverByVehicleId[parentVehicleId] : null;
  const driverId = mapText(
    ownVehicleDriver?.uid ||
    parentVehicleDriver?.uid ||
    asset?.currentDriverId ||
    state?.currentDriverId ||
    parentAsset?.currentDriverId ||
    parentState?.currentDriverId,
  );
  const driver = ownVehicleDriver || parentVehicleDriver || (driverId ? driverById[driverId] : null);
  const rawState = mapText(asset?.status || state?.state || state?.status || asset?.currentState).toUpperCase();
  const statusKey =
    rawState === "MAINTENANCE" || rawState === "REPAIR"
      ? "maintenance"
      : rawState === "RETIRED"
        ? "retired"
        : driverId || parentVehicleId || ["IN_USE", "WORKING", "BUSY", "ACTIVE"].includes(rawState)
          ? "working"
          : rawState === "PARKED" || rawState === "STORED"
            ? "parked"
            : "available";
  const parkedLocation = asset?.parkedLocation || state?.parkedLocation || {};
  const parentParkedCoordinate = vehicleCoordinateFromValue(parentAsset?.parkedLocation);
  const parentStateCoordinate = vehicleCoordinateFromValue(parentState);
  const parentCoordinate = parentVehicleDriver?.coordinate ||
    (parentAsset?.status === "PARKED"
      ? parentParkedCoordinate || parentStateCoordinate
      : parentStateCoordinate || parentParkedCoordinate);
  const masterParkedCoordinate = vehicleCoordinateFromValue(asset?.parkedLocation);
  const stateCoordinate = vehicleCoordinateFromValue(state);
  const coordinate =
    driver?.coordinate ||
    parentCoordinate ||
    (statusKey === "parked"
      ? masterParkedCoordinate || stateCoordinate || vehicleCoordinateFromValue(parkedLocation)
      : stateCoordinate || masterParkedCoordinate || vehicleCoordinateFromValue(parkedLocation));

  return {
    ...asset,
    statusKey,
    coordinate,
    currentDriverId: driverId,
    currentDriverName:
      mapText(state?.currentDriverName) ||
      mapText(asset?.currentDriverName) ||
      mapText(driver?.displayName),
    parkedLocationName:
      mapText(parkedLocation?.name) ||
      mapText(state?.parkedLocation?.name) ||
      mapText(parentAsset?.parkedLocation?.name) ||
      mapText(asset?.parkedLocationName),
    locationSource: driver ? "DRIVER" : parentVehicleId ? "VEHICLE" : statusKey === "parked" ? "PARKED" : "",
    latestUpdate:
      state?.updatedAt ||
      state?.locationUpdatedAt ||
      driver?.latestUpdate ||
      asset?.updatedAt ||
      null,
  };
}

function groupVehiclesByLocation(vehicles = []) {
  const groups = new Map();
  vehicles.forEach((vehicle) => {
    if (!vehicle?.coordinate) return;
    const key = vehicle.coordinate.map((value) => Number(value).toFixed(5)).join(",");
    const group = groups.get(key) || {
      key,
      coordinate: vehicle.coordinate,
      locationName: mapText(vehicle.parkedLocationName) || "目前位置",
      vehicles: [],
    };
    group.vehicles.push(vehicle);
    groups.set(key, group);
  });
  return Array.from(groups.values());
}

function createVehicleMarkerIcon(vehicle, selected) {
  const displayIdentifierText = vehicleDisplayIdentifier(vehicle);
  const displayIdentifier = markerHtmlEscape(displayIdentifierText);
  const parkingDetails = vehicleParkingDetails(vehicle);
  const durationText = parkingDetails?.duration || "";
  const parkedAtText = parkingDetails?.parkedAt || "";
  const estimateTextWidth = (value) => Array.from(value).reduce(
    (width, character) => width + (character.charCodeAt(0) > 255 ? 8 : 5.5),
    20,
  );
  const markerWidth = Math.min(
    210,
    Math.max(90, estimateTextWidth(`${durationText} ${displayIdentifierText}`), parkedAtText ? estimateTextWidth(parkedAtText) : 0),
  );
  const theme = vehicleStatusTheme(vehicle.statusKey);
  const markerHeight = (parkedAtText ? 44 : 26) + 26;
  const parkingMeta = parkedAtText
    ? `<span class="dmh-vehicle-parking-meta">${markerHtmlEscape(parkedAtText)}</span>`
    : "";
  const identifierRow = `<span class="dmh-vehicle-marker-main${selected ? " is-selected" : ""}" style="--marker-color:${theme.color}">${durationText ? `<span class="dmh-vehicle-marker-duration">${markerHtmlEscape(durationText)}</span>` : ""}<strong class="dmh-vehicle-plate">${displayIdentifier}</strong></span>`;
  const coordinatePoint = `<span class="dmh-vehicle-marker-point">P</span>`;

  return L.divIcon({
    className: "dmh-vehicle-label-marker-host",
    iconSize: [markerWidth, markerHeight],
    iconAnchor: [markerWidth / 2, markerHeight - 13],
    popupAnchor: [0, -(markerHeight - 26)],
    html: `<span class="dmh-vehicle-label-marker" style="--marker-width:${markerWidth}px">${identifierRow}${parkingMeta}${coordinatePoint}</span>`,
  });
}

function createVehicleClusterIcon(vehicles) {
  const colors = vehicles.map((vehicle) => vehicleStatusTheme(vehicle.statusKey).color);
  const segmentSize = 100 / Math.max(colors.length, 1);
  const background = `conic-gradient(${colors
    .map((color, index) => `${color} ${index * segmentSize}% ${(index + 1) * segmentSize}%`)
    .join(",")})`;

  return L.divIcon({
    className: "dmh-vehicle-cluster-marker-host",
    iconSize: [54, 54],
    iconAnchor: [27, 27],
    popupAnchor: [0, -27],
    html: `<span class="dmh-vehicle-cluster-marker" style="--cluster-background:${background}"><b>${vehicles.length}</b></span>`,
  });
}

function VehicleMapMarkers({ groups, selectedAssetId, onSelect }) {
  return (
    <>
      {groups.map((group) => {
        if (group.vehicles.length === 1) {
          const vehicle = group.vehicles[0];
          const coordinates = vehicleCoordinatesLabel(vehicle);
          return (
            <Marker
              key={group.key}
              position={group.coordinate}
              icon={createVehicleMarkerIcon(vehicle, vehicle.assetId === selectedAssetId)}
              eventHandlers={{ click: () => onSelect(vehicle) }}
            >
              <Popup>
                <div className="dmh-popup">
                  <strong>{vehicleDisplayIdentifier(vehicle)}</strong>
                  <span>{vehicle.statusKey === "parked" ? vehicleParkingSummary(vehicle) : `${vehicleStatusTheme(vehicle.statusKey).label}・${formatMapTime(vehicle.latestUpdate)}`}</span>
                  {coordinates ? <small style={{ color: "#64748b", fontSize: 10 }}>{coordinates}</small> : null}
                  <b>{vehicleTypeLabel(vehicle.assetType, vehicle.assetKind)}</b>
                </div>
              </Popup>
            </Marker>
          );
        }

        return (
          <Marker
            key={group.key}
            position={group.coordinate}
            icon={createVehicleClusterIcon(group.vehicles)}
          >
            <Popup>
              <div className="dmh-vehicle-group-popup">
                <strong>{group.locationName}，{group.vehicles.length} 項資產</strong>
                <div>
                  {group.vehicles.map((vehicle) => {
                    const parkingSummary = vehicleParkingSummary(vehicle);
                    const coordinates = vehicleCoordinatesLabel(vehicle);
                    return (
                      <button
                        key={vehicle.assetId}
                        type="button"
                        onClick={() => onSelect(vehicle)}
                      >
                        <span style={{ display: "grid", gap: 2, minWidth: 0 }}>
                          <strong style={{ fontSize: 11, fontWeight: 900 }}>{vehicleDisplayIdentifier(vehicle)}</strong>
                          {parkingSummary ? <small style={{ color: "#64748b", fontSize: 9, fontWeight: 700 }}>{parkingSummary}</small> : null}
                          {coordinates ? <small style={{ color: "#64748b", fontSize: 9, fontWeight: 700 }}>{coordinates}</small> : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}

function LiveFleetMap({ user: propUser, account, onOpenJob, mapExpanded, onMapExpandedChange }) {
  const [authUser, setAuthUser] = useState(
    propUser || auth.currentUser || null,
  );
  const [claimOrgId, setClaimOrgId] = useState("");
  const [accountReady, setAccountReady] = useState(Boolean(account?.orgId));
  const [driversMap, setDriversMap] = useState({});
  const [orgMembersMap, setOrgMembersMap] = useState({});
  const [driverVehiclesById, setDriverVehiclesById] = useState({});
  const [currentWorkTitlesByKey, setCurrentWorkTitlesByKey] = useState({});
  const currentWorkTitleCacheRef = useRef(new Map());
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [isDriverPickerOpen, setIsDriverPickerOpen] = useState(false);
  const [fitAllRequestKey, setFitAllRequestKey] = useState(0);
  const [liveRestStartAt, setLiveRestStartAt] = useState(0);
  const liveStopContinuityRef = useRef(new Map());
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedApplicationLoading, setSelectedApplicationLoading] = useState(false);
  const [selectedApplicationError, setSelectedApplicationError] = useState("");
  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [vehicleFilter, setVehicleFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState("");
  const [clockTick, setClockTick] = useState(0);
  const [overviewMode, setOverviewMode] = useState("drivers");
  const [viewMode, setViewMode] = useState("live");
  const handleFitAllMap = useCallback(() => {
    setSelectedDriverId("");
    setSelectedAssetId("");
    setFitAllRequestKey((current) => current + 1);
  }, []);
  const [vehicleAssetItems, setVehicleAssetItems] = useState([]);
  const [vehicleStatesMap, setVehicleStatesMap] = useState({});
  const [vehicleBindingsMap, setVehicleBindingsMap] = useState({});
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [vehicleAssetsReady, setVehicleAssetsReady] = useState(false);
  const [vehicleStatesReady, setVehicleStatesReady] = useState(false);
  const [vehicleBindingsReady, setVehicleBindingsReady] = useState(false);
  const [vehicleError, setVehicleError] = useState("");
  const [trackDate, setTrackDate] = useState(new Date());
  const [trackPoints, setTrackPoints] = useState([]);
  const [trackEvents, setTrackEvents] = useState([]);
  const [restRecords, setRestRecords] = useState([]);
  const [historyJobsById, setHistoryJobsById] = useState({});
  const [trackSummaryData, setTrackSummaryData] = useState(null);
  const [detailTrackPoints, setDetailTrackPoints] = useState([]);
  const [detailStartTime, setDetailStartTime] = useState("");
  const [detailEndTime, setDetailEndTime] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [isDetailRangeMenuOpen, setIsDetailRangeMenuOpen] = useState(false);
  const detailRangeDefaultsKeyRef = useRef("");
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackError, setTrackError] = useState("");
  const [playbackProgress, setPlaybackProgress] = useState(1);
  const [isPlaybackPlaying, setIsPlaybackPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isPlaybackFollowEnabled, setIsPlaybackFollowEnabled] = useState(false);
  const [selectedHistoryMarker, setSelectedHistoryMarker] = useState(null);
  const [inspectionDetail, setInspectionDetail] = useState(null);
  const [inspectionDetailLoading, setInspectionDetailLoading] = useState(false);
  const [inspectionDetailError, setInspectionDetailError] = useState("");
  const [historyWorkMedia, setHistoryWorkMedia] = useState({});
  const [historyWorkMediaLoading, setHistoryWorkMediaLoading] = useState(false);
  const [historyWorkPhotoViewer, setHistoryWorkPhotoViewer] = useState(null);
  const inspectionDetailRequestRef = useRef(0);
  const inspectionDetailCacheRef = useRef(new Map());
  const historyWorkMediaRequestRef = useRef(0);
  const user = propUser || authUser;
  const orgId = mapText(account?.orgId || user?.orgId || claimOrgId);

  useEffect(() => {
    if (propUser) {
      setAuthUser(propUser);
      return undefined;
    }
    return onAuthStateChanged(auth, (firebaseUser) => {
      setAuthUser(firebaseUser || null);
    });
  }, [propUser]);

  useEffect(() => {
    let alive = true;
    if (account?.orgId) {
      setClaimOrgId("");
      setAccountReady(true);
      return undefined;
    }
    if (!user?.uid || typeof user.getIdTokenResult !== "function") {
      setClaimOrgId("");
      setAccountReady(true);
      return undefined;
    }

    setAccountReady(false);
    user
      .getIdTokenResult()
      .then((tokenResult) => {
        if (!alive) return;
        setClaimOrgId(mapText(tokenResult?.claims?.orgId));
        setAccountReady(true);
      })
      .catch((error) => {
        console.error("[DriverManagerHome] account load error:", error);
        if (alive) {
          setClaimOrgId("");
          setAccountReady(true);
        }
      });

    return () => {
      alive = false;
    };
  }, [account, user?.uid]);

  useEffect(() => {
    const timer = window.setInterval(
      () => setClockTick((value) => value + 1),
      30000,
    );
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!accountReady) return undefined;
    if (!orgId) {
      setDriversMap({});
      setLoading(false);
      setDataError("找不到目前管理員的組織識別");
      return undefined;
    }

    setLoading(true);
    setDataError("");
    const driversQuery = query(
      collection(db, "drivers"),
      where("orgId", "==", orgId),
    );
    const unsubscribe = onSnapshot(
      driversQuery,
      (snapshot) => {
        const next = {};
        snapshot.docs.forEach((item) => {
          next[item.id] = { uid: item.id, ...(item.data() || {}) };
        });
        setDriversMap(next);
        setLoading(false);
      },
      (error) => {
        console.error("[DriverManagerHome] drivers snapshot error:", error);
        setDriversMap({});
        setLoading(false);
        setDataError("成員位置資料暫時無法讀取");
      },
    );

    return unsubscribe;
  }, [accountReady, orgId]);

  useEffect(() => {
    setOrgMembersMap({});
    if (!accountReady || !orgId) return undefined;

    const membersRef = collection(db, "orgs", orgId, "members");
    const unsubscribe = onSnapshot(
      membersRef,
      (snapshot) => {
        const next = {};
        snapshot.docs.forEach((item) => {
          const member = item.data() || {};
          next[item.id] = {
            ...member,
            uid: mapText(member.uid) || item.id,
          };
        });
        setOrgMembersMap(next);
      },
      (error) => {
        console.error("[DriverManagerHome] members snapshot error:", error);
        setOrgMembersMap({});
      },
    );

    return unsubscribe;
  }, [accountReady, orgId]);

  const drivers = useMemo(() => {
    return Object.entries(orgMembersMap)
      .map(([memberId, member]) => {
        if (mapText(member?.role).toLowerCase() !== "driver") return null;
        const uid = mapText(member?.uid) || memberId;
        const raw = driversMap[uid] || {};
        const driver = {
          ...mapDriverFromDocument(uid, raw, member),
          uid,
        };
        const workLookup = mapCurrentDriverWorkLookup(driver, orgId);
        const currentWorkName = ["working", "break"].includes(driver.statusKey) && workLookup.applicationId
          ? workLookup.inlineTitle || mapText(currentWorkTitlesByKey[workLookup.cacheKey])
          : "";
        return {
          ...driver,
          phone: mapText(raw?.phone || raw?.phoneNumber || member?.phone || member?.phoneNumber),
          currentWorkName,
        };
      })
      .filter(Boolean)
      .sort((left, right) => {
        const order = { working: 0, online: 1, break: 2, offline: 3 };
        const statusDiff =
          (order[left.statusKey] ?? 9) - (order[right.statusKey] ?? 9);
        if (statusDiff !== 0) return statusDiff;
        return (
          mapTimestamp(right.latestUpdate) - mapTimestamp(left.latestUpdate)
        );
      });
  }, [currentWorkTitlesByKey, driversMap, orgId, orgMembersMap]);

  const assignedVehicleIdsKey = useMemo(
    () => JSON.stringify([...new Set(drivers.map((driver) => mapText(driver.raw?.currentVehicleId)).filter(Boolean))].sort()),
    [drivers],
  );

  useEffect(() => {
    if (!accountReady || !orgId || overviewMode !== "drivers") {
      setDriverVehiclesById({});
      return undefined;
    }
    const vehicleIds = JSON.parse(assignedVehicleIdsKey);
    if (!vehicleIds.length) {
      setDriverVehiclesById({});
      return undefined;
    }
    let active = true;
    setDriverVehiclesById({});
    const unsubscribes = vehicleIds.map((vehicleId) => onSnapshot(
      doc(db, "orgs", orgId, "vehicles", vehicleId),
      (snapshot) => {
        if (!active) return;
        if (!snapshot.exists()) {
          setDriverVehiclesById((current) => ({ ...current, [vehicleId]: { notFound: true } }));
          return;
        }
        const vehicle = snapshot.data() || {};
        setDriverVehiclesById((current) => ({
          ...current,
          [vehicleId]: { plateNumber: mapText(vehicle.plateNumber || vehicle.plateNo) },
        }));
      },
      (error) => {
        if (!active) return;
        console.warn("[DriverManagerHome] assigned vehicle lookup failed:", vehicleId, error);
        setDriverVehiclesById((current) => ({ ...current, [vehicleId]: { error: true } }));
      },
    ));
    return () => {
      active = false;
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [accountReady, assignedVehicleIdsKey, orgId, overviewMode]);

  useEffect(() => {
    if (!orgId || overviewMode !== "drivers") return undefined;
    let active = true;
    const lookups = new Map();
    Object.entries(driversMap).forEach(([id, raw]) => {
      const driver = mapDriverFromDocument(id, raw);
      if (!["working", "break"].includes(driver.statusKey)) return;
      const lookup = mapCurrentDriverWorkLookup(driver, orgId);
      if (!lookup.applicationId || !lookup.dateKeys.length || lookup.inlineTitle) return;
      lookups.set(lookup.cacheKey, lookup);
    });

    lookups.forEach((lookup, cacheKey) => {
      let request = currentWorkTitleCacheRef.current.get(cacheKey);
      if (!currentWorkTitleCacheRef.current.has(cacheKey)) {
        request = (async () => {
          for (const dateKey of lookup.dateKeys) {
            const snapshot = await getDoc(
              doc(db, "orgs", orgId, "applications", dateKey, "jobs", lookup.applicationId),
            );
            if (snapshot.exists()) return mapApplicationTitle(snapshot.data() || {});
          }
          return "";
        })()
          .then((title) => {
            currentWorkTitleCacheRef.current.set(cacheKey, title);
            return title;
          })
          .catch((error) => {
            currentWorkTitleCacheRef.current.delete(cacheKey);
            console.warn("[DriverManagerHome] current work title lookup failed:", error);
            return "";
          });
        currentWorkTitleCacheRef.current.set(cacheKey, request);
      }
      Promise.resolve(request).then((title) => {
        if (!active || !title) return;
        setCurrentWorkTitlesByKey((current) =>
          current[cacheKey] === title ? current : { ...current, [cacheKey]: title },
        );
      });
    });

    return () => {
      active = false;
    };
  }, [driversMap, orgId, overviewMode]);

  const driverById = useMemo(
    () => Object.fromEntries(drivers.map((driver) => [driver.uid, driver])),
    [drivers],
  );

  const driverByVehicleId = useMemo(() => {
    const next = {};
    drivers.forEach((driver) => {
      const vehicleId = mapText(driver.raw?.currentVehicleId);
      if (!vehicleId) return;
      const existing = next[vehicleId];
      if (!existing || mapTimestamp(driver.latestUpdate) > mapTimestamp(existing.latestUpdate)) {
        next[vehicleId] = driver;
      }
    });
    return next;
  }, [drivers]);

  useEffect(() => {
    if (!orgId || overviewMode !== "vehicles") {
      setVehicleAssetsReady(false);
      setVehicleStatesReady(false);
      setVehicleBindingsReady(false);
      return undefined;
    }

    let active = true;
    setVehicleLoading(true);
    setVehicleAssetItems([]);
    setVehicleStatesMap({});
    setVehicleBindingsMap({});
    setVehicleAssetsReady(false);
    setVehicleStatesReady(false);
    setVehicleBindingsReady(false);
    setVehicleError("");
    const assetsUnsubscribe = onSnapshot(
      collection(db, "orgs", orgId, "vehicles"),
      (snapshot) => {
        if (!active) return;
        setVehicleAssetItems(normalizeVehicleAssets(snapshot.docs));
        setVehicleAssetsReady(true);
        setVehicleLoading(false);
      },
      (error) => {
        if (!active) return;
        console.error("[DriverManagerHome] vehicle master snapshot error:", error);
        setVehicleAssetItems([]);
        setVehicleAssetsReady(true);
        setVehicleLoading(false);
        setVehicleError("車輛資產主檔暫時無法讀取");
      },
    );
    const statesUnsubscribe = onSnapshot(
      collection(db, "orgs", orgId, "vehicleStates"),
      (snapshot) => {
        if (!active) return;
        const next = {};
        snapshot.docs.forEach((item) => {
          next[item.id] = { assetId: item.id, ...(item.data() || {}) };
        });
        setVehicleStatesMap(next);
        setVehicleStatesReady(true);
      },
      (error) => {
        if (!active) return;
        console.error("[DriverManagerHome] vehicle states snapshot error:", error);
        setVehicleStatesMap({});
        setVehicleStatesReady(true);
        setVehicleError((current) => current || "即時車輛位置暫時無法讀取");
      },
    );
    const bindingsUnsubscribe = onSnapshot(
      collection(db, "orgs", orgId, "vehicleBindings"),
      (snapshot) => {
        if (!active) return;
        const next = {};
        snapshot.docs.forEach((item) => {
          const data = item.data() || {};
          next[item.id] = {
            vehicleId: item.id,
            trailerIds: Array.isArray(data.trailerIds) ? data.trailerIds : [],
            containerIds: Array.isArray(data.containerIds) ? data.containerIds : [],
          };
        });
        setVehicleBindingsMap(next);
        setVehicleBindingsReady(true);
      },
      (error) => {
        if (!active) return;
        console.error("[DriverManagerHome] vehicle bindings snapshot error:", error);
        setVehicleBindingsMap({});
        setVehicleBindingsReady(true);
        setVehicleError((current) => current || "車輛掛載配置暫時無法讀取");
      },
    );

    return () => {
      active = false;
      assetsUnsubscribe();
      statesUnsubscribe();
      bindingsUnsubscribe();
    };
  }, [orgId, overviewMode]);

  const vehicles = useMemo(() => {
    const assetsById = Object.fromEntries(vehicleAssetItems.map((asset) => [asset.assetId, asset]));
    return vehicleAssetItems.map((asset) => {
      const bindingRecord = vehicleBindingsMap[asset.assetId];
      const trailerIds = bindingRecord
        ? bindingRecord.trailerIds
        : Array.isArray(asset.trailerIds) ? asset.trailerIds : [];
      const boundTrailerIds = [...new Set(trailerIds.map(mapText).filter(Boolean))];
      return normalizeVehicleState(
        {
          ...asset,
          trailerBindingCount: bindingRecord
            ? boundTrailerIds.length
            : boundTrailerIds.length || Number(asset.trailerBindingCount) || 0,
          hasBoundTrailer: bindingRecord
            ? boundTrailerIds.length > 0
            : boundTrailerIds.length > 0 || Boolean(asset.hasBoundTrailer),
        },
        vehicleStatesMap[asset.assetId],
        driverById,
        driverByVehicleId,
        vehicleStatesMap,
        assetsById,
      );
    });
  }, [driverById, driverByVehicleId, vehicleAssetItems, vehicleBindingsMap, vehicleStatesMap]);

  const filteredDrivers = useMemo(() => {
    const keyword = mapText(searchText).toLowerCase();
    return drivers.filter((driver) => {
      if (statusFilter !== "all" && driver.statusKey !== statusFilter)
        return false;
      const haystack = [
        driver.displayName,
        driver.phone,
        driver.stopId,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return !keyword || haystack.includes(keyword);
    });
  }, [drivers, searchText, statusFilter]);

  const liveCoordinateDrivers = useMemo(
    () => filteredDrivers.filter((driver) => driver.coordinate),
    [filteredDrivers],
  );
  const quickLiveDrivers = useMemo(() => {
    const selected = liveCoordinateDrivers.find((driver) => driver.uid === selectedDriverId);
    const ordered = selected
      ? [selected, ...liveCoordinateDrivers.filter((driver) => driver.uid !== selectedDriverId)]
      : liveCoordinateDrivers;
    return ordered.slice(0, 5);
  }, [liveCoordinateDrivers, selectedDriverId]);

  const handleSelectLiveDriver = (driverId) => {
    setSelectedDriverId(driverId);
    setSelectedAssetId("");
    setViewMode("live");
    setIsDriverPickerOpen(false);
  };

  useEffect(() => {
    if (!mapExpanded || overviewMode !== "drivers" || viewMode !== "live") {
      setIsDriverPickerOpen(false);
    }
  }, [mapExpanded, overviewMode, viewMode]);

  useEffect(() => {
    if (!isDriverPickerOpen) return undefined;
    const handlePickerKeyDown = (event) => {
      if (event.key === "Escape") setIsDriverPickerOpen(false);
    };
    window.addEventListener("keydown", handlePickerKeyDown);
    return () => window.removeEventListener("keydown", handlePickerKeyDown);
  }, [isDriverPickerOpen]);

  const filteredVehicles = useMemo(() => {
    const keyword = mapText(searchText).toLowerCase();
    return vehicles.filter((vehicle) => {
      if (vehicleFilter !== "all" && vehicle.assetKind !== vehicleFilter)
        return false;
      const haystack = [
        vehicle.assetId,
        vehicle.displayName,
        vehicle.assetTypeOther,
        vehicle.plateNumber,
        vehicleTypeLabel(vehicle.assetType, vehicle.assetKind),
        vehicle.make,
        vehicle.model,
        vehicle.currentDriverName,
        vehicle.parkedLocationName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return !keyword || haystack.includes(keyword);
    });
  }, [searchText, vehicleFilter, vehicles]);

  const vehicleMarkerGroups = useMemo(
    () => groupVehiclesByLocation(filteredVehicles),
    [filteredVehicles],
  );

  const selectedDriver = useMemo(
    () => drivers.find((driver) => driver.uid === selectedDriverId) || null,
    [drivers, selectedDriverId],
  );
  const selectedVehicle = useMemo(
    () => vehicles.find((vehicle) => vehicle.assetId === selectedAssetId) || null,
    [selectedAssetId, vehicles],
  );
  const selectedApplicationForDriver =
    selectedApplication &&
    mapText(selectedApplication.applicationId) ===
      mapText(selectedDriver?.applicationId)
      ? selectedApplication
      : null;

  useEffect(() => {
    if (
      overviewMode !== "drivers" ||
      viewMode !== "live" ||
      !selectedDriverId ||
      selectedDriver?.statusKey !== "break" ||
      !orgId
    ) {
      setLiveRestStartAt(0);
      return undefined;
    }

    setLiveRestStartAt(0);
    const dateKeys = Array.from({ length: 7 }, (_, index) =>
      historyDateKeyOffset(new Date(), -index),
    );
    const eventBuckets = new Map();
    const subscriptions = dateKeys.map((dateKey, index) =>
      onSnapshot(
        query(
          collection(
            db,
            "orgs",
            orgId,
            "dailyLogs",
            dateKey,
            "drivers",
            selectedDriverId,
            "statusEvents",
          ),
          orderBy("observedAt", "desc"),
          limit(10),
        ),
        (snapshot) => {
          eventBuckets.set(
            index,
            snapshot.docs.map((item) => ({ id: item.id, ...(item.data() || {}) })),
          );
          const latestRestStart = [...eventBuckets.values()]
            .flat()
            .filter((event) => event.eventType === "take_break")
            .map((event) => mapTimestamp(event.observedAt))
            .filter(Boolean)
            .sort((left, right) => right - left)[0] || 0;
          setLiveRestStartAt(latestRestStart);
        },
        (error) => {
          console.error("[DriverManagerHome] live rest status error:", error);
          eventBuckets.set(index, []);
          setLiveRestStartAt((current) => current || 0);
        },
      ),
    );

    return () => subscriptions.forEach((unsubscribe) => unsubscribe());
  }, [orgId, overviewMode, selectedDriver?.statusKey, selectedDriverId, viewMode]);

  const liveDriverStates = useMemo(() => {
    if (!selectedDriver) return [];

    const raw = selectedDriver.raw || {};
    const tracking = raw.currentTracking || {};
    const activeSession = mapText(raw.activeTrackingSessionId || tracking.sessionId);
    const states = [];

    if (selectedDriver.statusKey === "break") {
      states.push({
        type: "rest",
        title: "目前休息中",
        startedAt: liveRestStartAt,
      });
    }

    const locationState = mapText(tracking.locationState).toLowerCase();
    const stoppedAt = mapTimestamp(tracking.stoppedAt);
    const position = liveStopPosition(selectedDriver);
    const cachedStop = liveStopContinuityRef.current.get(selectedDriver.uid);
    const movedFromCachedStop = liveStopHasMoved(cachedStop, position);
    const continuousStop = cachedStop && !movedFromCachedStop ? cachedStop : null;
    const hasCurrentStopSignal =
      locationState === "stopped" &&
      stoppedAt &&
      (activeSession || selectedDriver.statusKey !== "offline");
    const stopStartedAt = continuousStop?.startedAt || (hasCurrentStopSignal ? stoppedAt : 0);

    if ((continuousStop || hasCurrentStopSignal) && stopStartedAt) {
      states.push({
        type: "stop",
        title: "目前自動停留",
        startedAt: stopStartedAt,
      });
    }

    return states;
  }, [liveRestStartAt, selectedDriver, clockTick]);

  useEffect(() => {
    if (!selectedDriver?.uid) return;

    const tracking = selectedDriver.raw?.currentTracking || {};
    const locationState = mapText(tracking.locationState).toLowerCase();
    const stoppedAt = mapTimestamp(tracking.stoppedAt);
    const position = liveStopPosition(selectedDriver);
    const cache = liveStopContinuityRef.current;
    const previousStop = cache.get(selectedDriver.uid);
    const movedFromPreviousStop = liveStopHasMoved(previousStop, position);
    const continuousStop = previousStop && !movedFromPreviousStop ? previousStop : null;
    const activeSession = mapText(
      selectedDriver.raw?.activeTrackingSessionId || tracking.sessionId,
    );
    const hasCurrentStopSignal =
      locationState === "stopped" &&
      stoppedAt &&
      (activeSession || selectedDriver.statusKey !== "offline");

    if (movedFromPreviousStop) cache.delete(selectedDriver.uid);
    if (hasCurrentStopSignal) {
      cache.set(selectedDriver.uid, {
        startedAt: continuousStop?.startedAt
          ? Math.min(continuousStop.startedAt, stoppedAt)
          : stoppedAt,
        position: continuousStop?.position || position,
      });
    } else if (continuousStop) {
      cache.set(selectedDriver.uid, continuousStop);
    }
  }, [selectedDriver]);

  useEffect(() => {
    if (selectedDriverId && !selectedDriver) setSelectedDriverId("");
  }, [selectedDriver, selectedDriverId]);

  useEffect(() => {
    const applicationId = mapText(selectedDriver?.applicationId);
    const workDateKey = mapText(selectedDriver?.workDateKey);
    const trackingObservedAt = mapTimestamp(selectedDriver?.observedAt);
    const trackingDateKey = trackingObservedAt ? mapDateKey(trackingObservedAt) : "";
    const workDateKeys = [...new Set([workDateKey, trackingDateKey].filter(Boolean))];
    const driverOrgId = mapText(selectedDriver?.raw?.orgId);
    if (!selectedDriverId || !applicationId || !workDateKeys.length || !driverOrgId) {
      setSelectedApplication(null);
      setSelectedApplicationLoading(false);
      setSelectedApplicationError(
        selectedDriverId && applicationId && !workDateKeys.length
          ? "成員目前有工作 ID，但缺少可用的工作日期，無法定位工作資料"
          : selectedDriverId && applicationId && !driverOrgId
            ? "此成員並不屬於本組織的同仁"
            : "",
      );
      return undefined;
    }

    setSelectedApplication(null);
    setSelectedApplicationLoading(true);
    setSelectedApplicationError("");
    let alive = true;
    let activeUnsubscribe = () => {};
    const subscribeAtDate = (dateIndex) => {
      const lookupDateKey = workDateKeys[dateIndex];
      activeUnsubscribe = onSnapshot(
        doc(
          db,
          "orgs",
          driverOrgId,
          "applications",
          lookupDateKey,
          "jobs",
          applicationId,
        ),
        (snapshot) => {
          if (!alive) return;
          if (!snapshot.exists() && dateIndex + 1 < workDateKeys.length) {
            activeUnsubscribe();
            subscribeAtDate(dateIndex + 1);
            return;
          }
          if (!snapshot.exists()) {
            setSelectedApplication(null);
            setSelectedApplicationError(
              `找不到工作資料（查詢日期：${workDateKeys.join("、")}；applicationId：${applicationId}）`,
            );
          } else {
            setSelectedApplication({
              ...(snapshot.data() || {}),
              applicationId: snapshot.id,
              workDateKey: lookupDateKey,
            });
            setSelectedApplicationError("");
          }
          setSelectedApplicationLoading(false);
        },
        (error) => {
          if (!alive) return;
          console.error("[DriverManagerHome] selected application error:", error);
          setSelectedApplication(null);
          setSelectedApplicationLoading(false);
          setSelectedApplicationError("目前工作資料暫時無法讀取");
        },
      );
    };

    subscribeAtDate(0);
    return () => {
      alive = false;
      activeUnsubscribe();
    };
  }, [
    selectedDriver?.applicationId,
    selectedDriver?.observedAt,
    selectedDriver?.raw?.orgId,
    selectedDriver?.workDateKey,
    selectedDriverId,
  ]);

  useEffect(() => {
    if (selectedAssetId && !selectedVehicle) setSelectedAssetId("");
  }, [selectedAssetId, selectedVehicle]);

  useEffect(() => {
    if (
      overviewMode !== "drivers" ||
      viewMode !== "track" ||
      !selectedDriverId ||
      !orgId
    ) {
      setTrackPoints([]);
      setTrackEvents([]);
      setRestRecords([]);
      setTrackSummaryData(null);
      setTrackLoading(false);
      setTrackError("");
      setPlaybackProgress(1);
      setIsPlaybackPlaying(false);
      setSelectedHistoryMarker(null);
      setDetailTrackPoints([]);
      setDetailStartTime("");
      setDetailEndTime("");
      setDetailError("");
      return undefined;
    }

    const dateKey = mapDateKey(trackDate);
    const summaryRef = doc(db, "orgs", orgId, "dailyLogs", dateKey, "drivers", selectedDriverId, "trackSummary", "summary");
    const previewCollection = collection(db, "orgs", orgId, "dailyLogs", dateKey, "drivers", selectedDriverId, "trackSummary", "summary", "previewChunks");
    const eventCollection = collection(db, "orgs", orgId, "dailyLogs", dateKey, "drivers", selectedDriverId, "trackSummary", "summary", "events");
    let alive = true;
    let requestStarted = false;
    let fallbackPoll = null;
    const enableApiFallback = (message) => {
      if (!alive) return;
      setTrackError(message);
      void requestSummary(true);
      if (!fallbackPoll) fallbackPoll = window.setInterval(() => void requestSummary(true), 60000);
    };
    setTrackPoints([]);
    setTrackEvents([]);
    setRestRecords([]);
    setTrackSummaryData(null);
    setTrackLoading(true);
    setTrackError("");
    setPlaybackProgress(1);
    setIsPlaybackPlaying(false);
    setSelectedHistoryMarker(null);
    setDetailTrackPoints([]);
    setDetailStartTime("");
    setDetailEndTime("");
    setDetailError("");

    const applyBundle = (payload) => {
      const data = payload?.summary || null;
      if (data) setTrackSummaryData(data);
      const chunks = Array.isArray(payload?.previewChunks) ? payload.previewChunks : [];
      const points = chunks.flatMap((chunk) => Array.isArray(chunk?.previewPoints) ? chunk.previewPoints : [])
        .map(mapTrackPoint).filter(Boolean).sort((left, right) => left.observedAt - right.observedAt);
      const events = Array.isArray(payload?.activityEvents) ? payload.activityEvents : [];
      setTrackPoints(points);
      setTrackEvents(events);
      setRestRecords(buildRestRecords(events));
      setTrackError("");
      setTrackLoading(false);
    };

    const requestSummary = async (force = false) => {
      if (requestStarted && !force) return;
      requestStarted = true;
      try {
        if (!user?.getIdToken) throw new Error("登入狀態已失效，請重新登入");
        const token = await user.getIdToken();
        const response = await fetch(`${TRACKING_HISTORY_API_URL}/summary`, {
          method: "POST",
          headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
          body: JSON.stringify({orgId, driverUid: selectedDriverId, dateKey}),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload?.ok !== true) throw new Error(payload?.error || "每日摘要暫時無法建立");
        if (!payload.exists && alive) {
          setTrackSummaryData(null);
          setTrackPoints([]);
          setTrackEvents([]);
          setRestRecords([]);
          setTrackLoading(false);
        } else if (payload.summary && alive) {
          applyBundle(payload);
        }
      } catch (error) {
        console.error("[DriverManagerHome] track summary request error:", error);
        if (alive) {
          setTrackLoading(false);
          setTrackError(error?.message || "每日摘要暫時無法讀取");
          if (!fallbackPoll) fallbackPoll = window.setInterval(() => void requestSummary(true), 60000);
        }
      }
    };

    const summaryUnsubscribe = onSnapshot(summaryRef, (snapshot) => {
      if (!snapshot.exists()) {
        if (!requestStarted) void requestSummary();
        return;
      }
      const data = snapshot.data() || {};
      setTrackSummaryData(data);
      setTrackError("");
      setTrackLoading(data.status === "building");
      const needsInspectionProjectionSync = data.status === "ready" &&
        Number(data.schemaVersion) >= 5 && Number(data.inspectionEventProjectionVersion || 0) < 1;
      if ((Number(data.schemaVersion) < 5 || needsInspectionProjectionSync) && !requestStarted) {
        void requestSummary();
      }
    }, (error) => {
      console.error("[DriverManagerHome] daily summary snapshot error:", error);
      enableApiFallback("摘要即時訂閱不可用，改用安全 API 讀取摘要");
    });

    const previewUnsubscribe = onSnapshot(previewCollection, (snapshot) => {
      const points = snapshot.docs.flatMap((item) => {
        const data = item.data() || {};
        return (Array.isArray(data.previewPoints) ? data.previewPoints : []).map((point, index) => ({
          ...point,
          id: `${item.id}_${index}`,
          eventId: `${item.id}_${index}`,
        }));
      }).map(mapTrackPoint).filter(Boolean).sort((left, right) => left.observedAt - right.observedAt);
      setTrackPoints(points);
    }, (error) => {
      console.error("[DriverManagerHome] daily preview chunks snapshot error:", error);
      enableApiFallback("摘要點即時訂閱不可用，改用安全 API 讀取摘要");
    });

    const eventsUnsubscribe = onSnapshot(eventCollection, (snapshot) => {
      const events = snapshot.docs.map((item) => ({...(item.data() || {}), id: item.data()?.id || item.id}))
        .sort((left, right) => mapTimestamp(left?.observedAt) - mapTimestamp(right?.observedAt));
      setTrackEvents(events);
      setRestRecords(buildRestRecords(events));
    }, (error) => {
      console.error("[DriverManagerHome] daily events snapshot error:", error);
      enableApiFallback("工作事件即時訂閱不可用，改用安全 API 讀取摘要");
    });

    return () => {
      alive = false;
      if (fallbackPoll) window.clearInterval(fallbackPoll);
      summaryUnsubscribe();
      previewUnsubscribe();
      eventsUnsubscribe();
    };
  }, [orgId, overviewMode, selectedDriverId, trackDate, user, viewMode]);

  const historyDateRange = useMemo(() => historyDayRange(trackDate), [trackDate]);

  const trackStops = useMemo(() => {
    const sourceStops = trackEvents
      .filter((event) => event.eventType === "stop_interval")
      .map((event) => {
        const stop = mapTrackStop(event);
        const fallback = nearestTrackPoint(trackPoints, stop.stopStartTime);
        return {
          ...stop,
          lat: stop.lat ?? fallback?.lat ?? null,
          lng: stop.lng ?? fallback?.lng ?? null,
          durationSeconds: stop.endedAt
            ? stop.durationSeconds
            : Math.max(0, Math.round((Date.now() - stop.stopStartTime) / 1000)),
        };
      })
      .filter((event) => event.stopStartTime > 0)
      .sort((left, right) => left.stopStartTime - right.stopStartTime);
    const mergedStops = mergeTrackStops(
      sourceStops.filter((event) =>
        historyRecordOverlapsDate(
          event.stopStartTime,
          event.endedAt,
          historyDateRange,
        ),
      ),
    );
    return mergedStops.map((stop) => {
      if (!shouldKeepLatestStopOpen(stop, mergedStops, trackPoints, trackEvents)) {
        return stop;
      }
      return {
        ...stop,
        endedAt: 0,
        durationSeconds: Math.max(
          0,
          Math.round((Date.now() - stop.stopStartTime) / 1000),
        ),
      };
    });
  }, [historyDateRange, trackEvents, trackPoints, clockTick]);

  const historyRestRecords = useMemo(
    () =>
      restRecords
        .filter((record) =>
          historyRecordOverlapsDate(
            record.restStartTime,
            record.restEndTime,
            historyDateRange,
          ),
        )
        .map((record) => {
          const fallback = nearestTrackPoint(trackPoints, record.restStartTime);
          return {
            ...record,
            lat: record.lat ?? fallback?.lat ?? null,
            lng: record.lng ?? fallback?.lng ?? null,
            durationSeconds: recordDurationSeconds(record),
          };
        }),
    [historyDateRange, restRecords, trackPoints, clockTick],
  );

  const visibleTrackStops = useMemo(
    () => excludeRestOverlapsFromStops(trackStops, historyRestRecords),
    [historyRestRecords, trackStops, clockTick],
  );

  const currentHistoryState = useMemo(() => {
    const activeStop = [...visibleTrackStops]
      .filter((stop) => !stop.endedAt)
      .sort((left, right) => right.stopStartTime - left.stopStartTime)[0];
    const activeRest = [...historyRestRecords]
      .filter((record) => !record.restEndTime)
      .sort((left, right) => right.restStartTime - left.restStartTime)[0];
    if (!activeStop && !activeRest) return null;

    const liveStop = activeStop
      ? {
          ...activeStop,
          endedAt: 0,
          durationSeconds: Math.max(
            0,
            Math.round((Date.now() - activeStop.stopStartTime) / 1000),
          ),
        }
      : null;
    const liveRest = activeRest
      ? {
          ...activeRest,
          restEndTime: 0,
          durationSeconds: recordDurationSeconds(activeRest),
        }
      : null;

    if (!liveRest) return { type: "stop", record: liveStop };
    if (!liveStop) return { type: "rest", record: liveRest };
    return liveRest.restStartTime > liveStop.stopStartTime
      ? { type: "rest", record: liveRest }
      : { type: "stop", record: liveStop };
  }, [clockTick, historyRestRecords, visibleTrackStops]);

  const playbackPoints = detailTrackPoints.length ? detailTrackPoints : trackPoints;
  const playbackStep = useMemo(
    () => playbackPoints.length < 2 ? 1 : 1 / (playbackPoints.length - 1),
    [playbackPoints.length],
  );
  const rawPlaybackPoint = useMemo(
    () => getPlaybackPoint(playbackPoints, playbackProgress),
    [playbackPoints, playbackProgress],
  );
  const playbackPoint = useMemo(() => {
    if (!rawPlaybackPoint) return null;
    if (isPlaybackPlaying) return rawPlaybackPoint;
    const playbackAt = Number(rawPlaybackPoint.observedAt);
    if (!Number.isFinite(playbackAt)) return rawPlaybackPoint;
    const matchingStop = visibleTrackStops.find((stop) => {
      const startAt = mapTimestamp(stop?.stopStartTime);
      const durationMs = Math.max(0, Number(stop?.durationSeconds) || 0) * 1000;
      const endAt = playbackStopEndAt(stop, visibleTrackStops, playbackPoints, trackEvents);
      return startAt > 0 && playbackAt >= startAt && playbackAt <= endAt;
    });
    const matchingRest = matchingStop ? null : historyRestRecords.find((record) => {
      const startAt = mapTimestamp(record?.restStartTime);
      const durationMs = Math.max(0, Number(record?.durationSeconds) || 0) * 1000;
      const endAt = mapTimestamp(record?.restEndTime) || (durationMs ? startAt + durationMs : Date.now());
      return startAt > 0 && playbackAt >= startAt && playbackAt <= endAt;
    });
    const stationaryRecord = matchingStop || matchingRest;
    if (!stationaryRecord) return rawPlaybackPoint;
    const anchorAt = matchingStop ? matchingStop.stopStartTime : matchingRest.restStartTime;
    const nearbyPoint = nearestTrackPoint(playbackPoints, mapTimestamp(anchorAt));
    const lat = mapNumber(stationaryRecord.lat) ?? mapNumber(nearbyPoint?.lat);
    const lng = mapNumber(stationaryRecord.lng) ?? mapNumber(nearbyPoint?.lng);
    if (lat === null || lng === null) return rawPlaybackPoint;
    return { ...rawPlaybackPoint, lat, lng, speed: 0 };
  }, [historyRestRecords, isPlaybackPlaying, playbackPoints, rawPlaybackPoint, visibleTrackStops]);
  const playbackMarkerAt = playbackPoint?.observedAt
    ? isPlaybackPlaying
      ? Math.floor(playbackPoint.observedAt / 1000) * 1000
      : playbackPoint.observedAt
    : 0;
  const playbackInStayOrRest = useMemo(
    () => isPlaybackInStayOrRest(playbackPoint?.observedAt, visibleTrackStops, historyRestRecords),
    [historyRestRecords, playbackPoint, visibleTrackStops],
  );
  const playbackTrackPoints = useMemo(
    () => getPlaybackTrackPoints(playbackPoints, playbackPoint),
    [playbackPoint, playbackPoints],
  );

  useEffect(() => {
    if (!isPlaybackPlaying || playbackPoints.length < 2) return undefined;
    const pointCount = playbackPoints.length;
    const safeSpeed = Math.max(0.1, Number(playbackSpeed) || 1);
    let lastTickAt = performance.now();
    const timer = window.setInterval(() => {
      const now = performance.now();
      const elapsedMs = Math.min(100, Math.max(0, now - lastTickAt));
      lastTickAt = now;
      const progressStep = (elapsedMs / 1000) * 10 * safeSpeed / (pointCount - 1);
      setPlaybackProgress((current) => {
        const next = Math.min(1, current + progressStep);
        if (next >= 1) setIsPlaybackPlaying(false);
        return next;
      });
    }, 1000 / 30);
    return () => window.clearInterval(timer);
  }, [isPlaybackPlaying, playbackPoints.length, playbackSpeed]);

  const mapPoints = useMemo(
    () =>
      filteredDrivers
        .filter((driver) => driver.coordinate)
        .map((driver) => driver.coordinate),
    [filteredDrivers],
  );
  const vehicleMapPoints = useMemo(
    () =>
      filteredVehicles
        .filter((vehicle) => vehicle.coordinate)
        .map((vehicle) => vehicle.coordinate),
    [filteredVehicles],
  );
  const routeBaseAt = trackPoints[0]?.observedAt || 0;
  const playbackGradientSegments = useMemo(
    () => buildTrackGradientSegments(
      playbackTrackPoints,
      trackEvents,
      visibleTrackStops,
      historyRestRecords,
      routeBaseAt,
    ),
    [historyRestRecords, playbackTrackPoints, routeBaseAt, trackEvents, visibleTrackStops],
  );

  const trackSummary = useMemo(() => {
    const first = trackPoints[0];
    const last = trackPoints[trackPoints.length - 1];
    const stopSeconds = visibleTrackStops.reduce(
      (total, stop) => total + Math.max(0, Number(stop.durationSeconds) || 0),
      0,
    );
    const restSeconds = restRecords.reduce(
      (total, record) => total + recordDurationSeconds(record),
      0,
    );
    return {
      distance: Number(trackSummaryData?.distanceMeters) >= 0
        ? Number(trackSummaryData.distanceMeters) / 1000
        : trackDistanceKm(trackPoints),
      firstAt: mapTimestamp(trackSummaryData?.firstAt) || first?.observedAt || 0,
      lastAt: mapTimestamp(trackSummaryData?.lastAt) || last?.observedAt || 0,
      averageSpeed: mapNumber(trackSummaryData?.averageSpeed),
      maxSpeed: mapNumber(trackSummaryData?.maxSpeed),
      stopCount: visibleTrackStops.length,
      stopSeconds,
      restCount: restRecords.length,
      restSeconds,
    };
  }, [restRecords, trackPoints, visibleTrackStops, trackSummaryData]);

  const historyStatusEvents = useMemo(
    () =>
      dedupeHistoryEvents(trackEvents
        .filter((event) => !["take_break", "resume_work"].includes(mapText(event?.eventType)))
        .filter((event) =>
          ["work", "station", "attendance", "inspection"].includes(mapText(event?.category)) ||
          ["go_online", "go_offline", "start_job", "job_paused", "end_work", "work_completed", "work_cancelled",
            "station_started", "station_approaching", "arrived", "station_completed", "exception", "stop_changed"].includes(
            mapText(event?.eventType),
          ),
        )
        .filter((event) => historyRecordOverlapsDate(event?.observedAt, event?.observedAt, historyDateRange))
        .sort((left, right) => mapTimestamp(left?.observedAt) - mapTimestamp(right?.observedAt))),
    [historyDateRange, trackEvents],
  );

  const historyJobKey = useMemo(() => JSON.stringify(
    [...new Map(historyStatusEvents
      .filter((event) => mapText(event?.applicationId))
      .map((event) => [
        `${event.workDateKey || mapDateKey(trackDate)}:${event.applicationId}`,
        {workDateKey: event.workDateKey || mapDateKey(trackDate), applicationId: event.applicationId},
      ])).values()],
  ), [historyStatusEvents, trackDate]);

  useEffect(() => {
    if (viewMode !== "track" || !orgId || !historyJobKey) {
      setHistoryJobsById({});
      return undefined;
    }
    let alive = true;
    const refs = JSON.parse(historyJobKey);
    Promise.all(refs.map(async ({workDateKey, applicationId}) => {
      try {
        const snapshot = await getDoc(doc(db, "orgs", orgId, "applications", workDateKey, "jobs", applicationId));
        if (!snapshot.exists()) return null;
        return [applicationId, {
          ...(snapshot.data() || {}),
          applicationId,
          workDateKey,
          orgId,
        }];
      } catch (error) {
        console.error("[DriverManagerHome] history job lookup error:", error);
        return null;
      }
    })).then((items) => {
      if (alive) setHistoryJobsById(Object.fromEntries(items.filter(Boolean)));
    });
    return () => { alive = false; };
  }, [historyJobKey, orgId, viewMode]);

  const historyTimelineItems = useMemo(
    () => buildHistoryTimelineItems(
      historyStatusEvents,
      historyJobsById,
      mapDateKey(trackDate),
    ),
    [historyJobsById, historyStatusEvents, trackDate],
  );

  const historyMapWorkMarkers = useMemo(() => {
    if (!playbackMarkerAt) return [];
    const playbackAtEnd = playbackProgress >= 1;
    const visibleEvents = historyStatusEvents.filter((event) =>
      playbackAtEnd || mapTimestamp(event?.observedAt) <= playbackMarkerAt,
    );
    return buildHistoryMapWorkMarkers(visibleEvents, historyJobsById);
  }, [historyJobsById, historyStatusEvents, playbackMarkerAt, playbackProgress]);

  const playbackAuxRecords = useMemo(
    () => historyAuxRecordsAtPlayback(
      visibleTrackStops,
      historyRestRecords,
      playbackMarkerAt,
    ),
    [historyRestRecords, playbackMarkerAt, visibleTrackStops],
  );

  const historyMapAuxMarkers = useMemo(
    () => buildHistoryAuxMarkers(playbackAuxRecords.stops, playbackAuxRecords.rests),
    [playbackAuxRecords],
  );
  const selectedHistoryWorkStations = useMemo(
    () => buildSelectedHistoryWorkStations(selectedHistoryMarker, historyJobsById),
    [historyJobsById, selectedHistoryMarker],
  );
  const renderHistoryWorkPhotoStrip = (files, category, sectionKey) => {
    const items = Array.isArray(files) ? files : [];
    return items.length ? (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
        {items.map((file, index) => {
          const mediaKey = historyMediaFileKey(file);
          const media = historyWorkMedia[mediaKey] || {};
          const previewUrl = media.previewUrl || mapText(file?.previewUrl || file?.previewURL || file?.url || file?.downloadURL || file?.downloadUrl || file?.originalUrl || file?.originalURL);
          const originalUrl = media.originalUrl || mapText(file?.originalUrl || file?.originalURL) || previewUrl;
          const label = mapText(file?.originalName || file?.name) || `${category} ${index + 1}`;
          return (
            <button key={`${mediaKey || sectionKey}_${index}`} type="button" onClick={() => previewUrl && setHistoryWorkPhotoViewer({ src: originalUrl, alt: label, category })} aria-label={`查看${category}原圖：${label}`} disabled={!previewUrl} style={{ display: "grid", placeItems: "center", width: 72, height: 72, padding: 3, overflow: "hidden", border: "1px solid #E5EAF0", borderRadius: 9, background: category === "簽收" ? "#EEF1F3" : "#F2F4F7", cursor: previewUrl ? "zoom-in" : "default" }}>
              {previewUrl ? <img src={previewUrl} alt={label} loading="lazy" style={{ display: "block", width: "100%", height: "100%", objectFit: category === "簽收" ? "contain" : "cover" }} /> : <span style={{ color: "#98A2B3", fontSize: 9, textAlign: "center" }}>{historyWorkMediaLoading ? "載入中" : "無法載入"}</span>}
            </button>
          );
        })}
      </div>
    ) : null;
  };

  useEffect(() => {
    let active = true;
    const requestId = ++historyWorkMediaRequestRef.current;
    const fileMap = new Map();
    selectedHistoryWorkStations.forEach((section) => {
      section.cargoItems.forEach((item) => {
        [
          ...(Array.isArray(item?.photos) ? item.photos : []),
          ...(Array.isArray(item?.execution?.photos) ? item.execution.photos : []),
        ].forEach((file) => {
          const key = historyMediaFileKey(file);
          if (key) fileMap.set(key, file);
        });
      });
      section.recipients.forEach((contact) => {
        const signature = contact?.proof?.signature;
        const key = historyMediaFileKey(signature);
        if (key) fileMap.set(key, signature);
      });
    });
    setHistoryWorkMedia({});
    setHistoryWorkPhotoViewer(null);
    if (!fileMap.size) {
      setHistoryWorkMediaLoading(false);
      return () => { active = false; };
    }
    setHistoryWorkMediaLoading(true);
    const storage = getStorage();
    const getUrl = async (path) => {
      if (!path) return "";
      try { return await getDownloadURL(storageRef(storage, path)); } catch (_) { return ""; }
    };
    Promise.all([...fileMap.entries()].map(async ([key, file]) => {
      const assetId = mapText(file?.assetId);
      let asset = {};
      if (assetId) {
        try {
          const snapshot = await getDoc(doc(db, "mediaAssets", assetId));
          if (snapshot.exists()) asset = snapshot.data() || {};
        } catch (_) {}
      }
      const previewPath = mapText(file?.previewPath || asset.previewPath);
      const originalPath = mapText(file?.originalPath || asset.originalPath) || (assetId ? `assets/${assetId}/original` : "");
      const previewUrl = await getUrl(previewPath) || mapText(file?.previewUrl || file?.previewURL || file?.url || file?.downloadURL || file?.downloadUrl) || await getUrl(originalPath) || mapText(file?.originalUrl || file?.originalURL);
      const originalUrl = await getUrl(originalPath) || mapText(file?.originalUrl || file?.originalURL) || previewUrl;
      return [key, { previewUrl, originalUrl }];
    })).then((entries) => {
      if (active && historyWorkMediaRequestRef.current === requestId) setHistoryWorkMedia(Object.fromEntries(entries));
    }).catch(() => {
      if (active && historyWorkMediaRequestRef.current === requestId) setHistoryWorkMedia({});
    }).finally(() => {
      if (active && historyWorkMediaRequestRef.current === requestId) setHistoryWorkMediaLoading(false);
    });
    return () => {
      active = false;
      if (historyWorkMediaRequestRef.current === requestId) historyWorkMediaRequestRef.current += 1;
    };
  }, [selectedHistoryWorkStations]);

  useEffect(() => {
    if (!selectedHistoryMarker) return;
    const visibleMarkers = ["station", "inspection", "workCluster"].includes(selectedHistoryMarker.type)
      ? historyMapWorkMarkers
      : historyMapAuxMarkers;
    const markerIsVisible = visibleMarkers.some((marker) => marker.id === selectedHistoryMarker.record?.id);
    if (!markerIsVisible && selectedHistoryMarker.type === "inspection") {
      const eventId = mapText(selectedHistoryMarker.record?.eventId);
      const sourceEventStillExists = eventId && historyStatusEvents.some((event) =>
        mapText(event?.eventId || event?.actionId || event?.id) === eventId,
      );
      if (sourceEventStillExists) return;
    }
    if (!markerIsVisible) {
      inspectionDetailRequestRef.current += 1;
      setSelectedHistoryMarker(null);
      setInspectionDetail(null);
      setInspectionDetailLoading(false);
    }
  }, [historyMapAuxMarkers, historyMapWorkMarkers, historyStatusEvents, selectedHistoryMarker]);

  const closeInspectionDetail = () => {
    inspectionDetailRequestRef.current += 1;
    setSelectedHistoryMarker(null);
    setInspectionDetail(null);
    setInspectionDetailLoading(false);
    setInspectionDetailError("");
  };

  const handleSelectHistoryMarker = useCallback((selection) => {
    setSelectedHistoryMarker(selection || null);
    inspectionDetailRequestRef.current += 1;
    const requestId = inspectionDetailRequestRef.current;
    if (selection?.type !== "inspection") {
      setInspectionDetail(null);
      setInspectionDetailLoading(false);
      setInspectionDetailError("");
      return;
    }

    const marker = selection.record || {};
    const fallback = {
      ...marker,
      title: mapText(marker.eventTitle || marker.title) || "巡檢紀錄",
      note: mapText(marker.notePreview),
      media: [],
    };
    setInspectionDetail(fallback);
    setInspectionDetailError("");
    setInspectionDetailLoading(true);
    const dateKey = mapText(marker.workDateKey) || mapDateKey(trackDate);
    const eventDocId = mapText(marker.eventId || marker.actionId);
    const cacheKey = `${orgId}:${dateKey}:${marker.applicationId}:${eventDocId}`;
    const cached = inspectionDetailCacheRef.current.get(cacheKey);
    if (cached) {
      setInspectionDetail(cached);
      setInspectionDetailLoading(false);
      return;
    }

    (async () => {
      try {
        let canonical = fallback;
        if (orgId && dateKey && marker.applicationId && eventDocId) {
          const eventRef = doc(
            db,
            "orgs", orgId,
            "applications", dateKey,
            "jobs", marker.applicationId,
            "inspectionEvents", eventDocId,
          );
          const eventSnapshot = await getDoc(eventRef);
          if (eventSnapshot.exists()) {
            canonical = { ...fallback, ...(eventSnapshot.data() || {}), id: eventSnapshot.id };
          }
        }

        const mediaItems = Array.isArray(canonical.media) ? canonical.media : [];
        const storage = getStorage();
        const resolvedMedia = await Promise.all(mediaItems.map(async (item) => {
          const assetId = mapText(item?.assetId);
          if (!assetId) return { ...item, previewUrl: "", originalUrl: "" };
          let asset = {};
          try {
            const assetSnapshot = await getDoc(doc(db, "mediaAssets", assetId));
            if (assetSnapshot.exists()) asset = assetSnapshot.data() || {};
          } catch (_) {}
          const originalPath = mapText(asset.originalPath) || `assets/${assetId}/original`;
          const previewPath = mapText(asset.previewPath);
          const getUrl = async (path) => {
            if (!path) return "";
            try { return await getDownloadURL(storageRef(storage, path)); } catch (_) { return ""; }
          };
          const originalUrl = await getUrl(originalPath);
          const previewUrl = item?.mediaType === "video"
            ? originalUrl
            : await getUrl(previewPath) || originalUrl;
          return { ...item, previewUrl, originalUrl };
        }));
        const result = {
          ...fallback,
          ...canonical,
          id: eventDocId || canonical.id || fallback.id,
          title: mapText(canonical.title || canonical.eventTitle || fallback.title),
          note: mapText(canonical.note || canonical.notePreview || canonical.reason || fallback.note),
          applicationTitle: mapText(canonical.applicationTitle || marker.title),
          workDateKey: mapText(canonical.workDateKey || dateKey),
          media: resolvedMedia,
        };
        inspectionDetailCacheRef.current.set(cacheKey, result);
        if (requestId === inspectionDetailRequestRef.current) {
          setInspectionDetail(result);
          const location = result.locationSnapshot || {};
          const resolvedLat = mapNumber(location.lat ?? location.latitude ?? result.lat ?? marker.lat);
          const resolvedLng = mapNumber(location.lng ?? location.longitude ?? result.lng ?? marker.lng);
          if (resolvedLat !== null && resolvedLng !== null) {
            setSelectedHistoryMarker((current) => current?.record?.id === marker.id
              ? {
                ...current,
                record: {
                  ...current.record,
                  lat: resolvedLat,
                  lng: resolvedLng,
                  accuracy: mapNumber(location.accuracy ?? result.accuracy ?? marker.accuracy),
                  locationObservedAt: mapTimestamp(location.observedAt) || mapTimestamp(result.locationObservedAt),
                },
              }
              : current);
          }
        }
      } catch (error) {
        if (requestId === inspectionDetailRequestRef.current) {
          setInspectionDetailError("完整巡檢內容暫時無法載入，先顯示摘要資訊。");
        }
      } finally {
        if (requestId === inspectionDetailRequestRef.current) setInspectionDetailLoading(false);
      }
    })();
  }, [orgId, trackDate]);

  const handleSelectInspectionTimelineEvent = useCallback((event) => {
    const eventType = mapText(event?.eventType);
    if (!["inspection_record_added", "inspection_exception_reported"].includes(eventType)) return;
    const eventId = mapText(event?.eventId || event?.actionId || event?.id);
    const applicationId = mapText(event?.applicationId);
    const workDateKey = mapText(event?.workDateKey) || mapDateKey(trackDate);
    const matchingMarker = buildHistoryMapWorkMarkers(historyStatusEvents, historyJobsById)
      .find((marker) => marker.type === "inspection" && marker.eventId === eventId);
    const relatedEvents = historyStatusEvents
      .filter((candidate) =>
        mapText(candidate?.applicationId) === applicationId &&
        (mapText(candidate?.workDateKey) || workDateKey) === workDateKey &&
        ["inspection_record_added", "inspection_exception_reported"].includes(mapText(candidate?.eventType)),
      )
      .sort((left, right) => mapTimestamp(left?.observedAt) - mapTimestamp(right?.observedAt));
    const markerNumber = Math.max(1, relatedEvents.findIndex((candidate) =>
      mapText(candidate?.eventId || candidate?.actionId || candidate?.id) === eventId,
    ) + 1);
    const coordinate = stationEventCoordinate(event);
    const fallbackMarker = matchingMarker || {
      id: `inspection_${eventId || `${applicationId}_${mapTimestamp(event?.observedAt)}`}`,
      eventId,
      workDateKey,
      type: "inspection",
      applicationId,
      title: mapText(event?.applicationTitle) || "巡檢工作",
      eventTitle: mapText(event?.eventTitle || event?.title) || historyEventLabel(eventType, event),
      eventType,
      markerLabel: `記${markerNumber}`,
      notePreview: mapText(event?.notePreview || event?.reason),
      observedAt: mapTimestamp(event?.observedAt),
      locationObservedAt: mapTimestamp(event?.locationObservedAt),
      accuracy: mapNumber(event?.accuracy),
      lat: coordinate?.lat ?? null,
      lng: coordinate?.lng ?? null,
    };
    setIsPlaybackPlaying(false);
    setPlaybackProgress(1);
    handleSelectHistoryMarker({ type: "inspection", record: fallbackMarker });
  }, [handleSelectHistoryMarker, historyJobsById, historyStatusEvents, trackDate]);

  const selectedInspectionLocation = inspectionDetail?.locationSnapshot || {};
  const selectedInspectionLat = mapNumber(
    selectedInspectionLocation.lat ?? selectedInspectionLocation.latitude ?? inspectionDetail?.lat,
  );
  const selectedInspectionLng = mapNumber(
    selectedInspectionLocation.lng ?? selectedInspectionLocation.longitude ?? inspectionDetail?.lng,
  );
  const selectedInspectionAccuracy = mapNumber(
    selectedInspectionLocation.accuracy ?? inspectionDetail?.accuracy,
  );

  const loadDetailTrackRange = async () => {
    const dateKey = mapDateKey(trackDate);
    const startAt = historyDateTimeFromInput(dateKey, detailStartTime);
    const endAt = historyDateTimeFromInput(dateKey, detailEndTime, true);
    if (!startAt || !endAt || endAt <= startAt) {
      setDetailError("請選擇有效的開始與結束時間。");
      return;
    }
    setDetailLoading(true);
    setDetailError("");
    try {
      if (!user?.getIdToken) throw new Error("登入狀態已失效，請重新登入");
      const token = await user.getIdToken();
      const response = await fetch(`${TRACKING_HISTORY_API_URL}/range`, {
        method: "POST",
        headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
        body: JSON.stringify({orgId, driverUid: selectedDriverId, dateKey, startAt, endAt}),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.ok !== true) {
        throw new Error(payload?.error === "history_range_too_large"
          ? "選取區段太大，請縮短時間範圍。"
          : payload?.error || "精細軌跡暫時無法載入");
      }
      const points = (Array.isArray(payload.points) ? payload.points : [])
        .map(mapTrackPoint).filter(Boolean).sort((left, right) => left.observedAt - right.observedAt);
      setDetailTrackPoints(points);
      setPlaybackProgress(points.length > 1 ? 0 : 1);
      setIsPlaybackPlaying(false);
      if (!points.length) setDetailError("這個時間區段沒有原始定位點。");
    } catch (error) {
      console.error("[DriverManagerHome] detailed track range error:", error);
      setDetailError(error?.message || "精細軌跡暫時無法載入");
    } finally {
      setDetailLoading(false);
    }
  };

  const clearDetailTrackRange = () => {
    setDetailTrackPoints([]);
    setDetailError("");
    setIsPlaybackPlaying(false);
    setPlaybackProgress(trackPoints.length > 1 ? 0 : 1);
  };
  const toggleDetailRangeMenu = () => {
    if (!isDetailRangeMenuOpen) {
      const dateKey = mapDateKey(trackDate);
      const defaultsKey = `${selectedDriverId}:${dateKey}`;
      if (detailRangeDefaultsKeyRef.current !== defaultsKey) {
        const latestAt = mapTimestamp(trackSummary.lastAt) || trackPoints[trackPoints.length - 1]?.observedAt || 0;
        setDetailStartTime("");
        setDetailEndTime("");
        if (latestAt) {
          const dayStartAt = new Date(`${dateKey}T00:00:00`).getTime();
          const dayEndAt = new Date(`${dateKey}T23:59:59.999`).getTime();
          const endAt = Math.min(latestAt, dayEndAt);
          const startAt = Math.max(dayStartAt, endAt - 2 * 60 * 60 * 1000);
          const toTimeInput = (value) => {
            const date = new Date(value);
            return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
          };
          setDetailStartTime(toTimeInput(startAt));
          setDetailEndTime(toTimeInput(endAt));
          detailRangeDefaultsKeyRef.current = defaultsKey;
        }
      }
    }
    setIsDetailRangeMenuOpen((current) => !current);
  };

  const seekPlaybackBySeconds = (seconds) => {
    if (playbackPoints.length < 2) return;
    const startAt = playbackPoints[0].observedAt;
    const endAt = playbackPoints[playbackPoints.length - 1].observedAt;
    const currentAt = getPlaybackPoint(playbackPoints, playbackProgress)?.observedAt ?? startAt;
    const nextAt = Math.max(startAt, Math.min(endAt, currentAt + seconds * 1000));
    setIsPlaybackPlaying(false);
    setPlaybackProgress(getPlaybackProgressAtTime(playbackPoints, nextAt));
  };

  useEffect(() => {
    setIsPlaybackFollowEnabled(false);
  }, [overviewMode, selectedDriverId, trackDate, viewMode]);

  const shiftTrackDate = (days) => {
    const nextDate = new Date(trackDate);
    nextDate.setDate(nextDate.getDate() + days);
    setTrackDate(nextDate);
  };

  const driverSummary = useMemo(
    () => ({
      total: drivers.length,
      working: drivers.filter((driver) => driver.statusKey === "working").length,
      break: drivers.filter((driver) => driver.statusKey === "break").length,
      offline: drivers.filter((driver) => driver.statusKey === "offline").length,
    }),
    [drivers],
  );

  const vehicleSummary = useMemo(
    () => ({
      total: vehicles.length,
      vehicle: vehicles.filter((vehicle) => vehicle.assetKind === "vehicle").length,
      trailer: vehicles.filter((vehicle) => vehicle.assetKind === "trailer").length,
      container: vehicles.filter((vehicle) => vehicle.assetKind === "container").length,
      equipment: vehicles.filter((vehicle) => vehicle.assetKind === "equipment").length,
    }),
    [vehicles],
  );

  const driverFilters = [
    { key: "all", label: "全部", count: driverSummary.total },
    { key: "working", label: "工作中", count: driverSummary.working },
    { key: "break", label: "休息中", count: driverSummary.break },
    { key: "offline", label: "離線", count: driverSummary.offline },
  ];
  const vehicleFilters = [
    { key: "all", label: "全部", count: vehicleSummary.total },
    { key: "vehicle", label: "車輛", count: vehicleSummary.vehicle },
    { key: "trailer", label: "托架", count: vehicleSummary.trailer },
    { key: "container", label: "容器", count: vehicleSummary.container },
    { key: "equipment", label: "設備", count: vehicleSummary.equipment },
  ];

  const clearFilters = () => {
    setSearchText("");
    if (overviewMode === "drivers") setStatusFilter("all");
    else setVehicleFilter("all");
  };
  const switchOverviewMode = (mode) => {
    setOverviewMode(mode);
    setViewMode("live");
    setSearchText("");
    setSelectedDriverId("");
    setSelectedAssetId("");
  };

  if (loading) {
    return (
      <div className="dmh-fleet-shell">
        <style>{LIVE_FLEET_STYLES}</style>
        <div className="dmh-fleet-loading">
          <span className="dmh-spinner" />
          <b>正在同步車隊位置…</b>
        </div>
      </div>
    );
  }

  const activeFilters = overviewMode === "drivers" ? driverFilters : vehicleFilters;
  const activeFilter = overviewMode === "drivers" ? statusFilter : vehicleFilter;
  const activeItems = overviewMode === "drivers" ? filteredDrivers : filteredVehicles;
  const activePoints = overviewMode === "drivers" ? mapPoints : vehicleMapPoints;
  const selectedMapItem = overviewMode === "drivers" ? selectedDriver : selectedVehicle;
  const mapDataReady =
    viewMode === "track"
      ? !trackLoading
      : overviewMode === "drivers"
      ? !loading
      : !vehicleLoading && vehicleAssetsReady && vehicleStatesReady && vehicleBindingsReady;

  return (
    <div className={`dmh-fleet-shell${mapExpanded ? " is-map-expanded" : ""}`}>
      <style>{LIVE_FLEET_STYLES}</style>
      <div className="dmh-fleet-heading">
        <div className="dmh-heading-title">
          <h1>總覽</h1>
        </div>
        <div className="dmh-mode-switch" role="tablist" aria-label="總覽模式">
          <button
            type="button"
            className={overviewMode === "drivers" ? "is-active" : ""}
            onClick={() => switchOverviewMode("drivers")}
          >
            成員位置
          </button>
          <button
            type="button"
            className={overviewMode === "vehicles" ? "is-active" : ""}
            onClick={() => switchOverviewMode("vehicles")}
          >
            車輛位置
          </button>
        </div>
      </div>

      <div className="dmh-fleet-grid">
        <section className={`dmh-map-card${viewMode === "track" ? " is-track" : " is-live-map"}`}>
          <div className="dmh-map-toolbar">
            <div className="dmh-map-title-block">
              <div className="dmh-map-title-row">
                <strong>{overviewMode === "drivers" ? "成員位置" : "車輛位置"}</strong>
              </div>
            </div>
            <div className="dmh-map-actions">
              {viewMode === "live" && (
                <button
                  type="button"
                  onClick={handleFitAllMap}
                >
                  顯示全部
                </button>
              )}
              {overviewMode === "drivers" && (
                <>
                  <button
                    type="button"
                    className={viewMode === "live" ? "is-active" : ""}
                    onClick={() => setViewMode("live")}
                  >
                    即時位置
                  </button>
                  <button
                    type="button"
                    className={viewMode === "track" ? "is-active" : ""}
                    onClick={() => setViewMode("track")}
                    disabled={!selectedDriverId}
                  >
                    歷史軌跡
                  </button>
                </>
              )}
              <button
                type="button"
                className="dmh-map-expand-toggle"
                aria-label={mapExpanded ? "還原地圖大小" : "展開地圖"}
                title={mapExpanded ? "還原地圖大小" : "展開地圖"}
                aria-pressed={mapExpanded}
                onClick={() => onMapExpandedChange?.(!mapExpanded)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path
                    d={mapExpanded
                      ? "M9 3v6H3M15 3v6h6M3 15h6v6M21 15h-6v6"
                      : "M8 3H4a1 1 0 0 0-1 1v4M16 3h4a1 1 0 0 1 1 1v4M3 16v4a1 1 0 0 0 1 1h4M21 16v4a1 1 0 0 1-1 1h-4"}
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="dmh-map-stage" style={{ position: "relative" }}>
            <MapContainer
              key={overviewMode}
              center={DEFAULT_MAP_CENTER}
              zoom={7}
              zoomControl
              scrollWheelZoom={false}
              zoomAnimation={false}
              fadeAnimation={false}
              markerZoomAnimation={false}
              className="dmh-leaflet-map"
            >
              <MapSizeSync layoutKey={mapExpanded} />
              <MapCenterWheelZoom />
              <MapMarkerProjectionSync />
              <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {viewMode === "track" ? (
                <>
                  <HistoryMapFocus target={selectedHistoryMarker?.record} />
                  <PlaybackFollowController
                    point={playbackPoint}
                    enabled={isPlaybackFollowEnabled}
                    onDisable={setIsPlaybackFollowEnabled}
                  />
                  {playbackGradientSegments.map((segment, index) => (
                    <React.Fragment key={`playback-gradient-${index}`}>
                      <Polyline
                        positions={segment.positions}
                        pathOptions={{ color: segment.color, weight: 8, opacity: 0.82, lineCap: "round", lineJoin: "round" }}
                      />
                      <Polyline
                        positions={segment.positions}
                        pathOptions={{ color: "#fff", weight: 3, opacity: 0.98, dashArray: "1 20", lineCap: "round", className: "dmh-track-route-flow" }}
                      />
                    </React.Fragment>
                  ))}
                  {playbackPoints[0] && <Marker position={[playbackPoints[0].lat, playbackPoints[0].lng]} icon={createTrackEndpointIcon("start")} />}
                  <HistoryMarkerLayer
                    stationMarkers={historyMapWorkMarkers}
                    auxiliaryMarkers={historyMapAuxMarkers}
                    selectedMarker={selectedHistoryMarker}
                    onSelect={handleSelectHistoryMarker}
                  />
                  {playbackPoint && selectedDriver && <Marker position={[playbackPoint.lat, playbackPoint.lng]} icon={createDriverMarkerIcon({ ...selectedDriver, speed: playbackPoint.speed, showSpeed: playbackPoint.speed > 0 && !playbackInStayOrRest }, true, 0.7, { showWorkName: false })} eventHandlers={{ click: () => setIsPlaybackFollowEnabled(true) }} />}
                  {selectedDriver?.coordinate && !playbackPoint && <Marker position={selectedDriver.coordinate} icon={createDriverMarkerIcon(selectedDriver, true, 0.7)}><Popup><div className="dmh-popup"><strong>{selectedDriver.displayName}</strong><span>目前位置・{formatMapTime(selectedDriver.latestUpdate)}</span></div></Popup></Marker>}
                </>
              ) : overviewMode === "drivers" ? (
                <>
                  <MapViewport selectedDriver={selectedDriver} points={mapPoints} fitAllRequestKey={fitAllRequestKey} />
                  {mapDataReady && filteredDrivers.map((driver) => driver.coordinate ? <Marker key={driver.uid} position={driver.coordinate} icon={createDriverMarkerIcon(driver, driver.uid === selectedDriverId)} eventHandlers={{ click: () => { setSelectedDriverId(driver.uid); setViewMode("live"); } }}><Popup><div className="dmh-popup"><strong>{driver.displayName}</strong><span>{mapStatusTheme(driver.statusKey).label}・{formatMapTime(driver.latestUpdate)}</span></div></Popup></Marker> : null)}
                </>
              ) : (
                <>
                  <MapViewport selectedDriver={selectedVehicle} points={vehicleMapPoints} fitAllRequestKey={fitAllRequestKey} />
                  {mapDataReady && <VehicleMapMarkers groups={vehicleMarkerGroups} selectedAssetId={selectedAssetId} onSelect={(vehicle) => setSelectedAssetId(vehicle.assetId)} />}
                </>
              )}
            </MapContainer>
            {viewMode === "live" && (
              <button
                type="button"
                className="dmh-map-fit-all-button"
                aria-label="置中顯示所有定位項目"
                title="置中顯示所有定位項目"
                disabled={!mapDataReady || (overviewMode === "drivers" ? mapPoints.length === 0 : vehicleMapPoints.length === 0)}
                onClick={(event) => {
                  event.stopPropagation();
                  handleFitAllMap();
                }}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M21 16v3a2 2 0 0 1-2 2h-3M8 21H5a2 2 0 0 1-2-2v-3" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            )}
            {mapExpanded && overviewMode === "drivers" && viewMode === "live" && mapDataReady && drivers.length > 0 && (
              <>
                <div className="dmh-live-driver-rail" role="group" aria-label="快速選取成員">
                  <svg className="dmh-live-driver-rail-shape" viewBox="0 0 100 300" preserveAspectRatio="none" aria-hidden="true" focusable="false">
                    <defs>
                      <linearGradient id="dmh-driver-rail-shape-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor="#fff" stopOpacity="0" />
                        <stop offset="0.07" stopColor="#fff" stopOpacity="0.25" />
                        <stop offset="0.14" stopColor="#fff" stopOpacity="0.88" />
                        <stop offset="0.86" stopColor="#fff" stopOpacity="0.88" />
                        <stop offset="0.93" stopColor="#fff" stopOpacity="0.25" />
                        <stop offset="1" stopColor="#fff" stopOpacity="0" />
                      </linearGradient>
                      <linearGradient id="dmh-driver-rail-shape-stroke" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor="#fff" stopOpacity="0" />
                        <stop offset="0.12" stopColor="#fff" stopOpacity="0.9" />
                        <stop offset="0.88" stopColor="#fff" stopOpacity="0.9" />
                        <stop offset="1" stopColor="#fff" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d="M100 0 C94 5 89 11 83 17 Q78 22 71 22 H20 C9 22 2 30 2 42 V258 C2 270 9 278 20 278 H71 Q78 278 83 283 C89 289 94 295 100 300 Z" fill="url(#dmh-driver-rail-shape-fill)" stroke="url(#dmh-driver-rail-shape-stroke)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                  </svg>
                {quickLiveDrivers.map((driver) => {
                  const theme = mapStatusTheme(driver.statusKey);
                  const selected = driver.uid === selectedDriverId;
                  return (
                    <button
                      type="button"
                      key={driver.uid}
                      className={`dmh-live-driver-avatar${selected ? " is-selected" : ""}`}
                      title={`${driver.displayName} · ${theme.label}`}
                      aria-label={`定位至${driver.displayName}，${theme.label}`}
                      aria-pressed={selected}
                      onClick={() => handleSelectLiveDriver(driver.uid)}
                    >
                      <span className="dmh-live-driver-avatar-image">
                        {driver.avatarUrl ? <img src={driver.avatarUrl} alt="" /> : driver.displayName.slice(0, 1) || "司"}
                      </span>
                      <i className="dmh-live-driver-status" style={{ background: theme.color }} />
                    </button>
                  );
                })}
                  <button
                    type="button"
                    className={`dmh-live-driver-list-toggle${isDriverPickerOpen ? " is-active" : ""}`}
                    aria-label={isDriverPickerOpen ? "關閉成員清單" : "開啟成員清單"}
                    aria-expanded={isDriverPickerOpen}
                    aria-controls="dmh-live-driver-picker"
                    onClick={() => setIsDriverPickerOpen((open) => !open)}
                  >
                    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                      <path d="M4 5h12M4 10h12M4 15h12" />
                    </svg>
                  </button>
                </div>
                {isDriverPickerOpen && (
                  <div
                    id="dmh-live-driver-picker"
                    className="dmh-live-driver-picker"
                    role="dialog"
                    aria-modal="false"
                    aria-label="成員清單"
                    onWheelCapture={(event) => event.stopPropagation()}
                  >
                    <div className="dmh-live-driver-picker-head">
                      <div>
                        <strong>成員清單</strong>
                        <span>{drivers.length} 位成員・{drivers.filter((driver) => driver.coordinate).length} 位有定位</span>
                      </div>
                      <button
                        type="button"
                        aria-label="關閉成員清單"
                        onClick={() => setIsDriverPickerOpen(false)}
                      >
                        ×
                      </button>
                    </div>
                    <div className="dmh-live-driver-picker-list" tabIndex={0} aria-label="捲動成員清單">
                      {drivers.map((driver) => {
                        const theme = mapStatusTheme(driver.statusKey);
                        const selected = driver.uid === selectedDriverId;
                        return (
                          <button
                            type="button"
                            key={driver.uid}
                            className={`dmh-live-driver-picker-row${selected ? " is-selected" : ""}`}
                            aria-pressed={selected}
                            onClick={() => handleSelectLiveDriver(driver.uid)}
                          >
                            <span className="dmh-live-driver-picker-avatar">
                              {driver.avatarUrl ? <img src={driver.avatarUrl} alt="" /> : driver.displayName.slice(0, 1) || "司"}
                            </span>
                            <span className="dmh-live-driver-picker-name">
                              <strong>{driver.displayName}</strong>
                              {!driver.coordinate && <small>尚無定位</small>}
                            </span>
                            <span
                              className="dmh-live-driver-picker-status"
                              style={{ color: theme.color, background: theme.soft }}
                            >
                              {theme.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
            {viewMode === "track" && selectedDriverId && (
              <div
                className={`dmh-track-timeline${isDetailRangeMenuOpen ? " is-range-menu-open" : ""}`}
                style={mapExpanded ? { left: 46, right: 46 } : undefined}
                aria-label="歷史軌跡時間軸"
              >
                <div className="dmh-track-timeline-top">
                  <div className="dmh-track-timeline-date-control" onClick={(event) => event.stopPropagation()}>
                    <button type="button" className="dmh-track-timeline-date-step" onClick={() => shiftTrackDate(-1)} aria-label="前一天" title="前一天">‹</button>
                    <label className="dmh-track-timeline-date" aria-label="選擇歷史軌跡日期">
                      <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="5" width="17" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8"/><path d="M7.5 3v4M16.5 3v4M4 9.5h16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M8 13h2v2H8zM12 13h2v2h-2zM16 13h1v2h-1zM8 17h2v2H8z" fill="currentColor"/></svg>
                      <span>{mapDateKey(trackDate).replace(/-/g, "/")}</span>
                      <input
                        type="date"
                        value={mapDateKey(trackDate)}
                        max={mapDateKey(new Date())}
                        onChange={(event) => setTrackDate(new Date(`${event.target.value}T12:00:00`))}
                        aria-label="選擇歷史軌跡日期"
                      />
                    </label>
                    <button type="button" className="dmh-track-timeline-date-step" onClick={() => shiftTrackDate(1)} aria-label="後一天" title="後一天">›</button>
                  </div>
                  <strong className="dmh-track-timeline-clock">
                    {playbackPoint ? mapDateTime(playbackPoint.observedAt).slice(-5) : "--:--"}
                  </strong>
                  <div className="dmh-track-timeline-top-actions">
                    <button
                      type="button"
                      className={`dmh-track-timeline-follow${isPlaybackFollowEnabled ? " is-active" : ""}`}
                      aria-label={isPlaybackFollowEnabled ? "停止跟隨成員" : "跟隨成員"}
                      aria-pressed={isPlaybackFollowEnabled}
                      title={isPlaybackFollowEnabled ? "停止跟隨成員" : "跟隨成員"}
                      disabled={!playbackPoint}
                      onClick={(event) => {
                        event.stopPropagation();
                        setIsPlaybackFollowEnabled((enabled) => !enabled);
                      }}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4M2 12h4m12 0h4M5.64 5.64l2.83 2.83m7.06 7.06 2.83 2.83m0-12.72-2.83 2.83m-7.06 7.06-2.83 2.83"/></svg>
                    </button>
                    <button
                      type="button"
                      className="dmh-track-timeline-more"
                      aria-label="設定精確位置時間區間"
                      aria-expanded={isDetailRangeMenuOpen}
                      aria-controls="dmh-track-range-popover"
                      title="精確位置"
                      onClick={toggleDetailRangeMenu}
                    >
                      ⋯
                    </button>
                  </div>
                </div>
                <div className="dmh-track-timeline-player-row" aria-label="歷史軌跡播放控制">
                  <div className="dmh-track-timeline-transport">
                    <button type="button" className="dmh-track-timeline-seek" onClick={() => seekPlaybackBySeconds(-10)} disabled={playbackPoints.length < 2} aria-label="倒退 10 秒" title="倒退 10 秒">
                      <span aria-hidden="true">↶</span><small>10</small>
                    </button>
                    <button
                      type="button"
                      className="dmh-track-timeline-play"
                      onClick={() => {
                        if (playbackPoints.length < 2) return;
                        if (playbackProgress >= 1) setPlaybackProgress(0);
                        setIsPlaybackPlaying((playing) => !playing);
                      }}
                      disabled={playbackPoints.length < 2}
                      aria-label={isPlaybackPlaying ? "暫停播放" : "開始播放"}
                      title={isPlaybackPlaying ? "暫停" : "播放"}
                    >
                      {isPlaybackPlaying ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zM15 5h4v14h-4z" fill="currentColor"/></svg>
                      ) : (
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>
                      )}
                    </button>
                    <button type="button" className="dmh-track-timeline-seek" onClick={() => seekPlaybackBySeconds(10)} disabled={playbackPoints.length < 2} aria-label="快轉 10 秒" title="快轉 10 秒">
                      <span aria-hidden="true">↷</span><small>10</small>
                    </button>
                  </div>
                  <div className="dmh-track-timeline-slider">
                    <input
                      className="dmh-track-range"
                      type="range"
                      min="0"
                      max="1"
                      step={playbackStep}
                      value={playbackProgress}
                      disabled={playbackPoints.length < 2}
                      onChange={(event) => {
                        setIsPlaybackPlaying(false);
                        setPlaybackProgress(Number(event.target.value));
                      }}
                      aria-label="歷史軌跡播放進度"
                    />
                    <div className="dmh-track-timeline-time-labels">
                      <span>{detailTrackPoints.length ? mapDateTime(detailTrackPoints[0].observedAt).slice(-5) : trackSummary.firstAt ? mapDateTime(trackSummary.firstAt).slice(-5) : "--:--"}</span>
                      <span>{detailTrackPoints.length ? mapDateTime(detailTrackPoints[detailTrackPoints.length - 1].observedAt).slice(-5) : trackSummary.lastAt ? mapDateTime(trackSummary.lastAt).slice(-5) : "--:--"}</span>
                    </div>
                  </div>
                  <select className="dmh-track-timeline-speed" value={playbackSpeed} onChange={(event) => setPlaybackSpeed(Number(event.target.value))} aria-label="播放速度">
                    <option value="0.5">0.5×</option>
                    <option value="1">1×</option>
                    <option value="2">2×</option>
                    <option value="4">4×</option>
                  </select>
                </div>
                {isDetailRangeMenuOpen && (
                  <div
                    id="dmh-track-range-popover"
                    className="dmh-track-range-popover"
                    role="dialog"
                    aria-label="精確位置時間區間"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className="dmh-track-range-popover-head">
                      <div>
                        <strong>精確位置</strong>
                      </div>
                      <button
                        type="button"
                        className="dmh-track-range-popover-close"
                        aria-label="關閉精確位置設定"
                        onClick={() => setIsDetailRangeMenuOpen(false)}
                      >
                        ×
                      </button>
                    </div>
                    <div className="dmh-track-range-popover-fields">
                      <label>
                        <span>開始時間</span>
                        <div className="dmh-track-time-control">
                          <select aria-label="開始時間（時）" value={detailStartTime.slice(0, 2)} disabled={detailLoading} onChange={(event) => { detailRangeDefaultsKeyRef.current = `${selectedDriverId}:${mapDateKey(trackDate)}`; setDetailStartTime(`${event.target.value}:${detailStartTime.slice(3, 5) || "00"}`); }}>
                            <option value="" disabled>時</option>
                            {Array.from({length: 24}, (_, hour) => <option key={hour} value={String(hour).padStart(2, "0")}>{String(hour).padStart(2, "0")}</option>)}
                          </select>
                          <small>時</small><b aria-hidden="true">:</b>
                          <select aria-label="開始時間（分）" value={detailStartTime.slice(3, 5)} disabled={detailLoading} onChange={(event) => { detailRangeDefaultsKeyRef.current = `${selectedDriverId}:${mapDateKey(trackDate)}`; setDetailStartTime(`${detailStartTime.slice(0, 2) || "00"}:${event.target.value}`); }}>
                            <option value="" disabled>分</option>
                            {Array.from({length: 60}, (_, minute) => <option key={minute} value={String(minute).padStart(2, "0")}>{String(minute).padStart(2, "0")}</option>)}
                          </select>
                          <small>分</small>
                        </div>
                      </label>
                      <label>
                        <span>結束時間</span>
                        <div className="dmh-track-time-control">
                          <select aria-label="結束時間（時）" value={detailEndTime.slice(0, 2)} disabled={detailLoading} onChange={(event) => { detailRangeDefaultsKeyRef.current = `${selectedDriverId}:${mapDateKey(trackDate)}`; setDetailEndTime(`${event.target.value}:${detailEndTime.slice(3, 5) || "00"}`); }}>
                            <option value="" disabled>時</option>
                            {Array.from({length: 24}, (_, hour) => <option key={hour} value={String(hour).padStart(2, "0")}>{String(hour).padStart(2, "0")}</option>)}
                          </select>
                          <small>時</small><b aria-hidden="true">:</b>
                          <select aria-label="結束時間（分）" value={detailEndTime.slice(3, 5)} disabled={detailLoading} onChange={(event) => { detailRangeDefaultsKeyRef.current = `${selectedDriverId}:${mapDateKey(trackDate)}`; setDetailEndTime(`${detailEndTime.slice(0, 2) || "00"}:${event.target.value}`); }}>
                            <option value="" disabled>分</option>
                            {Array.from({length: 60}, (_, minute) => <option key={minute} value={String(minute).padStart(2, "0")}>{String(minute).padStart(2, "0")}</option>)}
                          </select>
                          <small>分</small>
                        </div>
                      </label>
                    </div>
                    <div className="dmh-track-range-popover-actions">
                      {detailTrackPoints.length > 0 && <small>已載入 {detailTrackPoints.length.toLocaleString()} 個原始定位點。</small>}
                      <div className="dmh-track-range-popover-buttons">
                        {detailTrackPoints.length > 0 && (
                          <button type="button" className="is-secondary" onClick={clearDetailTrackRange} disabled={detailLoading}>
                            清除
                          </button>
                        )}
                        <button type="button" onClick={loadDetailTrackRange} disabled={detailLoading || !detailStartTime || !detailEndTime}>
                          {detailLoading ? "載入中…" : "載入精確位置"}
                        </button>
                      </div>
                    </div>
                    {detailError && <small className="is-error" role="alert">{detailError}</small>}
                  </div>
                )}
              </div>
            )}
            {!mapDataReady && <div className="dmh-map-ready-overlay"><span className="dmh-spinner" /><strong>正在準備地圖位置…</strong><small>資料與圖片載入完成後顯示</small></div>}
            {viewMode === "live" && mapDataReady && !activeItems.some((item) => item.coordinate) && <div className="dmh-map-empty">目前篩選條件沒有可顯示的位置</div>}
            {overviewMode === "vehicles" && vehicleLoading && <div className="dmh-map-empty">正在讀取車輛位置…</div>}
            {viewMode === "track" && trackLoading && <div className="dmh-map-empty">正在讀取歷史軌跡…</div>}
            {viewMode === "track" && !trackLoading && !trackPoints.length && <div className="dmh-map-empty">{trackError || "這一天沒有歷史軌跡"}</div>}
            {(dataError || vehicleError) && <div className="dmh-map-error">{dataError || vehicleError}</div>}
            {viewMode === "track" && selectedHistoryMarker?.type === "inspection" && inspectionDetail ? (() => {
              const isException = inspectionDetail.eventType === "inspection_exception_reported";
              const accent = isException ? "#C7473A" : "#287EAA";
              const eventTitle = mapText(inspectionDetail.title || inspectionDetail.eventTitle) || "巡檢紀錄";
              const note = mapText(inspectionDetail.note || inspectionDetail.notePreview || inspectionDetail.reason);
              const media = Array.isArray(inspectionDetail.media) ? inspectionDetail.media : [];
              return (
                <div role="dialog" aria-label="巡檢紀錄詳情" style={{ position: "absolute", zIndex: 1200, top: mapExpanded ? 104 : 14, right: 14, width: "min(370px, calc(100% - 28px))", maxHeight: "calc(100% - 28px)", overflowY: "auto", border: "1px solid #E5EAF0", borderRadius: 18, background: "#FFFFFF", boxShadow: "0 18px 44px rgba(15,23,42,.22)", color: "#17212B" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px 10px", borderBottom: "1px solid #EEF1F4" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 9px", borderRadius: 20, background: isException ? "#FFF0EE" : "#EAF4FA", color: accent, fontSize: 11, fontWeight: 800 }}>
                        {isException ? "異常回報" : "巡檢紀錄"} · {selectedHistoryMarker.record.markerLabel}
                      </span>
                      <strong style={{ display: "block", marginTop: 7, fontSize: 16, lineHeight: 1.3, fontWeight: 800 }}>{eventTitle}</strong>
                      <span style={{ display: "block", marginTop: 3, color: "#667085", fontSize: 11, lineHeight: 1.4 }}>
                        {mapText(inspectionDetail.applicationTitle || selectedHistoryMarker.record.title)} · {mapDateTime(inspectionDetail.occurredAt || selectedHistoryMarker.record.observedAt)}
                      </span>
                    </div>
                    <button type="button" onClick={closeInspectionDetail} aria-label="關閉巡檢詳情" style={{ width: 30, height: 30, flex: "0 0 30px", border: 0, borderRadius: 15, background: "#F2F4F7", color: "#475467", fontSize: 20, lineHeight: 1, cursor: "pointer" }}>×</button>
                  </div>
                  <div style={{ padding: 12 }}>
                    {inspectionDetailLoading && <div style={{ marginBottom: 8, color: "#667085", fontSize: 12 }}>正在載入完整紀錄與附件…</div>}
                    {inspectionDetailError && <div style={{ marginBottom: 8, color: "#B54708", fontSize: 11 }}>{inspectionDetailError}</div>}
                    {note ? <div style={{ marginBottom: 9, whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: "#344054", fontSize: 13, lineHeight: 1.5 }}>{note}</div> : null}
                    {media.length ? (
                      <div style={{ display: "grid", gridTemplateColumns: media.length === 1 ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))", gap: 8, marginBottom: 9 }}>
                        {media.map((item, index) => item?.mediaType === "video" ? (
                          <video key={`${item.assetId || "video"}_${index}`} controls preload="metadata" src={item.originalUrl || item.previewUrl} style={{ width: "100%", minHeight: 100, maxHeight: 140, borderRadius: 10, background: "#1F2937", objectFit: "cover" }} />
                        ) : (
                          <a key={`${item?.assetId || "image"}_${index}`} href={item?.originalUrl || item?.previewUrl || undefined} target="_blank" rel="noreferrer" style={{ display: "block", overflow: "hidden", minHeight: 100, borderRadius: 10, background: "#F1F3F5" }}>
                            {item?.previewUrl || item?.originalUrl ? <img src={item.previewUrl || item.originalUrl} alt={mapText(item?.originalName) || "巡檢照片"} style={{ display: "block", width: "100%", height: 136, objectFit: "cover" }} /> : <span style={{ display: "grid", height: 100, placeItems: "center", color: "#98A2B3", fontSize: 11 }}>無法載入照片</span>}
                          </a>
                        ))}
                      </div>
                    ) : null}
                    {selectedInspectionLat !== null && selectedInspectionLng !== null ? (
                      <div style={{ paddingTop: 8, borderTop: "1px solid #EEF1F4", color: "#667085", fontSize: 11, lineHeight: 1.45 }}>
                        <strong style={{ color: "#344054" }}>紀錄位置</strong><br />
                        {selectedInspectionLat.toFixed(6)}, {selectedInspectionLng.toFixed(6)}
                        {selectedInspectionAccuracy !== null ? ` · 精度約 ${Math.round(selectedInspectionAccuracy)} 公尺` : ""}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })() : null}
            {viewMode === "track" && selectedHistoryMarker?.type === "station" ? (
              <div role="dialog" aria-label="工作站點資訊" style={{ position: "absolute", zIndex: 1200, top: mapExpanded ? 104 : 14, right: 14, width: "min(390px, calc(100% - 28px))", maxHeight: mapExpanded ? "calc(100% - 118px)" : "calc(100% - 28px)", minHeight: 0, boxSizing: "border-box", overflowY: "auto", overscrollBehavior: "contain", border: "1px solid #E5EAF0", borderRadius: 18, background: "#FFFFFF", boxShadow: "0 18px 44px rgba(15,23,42,.22)", color: "#17212B" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "12px 14px", borderBottom: "1px solid #EEF1F4" }}>
                  <strong style={{ color: "#23384A", fontSize: 15 }}>站點資訊</strong>
                  <button type="button" onClick={closeInspectionDetail} aria-label="關閉工作站詳情" style={{ width: 30, height: 30, flex: "0 0 30px", border: 0, borderRadius: 15, background: "#F2F4F7", color: "#475467", fontSize: 20, lineHeight: 1, cursor: "pointer" }}>×</button>
                </div>
                <div style={{ display: "grid", gap: 14, padding: 12 }}>
                  {selectedHistoryWorkStations.length ? selectedHistoryWorkStations.map((section) => (
                    <section key={section.key} style={{ display: "grid", gap: 11, padding: 12, border: "1px solid #D6E0E8", borderLeft: "4px solid #287EAA", borderRadius: 12, background: "#F8FAFC", boxShadow: "0 4px 12px rgba(15,23,42,.06)" }}>
                      <strong style={{ color: "#23384A", fontSize: 14, lineHeight: 1.4 }}>{section.stopName}</strong>
                      <div style={{ display: "grid", gap: 4 }}>
                        <strong style={{ color: "#667085", fontSize: 10 }}>地址</strong>
                        <span style={{ color: "#344054", fontSize: 11, lineHeight: 1.45 }}>{section.address || "尚未提供地址"}</span>
                      </div>
                      <div style={{ display: "grid", gap: 6 }}>
                        <strong style={{ color: "#344054", fontSize: 11 }}>聯絡人</strong>
                        {section.recipients.length ? section.recipients.map((contact, index) => {
                          const name = mapText(contact?.name) || "尚未提供";
                          const email = mapText(contact?.email || contact?.emailNormalized);
                          const phone = mapText(contact?.phone || contact?.mobile);
                          const company = mapText(contact?.companyName || contact?.company);
                          const signature = contact?.proof?.signature;
                          return (
                            <article key={mapText(contact?.id || contact?.recipientId) || `${section.key}_contact_${index}`} style={{ display: "grid", gap: 5, padding: 9, borderRadius: 10, background: "#F8FAFC" }}>
                              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                                <strong style={{ color: "#253B4D", fontSize: 12 }}>{name}</strong>
                                <span style={{ color: "#287EAA", fontSize: 10, fontWeight: 800 }}>{section.roleLabel}</span>
                              </div>
                              {email ? <span style={{ overflowWrap: "anywhere", color: "#475467", fontSize: 11 }}>{email}</span> : null}
                              {phone || company ? <span style={{ color: "#667085", fontSize: 10 }}>{[phone, company].filter(Boolean).join(" · ")}</span> : null}
                              {signature ? (
                                <div style={{ display: "grid", gap: 5 }}>
                                  <strong style={{ color: "#667085", fontSize: 10 }}>簽收</strong>
                                  {renderHistoryWorkPhotoStrip([signature], "簽收", `${section.key}_signature_${index}`)}
                                </div>
                              ) : null}
                            </article>
                          );
                        }) : <span style={{ color: "#98A2B3", fontSize: 11 }}>尚無聯絡人資料</span>}
                      </div>
                      <div style={{ display: "grid", gap: 7 }}>
                        <strong style={{ color: "#344054", fontSize: 11 }}>貨物資訊</strong>
                        {section.cargoItems.length ? section.cargoItems.map((item, index) => {
                          const itemName = mapText(item?.name || item?.itemName || item?.cargoName) || "未命名貨物";
                          const cargoPhotos = Array.isArray(item?.photos) ? item.photos : [];
                          const executionPhotos = Array.isArray(item?.execution?.photos) ? item.execution.photos : [];
                          return (
                            <article key={mapText(item?.id || item?.contentId) || `${section.key}_cargo_${index}`} style={{ display: "grid", gap: 7, padding: 9, borderRadius: 10, background: "#F8FAFC" }}>
                              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                                <strong style={{ minWidth: 0, color: "#344054", fontSize: 11, overflowWrap: "anywhere" }}>{itemName}</strong>
                                <strong style={{ flex: "0 0 auto", color: "#253B4D", fontSize: 11 }}>{item?.quantity ?? item?.totalQuantity ?? item?.qty ?? 0} {mapText(item?.unit) || "件"}</strong>
                              </div>
                              {cargoPhotos.length ? (
                                <div style={{ display: "grid", gap: 5 }}>
                                  <strong style={{ color: "#667085", fontSize: 10 }}>貨物照片</strong>
                                  {renderHistoryWorkPhotoStrip(cargoPhotos, "貨物照片", `${section.key}_cargo_${index}`)}
                                </div>
                              ) : null}
                              {executionPhotos.length ? (
                                <div style={{ display: "grid", gap: 5 }}>
                                  <strong style={{ color: "#667085", fontSize: 10 }}>成員執行照片</strong>
                                  {renderHistoryWorkPhotoStrip(executionPhotos, "成員執行照片", `${section.key}_execution_${index}`)}
                                </div>
                              ) : null}
                            </article>
                          );
                        }) : <span style={{ color: "#98A2B3", fontSize: 11 }}>尚無貨物明細</span>}
                      </div>
                    </section>
                  )) : <div style={{ padding: 12, color: "#667085", fontSize: 12 }}>目前無法讀取這個工作站點的詳細資料。</div>}
                </div>
                {historyWorkPhotoViewer ? (
                  <div role="dialog" aria-modal="true" aria-label={historyWorkPhotoViewer.category} onClick={() => setHistoryWorkPhotoViewer(null)} style={{ position: "fixed", zIndex: 1300, inset: 0, display: "grid", placeItems: "center", padding: 20, background: "rgba(15,23,42,.78)" }}>
                    <button type="button" onClick={() => setHistoryWorkPhotoViewer(null)} aria-label="關閉原圖" style={{ position: "absolute", top: 16, right: 16, width: 40, height: 40, border: 0, borderRadius: 20, background: "#FFFFFF", color: "#334155", fontSize: 24, cursor: "pointer" }}>×</button>
                    <img src={historyWorkPhotoViewer.src} alt={historyWorkPhotoViewer.alt} onClick={(event) => event.stopPropagation()} style={{ display: "block", maxWidth: "min(100%, 1100px)", maxHeight: "calc(100vh - 40px)", objectFit: "contain", borderRadius: 10, background: historyWorkPhotoViewer.category === "簽收" ? "#EEF1F3" : "#111827" }} />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>

        <aside className={`dmh-fleet-panel${viewMode === "track" ? " is-track-panel" : ""}`}>
          {overviewMode === "drivers" && viewMode === "track" ? (
            <>
              <div className="dmh-driver-detail-head">
                <button
                  type="button"
                  className="dmh-driver-detail-back"
                  onClick={() => {
                    setSelectedDriverId("");
                    setViewMode("live");
                  }}
                >
                  ‹ 成員列表
                </button>
                {selectedDriver ? (
                  <div className="dmh-driver-detail-profile">
                    <span className="dmh-driver-detail-avatar">
                      {selectedDriver.avatarUrl ? <img src={selectedDriver.avatarUrl} alt="" /> : selectedDriver.displayName.slice(0, 1) || "司"}
                      <i style={{ background: mapStatusTheme(selectedDriver.statusKey).color }} />
                    </span>
                    <div>
                      <div className="dmh-driver-detail-name-row">
                        <strong>{selectedDriver.displayName}</strong>
                        <em style={{ color: mapStatusTheme(selectedDriver.statusKey).color, background: mapStatusTheme(selectedDriver.statusKey).soft }}>{mapStatusTheme(selectedDriver.statusKey).label}</em>
                      </div>
                      <span>{selectedDriver.phone || "未提供電話"}</span>
                    </div>
                  </div>
                ) : <div className="dmh-track-no-driver">請先從即時位置選擇一位成員</div>}
              </div>
              {selectedApplicationLoading ? (
                <div className="dmh-driver-detail-empty">正在讀取目前工作…</div>
              ) : selectedApplicationForDriver ? (
                <div
                  className="dmh-route-summary dmh-route-summary-clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpenJob?.(selectedApplicationForDriver)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onOpenJob?.(selectedApplicationForDriver);
                    }
                  }}
                >
                  <span className="dmh-route-summary-icon">⌖</span>
                  <div>
                    <strong>{mapApplicationTitle(selectedApplicationForDriver)}</strong>
                    <span>{mapApplicationRoute(selectedApplicationForDriver)}</span>
                  </div>
                  <span className="dmh-route-summary-arrow" aria-hidden="true">›</span>
                </div>
              ) : (
                <div className="dmh-driver-detail-empty">
                  {selectedApplicationError || "目前沒有進行中的工作"}
                </div>
              )}
              <div className="dmh-track-stats dmh-track-stats-rows">
                <div>
                  <span>行駛距離</span>
                  <strong>{trackSummary.distance ? `${trackSummary.distance.toFixed(1)} km` : "0 km"}</strong>
                </div>
                <div>
                  <span>平均／最高速度</span>
                  <strong>{trackSummary.averageSpeed === null ? "—" : `${formatSpeedKmh(trackSummary.averageSpeed)} / ${formatSpeedKmh(trackSummary.maxSpeed)} km/h`}</strong>
                </div>
                <div>
                  <span>停留</span>
                  <strong>{formatSummaryDuration(trackSummary.stopSeconds)}</strong>
                  <small>{trackSummary.stopCount} 次</small>
                </div>
                <div>
                  <span>休息</span>
                  <strong>{formatSummaryDuration(trackSummary.restSeconds)}</strong>
                  <small>{trackSummary.restCount} 次</small>
                </div>
              </div>
              {currentHistoryState && (
                <section className={`dmh-current-history-state is-${currentHistoryState.type}`} aria-live="polite">
                  <span className="dmh-current-history-icon">{currentHistoryState.type === "rest" ? "休" : "停"}</span>
                  <div>
                    <strong>{currentHistoryState.type === "rest" ? "目前休息" : "目前停留"}</strong>
                    <b>{formatElapsedDuration(currentHistoryState.record.durationSeconds)}</b>
                    <span>開始於 {formatRelativeTime(currentHistoryState.type === "rest" ? currentHistoryState.record.restStartTime : currentHistoryState.record.stopStartTime)}</span>
                  </div>
                </section>
              )}
              <section className="dmh-history-section" aria-label="自動停留紀錄">
                <div className="dmh-track-section-heading">
                  <div>
                    <strong>自動停留</strong>
                  </div>
                  <b>{trackSummary.stopCount}</b>
                </div>
                {visibleTrackStops.length ? (
                  <div className="dmh-history-record-list">
                    {visibleTrackStops.map((stop) => (
                      <button
                        type="button"
                        className={`dmh-history-record${selectedHistoryMarker?.type === "stop" && selectedHistoryMarker.record.id === stop.id ? " is-selected" : ""}`}
                        key={stop.id}
                        onClick={() => setSelectedHistoryMarker({ type: "stop", record: stop })}
                      >
                        <span className="dmh-history-record-icon is-stop">停</span>
                        <div>
                          <strong>{historyStopLabel(stop, historyJobsById)}</strong>
                          <span>{mapDateTime(stop.stopStartTime)}{stop.endedAt ? ` ～ ${mapDateTime(stop.endedAt)}` : " ～ 持續中"}</span>
                        </div>
                        <em>{formatSummaryDuration(stop.durationSeconds)}</em>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="dmh-history-record-empty">這一天沒有自動停留紀錄</div>
                )}
              </section>
              <section className="dmh-history-section" aria-label="休息紀錄">
                <div className="dmh-track-section-heading">
                  <div>
                    <strong>休息紀錄</strong>
                    <span>成員手動按下休息到返回工作</span>
                  </div>
                  <b>{restRecords.length}</b>
                </div>
                {historyRestRecords.length ? (
                  <div className="dmh-history-record-list">
                    {historyRestRecords.map((record) => {
                      return (
                        <button
                          type="button"
                          className={`dmh-history-record${selectedHistoryMarker?.type === "rest" && selectedHistoryMarker.record.id === record.id ? " is-selected" : ""}`}
                          key={record.id}
                          onClick={() => setSelectedHistoryMarker({ type: "rest", record })}
                        >
                          <span className="dmh-history-record-icon is-rest">休</span>
                          <div>
                            <strong>手動休息</strong>
                            <span>{mapDateTime(record.restStartTime)}{record.restEndTime ? ` ～ ${mapDateTime(record.restEndTime)}` : " ～ 休息中"}</span>
                          </div>
                          <em>{formatSummaryDuration(record.durationSeconds)}</em>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="dmh-history-record-empty">這一天沒有手動休息紀錄</div>
                )}
              </section>
              <section className="dmh-history-section" aria-label="工作狀態紀錄">
                <div className="dmh-track-section-heading">
                  <div>
                    <strong>工作與站點時間線</strong>
                    <span>同一份工作收在一起，站點事件依站點整理</span>
                  </div>
                  <b>{historyTimelineItems.length}</b>
                </div>
                {historyTimelineItems.length ? (
                  <div className="dmh-history-work-list">
                    {historyTimelineItems.map((item) => item.type === "work" ? (
                      <article className="dmh-history-work-group" key={item.key}>
                        <div className="dmh-history-work-header">
                          <span className="dmh-history-work-icon">工</span>
                          <div className="dmh-history-work-title">
                            <strong>{item.title}</strong>
                            <span>
                              {historyClockTime(item.startAt)}–{historyClockTime(item.endAt)}
                              {` · ${item.statusLabel} · `}
                              {item.isInspection ? `${item.inspectionEventCount} 筆巡檢事件` : item.stations.length ? `${item.stations.length} 個站點` : "尚無站點紀錄"}
                            </span>
                            {item.routeLabel && <small>{item.routeLabel}</small>}
                          </div>
                          {item.job && (
                            <button
                              type="button"
                              className="dmh-history-work-open"
                              onClick={() => onOpenJob?.(item.job)}
                            >
                              查看工作 ›
                            </button>
                          )}
                        </div>
                        {item.stations.length > 0 && (
                          <div className="dmh-history-station-list">
                            {item.stations.map((station) => (
                              <div className="dmh-history-station" key={station.key}>
                                <div className="dmh-history-station-heading">
                                  <strong>{station.label}</strong>
                                  <span>
                                    {historyClockTime(station.events[0]?.observedAt)}
                                    {station.events.length > 1
                                      ? `–${historyClockTime(station.events[station.events.length - 1]?.observedAt)}`
                                      : ""}
                                  </span>
                                </div>
                                <div className="dmh-history-station-events">
                                  {station.events.map((event) => (
                                    <span className="dmh-history-station-event" key={event.id}>
                                      <time>{historyClockTime(event.observedAt)}</time>
                                      {historyEventLabel(event.eventType, event)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {item.otherEvents.length > 0 && (
                          <div className="dmh-history-work-other-events">
                            {item.otherEvents.map((event) => {
                              const isInspectionAction = ["inspection_record_added", "inspection_exception_reported"].includes(mapText(event?.eventType));
                              const content = <>
                                {historyClockTime(event.observedAt)} · {historyEventLabel(event.eventType, event)}
                                {event.eventTitle ? ` · ${event.eventTitle}` : ""}
                                {event.notePreview ? ` · ${event.notePreview}` : ""}
                              </>;
                              return isInspectionAction ? (
                                <button
                                  key={event.id}
                                  type="button"
                                  onClick={() => handleSelectInspectionTimelineEvent(event)}
                                  style={{ border: 0, borderBottom: "1px dashed rgba(100,116,139,.45)", padding: 0, background: "transparent", color: "inherit", font: "inherit", textAlign: "left", cursor: "pointer" }}
                                >
                                  {content}
                                </button>
                              ) : <span key={event.id}>{content}</span>;
                            })}
                          </div>
                        )}
                      </article>
                    ) : (
                      <div className="dmh-history-loose-event" key={item.key}>
                        <span className="dmh-history-work-icon is-event">●</span>
                        <div>
                          <strong>{historyEventLabel(item.event.eventType, item.event)}</strong>
                          <span>
                            {mapDateTime(item.event.observedAt)}
                            {item.event.stopName ? ` · ${item.event.stopName}` : ""}
                            {item.event.stopId ? ` · ${item.event.stopId}` : ""}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="dmh-history-record-empty">這一天沒有工作狀態紀錄</div>
                )}
              </section>
            </>
          ) : overviewMode === "drivers" && selectedDriver ? (
            <>
              <div className="dmh-driver-detail-head">
                <button
                  type="button"
                  className="dmh-driver-detail-back"
                  onClick={() => {
                    setSelectedDriverId("");
                    setViewMode("live");
                  }}
                >
                  ‹ 成員列表
                </button>
                <div className="dmh-driver-detail-profile">
                  <span className="dmh-driver-detail-avatar">
                    {selectedDriver.avatarUrl ? (
                      <img src={selectedDriver.avatarUrl} alt="" />
                    ) : (
                      selectedDriver.displayName.slice(0, 1) || "司"
                    )}
                    <i style={{ background: mapStatusTheme(selectedDriver.statusKey).color }} />
                  </span>
                  <div>
                    <div className="dmh-driver-detail-name-row">
                      <strong>{selectedDriver.displayName}</strong>
                      <em
                        style={{
                          color: mapStatusTheme(selectedDriver.statusKey).color,
                          background: mapStatusTheme(selectedDriver.statusKey).soft,
                        }}
                      >
                        {mapStatusTheme(selectedDriver.statusKey).label}
                      </em>
                    </div>
                    <span>{selectedDriver.phone || "未提供電話"}</span>
                  </div>
                </div>
              </div>
              {selectedApplicationLoading ? (
                <div className="dmh-driver-detail-empty">正在讀取目前工作…</div>
              ) : selectedApplicationForDriver ? (
                <div
                  className="dmh-route-summary dmh-route-summary-clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpenJob?.(selectedApplicationForDriver)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onOpenJob?.(selectedApplicationForDriver);
                    }
                  }}
                >
                  <span className="dmh-route-summary-icon">⌖</span>
                  <div>
                    <strong>{mapApplicationTitle(selectedApplicationForDriver)}</strong>
                    <span>{mapApplicationRoute(selectedApplicationForDriver)}</span>
                  </div>
                  <span className="dmh-route-summary-arrow" aria-hidden="true">›</span>
                </div>
              ) : (
                <div className="dmh-driver-detail-empty">
                  {selectedApplicationError || "目前沒有進行中的工作"}
                </div>
              )}
              <section aria-label="成員目前停留與休息狀態">
                {liveDriverStates.length ? (
                  liveDriverStates.map((state) => (
                    <div
                      className={`dmh-current-history-state is-${state.type}`}
                      key={state.type}
                      aria-live="polite"
                    >
                      <span className="dmh-current-history-icon">
                        {state.type === "rest" ? "休" : "停"}
                      </span>
                      <div>
                        <strong>{state.title}</strong>
                        <b>
                          {state.startedAt
                            ? formatElapsedDuration(
                                Math.max(
                                  0,
                                  Math.round((Date.now() - state.startedAt) / 1000),
                                ),
                              )
                            : "開始時間暫無資料"}
                        </b>
                        <span>
                          {state.startedAt
                            ? `開始於 ${formatRelativeTime(state.startedAt)}`
                            : "狀態已同步；開始時間尚未取得"}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="dmh-history-record-empty">
                    目前沒有自動停留或手動休息
                  </div>
                )}
              </section>
              <div className="dmh-driver-detail-facts">
                <div><span>目前狀態</span><strong>{mapStatusTheme(selectedDriver.statusKey).label}</strong></div>
                {selectedApplicationForDriver && <div><span>目前車輛</span><strong>{mapVehicleLabel(selectedApplicationForDriver) || "未提供"}</strong></div>}
                <div><span>最後更新</span><strong>{mapDateTime(selectedDriver.latestUpdate)}</strong></div>
              </div>
            </>
          ) : overviewMode === "drivers" ? (
            <>
              <div className="dmh-panel-title-row"><div><strong>目前成員</strong><span>{filteredDrivers.length} 位符合條件</span></div><span className="dmh-panel-total">{driverSummary.total}</span></div>
              <div className="dmh-search-box"><span>⌕</span><input value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="搜尋成員或電話" />{searchText && <button type="button" onClick={() => setSearchText("")}>×</button>}</div>
              <div className="dmh-filter-row">{driverFilters.map((filter) => <button type="button" key={filter.key} className={statusFilter === filter.key ? "is-active" : ""} onClick={() => setStatusFilter(filter.key)}>{filter.label}<b>{filter.count}</b></button>)}</div>
              <div className="dmh-driver-list">{filteredDrivers.map((driver) => { const theme = mapStatusTheme(driver.statusKey); const vehicleId = mapText(driver.raw?.currentVehicleId); const vehicleLookup = vehicleId ? driverVehiclesById[vehicleId] : null; const vehiclePlate = !vehicleId ? "尚未掛車" : !vehicleLookup ? "讀取車牌…" : vehicleLookup.error ? "車牌讀取失敗" : vehicleLookup.notFound ? "車輛不存在" : vehicleLookup.plateNumber || "車牌未設定"; return <button type="button" key={driver.uid} className={`dmh-driver-row${selectedDriverId === driver.uid ? " is-selected" : ""}`} onClick={() => { setSelectedDriverId(driver.uid); setViewMode("live"); }}><span className="dmh-avatar" style={{ background: theme.soft, color: theme.color }}>{driver.avatarUrl ? <img src={driver.avatarUrl} alt="" /> : driver.displayName.slice(0, 1) || "司"}<i style={{ background: theme.color }} /></span><span className="dmh-driver-copy"><span className="dmh-driver-name-row"><strong>{driver.displayName}</strong><em style={{ color: theme.color, background: theme.soft }}>{theme.label}</em></span><span className="dmh-driver-vehicle-row"><span className="dmh-driver-vehicle-plate" title={vehiclePlate}>{vehiclePlate}</span><span className="dmh-driver-updated">{formatDriverListTime(driver.latestUpdate)}</span></span></span><span className="dmh-chevron">›</span></button>; })}{!filteredDrivers.length && <div className="dmh-list-empty"><strong>沒有符合條件的成員</strong><span>請清除搜尋或切換狀態篩選。</span><button type="button" onClick={clearFilters}>清除篩選</button></div>}</div>
            </>
          ) : overviewMode === "vehicles" && selectedVehicle ? (
            <>
              <div className="dmh-driver-detail-head">
                <button
                  type="button"
                  className="dmh-driver-detail-back"
                  onClick={() => setSelectedAssetId("")}
                >
                  ‹ 車輛列表
                </button>
                <div className="dmh-vehicle-detail-profile">
                  <span className="dmh-vehicle-detail-avatar">
                    {selectedVehicle.photoUrl ? (
                      <img src={selectedVehicle.photoUrl} alt="" />
                    ) : (
                      <span>▰</span>
                    )}
                  </span>
                  <div>
                    <div className="dmh-driver-detail-name-row">
                      <strong>{vehicleDisplayIdentifier(selectedVehicle)}</strong>
                      <em
                        style={{
                          color: vehicleStatusTheme(selectedVehicle.statusKey).color,
                          background: vehicleStatusTheme(selectedVehicle.statusKey).soft,
                        }}
                      >
                        {vehicleStatusTheme(selectedVehicle.statusKey).label}
                      </em>
                    </div>
                    <span>{vehicleTypeLabel(selectedVehicle.assetType, selectedVehicle.assetKind)}</span>
                  </div>
                </div>
              </div>
              <div className="dmh-driver-detail-facts dmh-vehicle-detail-facts">
                {selectedVehicle.statusKey === "working" && (
                  <div>
                    <span>目前成員</span>
                    <strong>{selectedVehicle.currentDriverName || "未指定"}</strong>
                  </div>
                )}
                {selectedVehicle.statusKey === "parked" && (
                  <div>
                    <span>停放位置</span>
                    <strong>{selectedVehicle.parkedLocationName || "尚未設定位置"}</strong>
                  </div>
                )}
                <div>
                  <span>目前狀態</span>
                  <strong>{vehicleStatusTheme(selectedVehicle.statusKey).label}</strong>
                </div>
                <div>
                  <span>最後更新</span>
                  <strong>{formatMapTime(selectedVehicle.latestUpdate)}</strong>
                </div>
                <div>
                  <span>資產類型</span>
                  <strong>{vehicleTypeLabel(selectedVehicle.assetType, selectedVehicle.assetKind)}</strong>
                </div>
                {selectedVehicle.assetKind === "vehicle" && selectedVehicle.trailerBindingCount > 0 && (
                  <div>
                    <span>綁定拖車</span>
                    <strong>{selectedVehicle.trailerBindingCount} 台</strong>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="dmh-panel-title-row"><div><strong>目前車輛</strong></div><span className="dmh-panel-total">{vehicleSummary.total}</span></div>
              {selectedVehicle && <div className="dmh-current-focus-card"><span className="dmh-current-focus-avatar is-vehicle">{selectedVehicle.photoUrl ? <img src={selectedVehicle.photoUrl} alt="" /> : <span>▰</span>}</span><span className="dmh-current-focus-copy"><small>目前選取車輛</small><strong>{vehicleDisplayIdentifier(selectedVehicle)}</strong><span>{vehicleStatusTheme(selectedVehicle.statusKey).label}・{selectedVehicle.statusKey === "working" ? `成員 ${selectedVehicle.currentDriverName || "未指定"}` : selectedVehicle.parkedLocationName || "尚未設定位置"}</span></span><em className="dmh-current-focus-type">{vehicleTypeLabel(selectedVehicle.assetType, selectedVehicle.assetKind)}</em></div>}
              <div className="dmh-search-box"><span>⌕</span><input value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="搜尋車牌、資產名稱" />{searchText && <button type="button" onClick={() => setSearchText("")}>×</button>}</div>
              <div className="dmh-filter-row">{vehicleFilters.map((filter) => <button type="button" key={filter.key} className={vehicleFilter === filter.key ? "is-active" : ""} onClick={() => setVehicleFilter(filter.key)}>{filter.label}<b>{filter.count}</b></button>)}</div>
              {vehicleLoading ? <div className="dmh-list-loading"><span className="dmh-spinner" />正在讀取車輛狀態…</div> : <div className="dmh-vehicle-list">{filteredVehicles.map((vehicle) => { const theme = vehicleStatusTheme(vehicle.statusKey); return <button type="button" key={vehicle.assetId} className={`dmh-vehicle-row${selectedAssetId === vehicle.assetId ? " is-selected" : ""}`} onClick={() => setSelectedAssetId(vehicle.assetId)}><span className="dmh-vehicle-thumb">{vehicle.photoUrl ? <img src={vehicle.photoUrl} alt="" /> : <span>▰</span>}</span><span className="dmh-vehicle-copy"><span className="dmh-vehicle-name-row"><strong>{vehicleDisplayIdentifier(vehicle)}</strong>{vehicle.trailerBindingCount > 0 && vehicle.assetKind === "vehicle" ? <b className="dmh-trailer-badge">{vehicle.trailerBindingCount}</b> : null}</span><small>{vehicleTypeLabel(vehicle.assetType, vehicle.assetKind)}</small></span><span className="dmh-vehicle-status"><em style={{ color: theme.color, background: theme.soft }}><i style={{ background: theme.color }} />{theme.label}</em><small>{vehicle.statusKey === "working" ? `成員 ${vehicle.currentDriverName || "未指定"}` : vehicle.parkedLocationName || "尚未設定位置"}</small></span><span className="dmh-chevron">›</span></button>; })}{!filteredVehicles.length && !vehicleLoading && <div className="dmh-list-empty"><strong>沒有符合條件的車輛</strong><span>請清除搜尋或切換狀態篩選。</span><button type="button" onClick={clearFilters}>清除篩選</button></div>}</div>}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

const ACTIVE_ORDERS = [
  {
    name: "王大明",
    route: "台北 → 高雄",
    status: "執行中",
    statusColor: TOKENS.orange,
    statusBg: "rgba(246,139,44,0.18)",
    eta: "09:00",
  },
  {
    name: "李小龍",
    route: "台北 → 高雄",
    status: "待命中",
    statusColor: TOKENS.green,
    statusBg: "rgba(49,182,106,0.18)",
    eta: "29:30",
  },
  {
    name: "陳怡君",
    route: "台北 → 高雄",
    status: "維修中",
    statusColor: TOKENS.yellow,
    statusBg: "rgba(244,180,0,0.20)",
    eta: "10:00",
  },
];

export default function DriverManagerHome({ user, account }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [authUser, setAuthUser] = useState(user || auth.currentUser || null);
  const [authResolved, setAuthResolved] = useState(Boolean(user || auth.currentUser));
  const [activeMenu, setActiveMenu] = useState(() =>
    location.state?.[HISTORY_VIEW_KEY] || menuKeyFromPath(location.pathname, location.search),
  );
  const [fleetMapExpanded, setFleetMapExpanded] = useState(false);
  const [createChoiceOpen, setCreateChoiceOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const isCopyingJob = location.state?.copyJobMode === true;
  const [selectedChat, setSelectedChat] = useState(null);
  const [jobDateRange, setJobDateRange] = useState(() => {
    const today = mapDateKey(new Date());
    return { start: today, end: today };
  });
  const [quickLogisticsData, setQuickLogisticsData] = useState(null);
  const [cargoWorkPlan, setCargoWorkPlan] = useState(() => location.state?.cargoWorkPlan || null);
  const [sidebarOpen, setSidebarOpen] = useState(
    typeof window !== "undefined" && window.innerWidth >= 1280,
  );
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 980,
  );
  const contentRef = useRef(null);

  const sessionUser = user || authUser;
  const [driverProfile, setDriverProfile] = useState(null);
  const [driverProfileLoading, setDriverProfileLoading] = useState(true);

  const [notificationOpen, setNotificationOpen] = useState(false);
  const [chatPushToast, setChatPushToast] = useState(null);
  const activeChatRef = useRef({ activeMenu, selectedChat });

  useEffect(() => {
    activeChatRef.current = { activeMenu, selectedChat };
  }, [activeMenu, selectedChat]);

  useEffect(() => {
    const uid = mapText(sessionUser?.uid);
    if (!uid) {
      setDriverProfile(null);
      setDriverProfileLoading(!authResolved);
      return undefined;
    }

    setDriverProfileLoading(true);
    setDriverProfile(null);
    const profileRef = doc(db, "drivers", uid);
    return onSnapshot(
      profileRef,
      (snapshot) => {
        setDriverProfile(
          snapshot.exists() ? { uid, ...(snapshot.data() || {}) } : null,
        );
        setDriverProfileLoading(false);
      },
      (error) => {
        console.error("[DriverManagerHome] driver profile load error:", error);
        setDriverProfile(null);
        setDriverProfileLoading(false);
      },
    );
  }, [sessionUser?.uid, authResolved]);

  const effectiveOrgId = mapText(driverProfile?.orgId);
  const effectiveAccount = useMemo(() => {
    return {
      ...(account || {}),
      orgId: effectiveOrgId,
    };
  }, [account, effectiveOrgId]);

  const accountDisplayName =
    mapText(driverProfile?.displayName) ||
    mapText(sessionUser?.displayName) ||
    "管理員";
  const accountPhotoUrl = imageUrl(driverProfile || {});
  const accountInitial = accountDisplayName.slice(0, 1) || "管";

  const navigateView = (path, view, state = {}, options = {}) => {
    navigate(path, {
      ...options,
      state: {
        ...state,
        [HISTORY_VIEW_KEY]: view,
      },
    });
  };

  useEffect(() => {
    if (user) {
      setAuthUser(user);
      setAuthResolved(true);
      return undefined;
    }

    return onAuthStateChanged(auth, (firebaseUser) => {
      setAuthUser(firebaseUser || null);
      setAuthResolved(true);
    });
  }, [user]);

  useEffect(() => {
    const uid = mapText(sessionUser?.uid);
    if (!uid) return undefined;
    let active = true;
    let stopListening = () => {};

    subscribeToWebChatPushMessages((payload) => {
      if (!active) return;
      const currentChat = activeChatRef.current || {};
      if (
        currentChat.activeMenu === "chatRoom" &&
        currentChat.selectedChat?.orgId === payload.orgId &&
        currentChat.selectedChat?.chatId === payload.chatId
      ) return;
      setChatPushToast(payload);
    }, uid).then((unsubscribe) => {
      if (!active) {
        unsubscribe();
        return;
      }
      stopListening = unsubscribe;
    }).catch((error) => {
      console.warn("[DriverManagerHome] Web chat push listener unavailable:", error);
    });

    return () => {
      active = false;
      stopListening();
    };
  }, [sessionUser?.uid]);

  useEffect(() => {
    if (!chatPushToast) return undefined;
    const timeoutId = window.setTimeout(() => setChatPushToast(null), 10000);
    return () => window.clearTimeout(timeoutId);
  }, [chatPushToast]);

  useEffect(() => {
    const historyState = location.state || {};
    const nextMenu = historyState[HISTORY_VIEW_KEY] || menuKeyFromPath(location.pathname, location.search);
    const isJobDetail = nextMenu === "jobDetail" || nextMenu === "logisticsJobDetail";
    const isJobFormEdit = ["createInternal", "createInspection"].includes(nextMenu) && Boolean(historyState.selectedJob);
    const isLogisticsEdit = nextMenu === "createLogistics" && Boolean(historyState.selectedJob);
    if (historyState.cargoWorkPlan) {
      setCargoWorkPlan(historyState.cargoWorkPlan);
    } else if (!["createInternal", "createLogistics"].includes(nextMenu)) {
      setCargoWorkPlan(null);
    }
    setActiveMenu((currentMenu) => currentMenu === nextMenu ? currentMenu : nextMenu);
    if (nextMenu !== "overview") setFleetMapExpanded(false);

    if (!isJobDetail && !isJobFormEdit && !isLogisticsEdit) setSelectedJob(null);
    if (nextMenu !== "chats") setSelectedChat(null);
    if (isJobDetail || isJobFormEdit || isLogisticsEdit) {
      setSelectedJob(historyState.selectedJob || null);
    }
    if (nextMenu === "chatRoom") setSelectedChat(historyState.selectedChat || null);
    if (historyState.quickLogisticsData) setQuickLogisticsData(historyState.quickLogisticsData);
    setCreateChoiceOpen(false);

    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [location.key, location.pathname, location.search, location.state]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 980;
      setIsMobile(mobile);

      if (mobile) {
        setSidebarOpen(false);
      } else if (window.innerWidth >= 1280) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const stats = useMemo(
    () => [
      {
        label: "今日任務",
        value: "28",
        sub: "含已完成與進行中",
        color: TOKENS.orange,
        bg: "#FDE9DA",
        icon: "task",
      },
      {
        label: "執行中成員",
        value: "14",
        sub: "目前活躍派工",
        color: TOKENS.green,
        bg: "#DFF3E6",
        icon: "user",
      },
      {
        label: "待命成員",
        value: "9",
        sub: "可立即派工",
        color: TOKENS.blue,
        bg: "#DDEAFE",
        icon: "clock",
      },
      {
        label: "異常回報",
        value: "2",
        sub: "待處理事件",
        color: TOKENS.red,
        bg: "#FCE3E3",
        icon: "alert",
      },
    ],
    [],
  );

  const statusDistribution = useMemo(
    () => [
      { label: "執行中", value: 60, color: TOKENS.orange },
      { label: "待命中", value: 25, color: TOKENS.green },
      { label: "維修中", value: 15, color: TOKENS.yellow },
    ],
    [],
  );

  const donutGradient = useMemo(() => {
    const a = statusDistribution[0].value;
    const b = statusDistribution[1].value;
    return `conic-gradient(
      ${statusDistribution[0].color} 0% ${a}%,
      ${statusDistribution[1].color} ${a}% ${a + b}%,
      ${statusDistribution[2].color} ${a + b}% 100%
    }`;
  }, [statusDistribution]);

  const handleMenuChange = (menuKey) => {
    setCreateChoiceOpen(false);
    setSelectedJob(null);
    if (menuKey !== "overview") setFleetMapExpanded(false);
    if (menuKey !== "chatRoom") setSelectedChat(null);
    setActiveMenu(menuKey);

    const routeKey = MENU_ROUTE_KEYS[menuKey];
    if (routeKey) {
      navigateView(internalPathForFeature(routeKey), menuKey);
    } else if (menuKey === "cargoManagement") {
      navigateView(`${INTERNAL_ENTRY_BASE}?view=cargoManagement`, menuKey);
    } else if (menuKey === "deliveryDetails" && isLocalDeliveryDetailsEnabled()) {
      navigateView(`${INTERNAL_ENTRY_BASE}?view=deliveryDetails`, menuKey);
    } else if (menuKey === "overview") {
      navigateView(INTERNAL_ENTRY_BASE, menuKey);
    }

    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };

  const handleOpenChat = (chat) => {
    setCreateChoiceOpen(false);
    setSelectedChat(chat);
    setActiveMenu("chatRoom");
    navigateView(INTERNAL_ROUTES.chat, "chatRoom", { selectedChat: chat });
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const openChatFromPush = useCallback(async (payload, options = {}) => {
    const uid = mapText(sessionUser?.uid);
    const orgId = mapText(payload?.orgId);
    const chatId = mapText(payload?.chatId);
    if (!uid || !orgId || !chatId) return false;

    const snapshot = await getDoc(doc(db, "orgs", orgId, "chats", chatId));
    if (!snapshot.exists()) return false;
    if (typeof options.shouldContinue === "function" && !options.shouldContinue()) return false;

    const chat = snapshot.data() || {};
    const participantUids = Array.isArray(chat.participantUids)
      ? chat.participantUids.map(mapText).filter(Boolean)
      : [];
    if (!participantUids.includes(uid)) return false;

    const chatType = mapText(chat.chatType) || (participantUids.length > 2 ? "group" : "direct");
    const otherUid = participantUids.find((participantUid) => participantUid !== uid);
    const otherMeta = otherUid ? chat.participantMeta?.[otherUid] || {} : {};
    const title = chatType === "direct"
      ? mapText(otherMeta.displayName || otherMeta.name) || mapText(payload?.senderName) || mapText(chat.title) || "一對一聊天室"
      : mapText(chat.title) || mapText(payload?.chatTitle) || "車隊群組";
    const selectedChatFromPush = {
      orgId,
      chatId,
      chatType,
      title,
      inboxBucketId: mapText(chat.inboxBucketByUid?.[uid]),
    };

    setChatPushToast(null);
    setCreateChoiceOpen(false);
    setSelectedChat(selectedChatFromPush);
    setActiveMenu("chatRoom");
    if (contentRef.current) contentRef.current.scrollTop = 0;
    navigate(INTERNAL_ROUTES.chat, {
      replace: Boolean(options.replace),
      state: {
        selectedChat: selectedChatFromPush,
        [HISTORY_VIEW_KEY]: "chatRoom",
      },
    });
    return true;
  }, [navigate, sessionUser?.uid]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orgId = mapText(params.get("chatPushOrgId"));
    const chatId = mapText(params.get("chatPushChatId"));
    const uid = mapText(sessionUser?.uid);
    if (!orgId || !chatId || !authResolved || !uid) return undefined;

    let active = true;
    const clearPushQuery = () => {
      const cleanedParams = new URLSearchParams(location.search);
      cleanedParams.delete("chatPushOrgId");
      cleanedParams.delete("chatPushChatId");
      const cleanedSearch = cleanedParams.toString();
      navigate(`${location.pathname}${cleanedSearch ? `?${cleanedSearch}` : ""}${location.hash || ""}`, {
        replace: true,
        state: location.state,
      });
    };

    openChatFromPush({ orgId, chatId }, {
      replace: true,
      shouldContinue: () => active,
    }).then((opened) => {
      if (active && !opened) clearPushQuery();
    }).catch((error) => {
      if (!active) return;
      console.warn("[DriverManagerHome] notification chat could not be opened:", error);
      clearPushQuery();
    });

    return () => { active = false; };
  }, [authResolved, location.hash, location.pathname, location.search, location.state, navigate, openChatFromPush, sessionUser?.uid]);

  const handleCreateWork = (mode) => {
    setCargoWorkPlan(null);
    setSelectedJob(null);
    if (mode === "choose") {
      setCreateChoiceOpen(true);
      return;
    }
    if (mode === "quickLogistics") setQuickLogisticsData(null);
    setCreateChoiceOpen(false);
    const nextView = mode === "quickLogistics"
      ? "quickLogistics"
      : mode === "logistics"
        ? "createLogistics"
        : mode === "inspection"
          ? "createInspection"
          : "createInternal";
    setActiveMenu(nextView);
    navigateView(INTERNAL_ROUTES.job, nextView);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const handleCargoWorkPlan = (plan) => {
    if (!plan || !Array.isArray(plan.stops) || !plan.stops.length) return;
    setSelectedJob(null);
    setCreateChoiceOpen(false);
    setCargoWorkPlan(plan);
    const nextView = plan.mode === "single" ? "createInternal" : "createLogistics";
    setActiveMenu(nextView);
    navigateView(INTERNAL_ROUTES.job, nextView);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const handleCargoPlanCreated = async (result = {}) => {
    if (!cargoWorkPlan || result.editMode) return;
    const applicationId = mapText(result.applicationId || result.application?.applicationId || result.application?.id);
    const dateKey = mapText(result.dateKey || result.application?.dateKey || result.application?.schedule?.dateKey) || reportDayFromStartAt(result.application?.schedule?.startAt, "");
    try {
      await markCargoInventoryAssigned({
        orgId: effectiveOrgId,
        cargoRefs: cargoWorkPlan.cargoRefs,
        applicationId,
        dateKey,
      });
    } catch (error) {
      console.error("[DriverManagerHome] cargo assignment sync:", error);
      window.alert(`工作已建立，但貨物派單狀態同步失敗。請勿重複建立同一批貨物工作，並重新載入貨物管理後確認：${error?.message || "未知錯誤"}`);
    } finally {
      setCargoWorkPlan(null);
    }
  };

  const handleOpenJob = (application) => {
    const stops = Array.isArray(application?.stops)
      ? application.stops
      : Array.isArray(application?.routeSnapshot?.stops)
        ? application.routeSnapshot.stops
        : [];
    const inspectionMode = isInspectionApplication(application);
    const logistics = !inspectionMode && (
      String(application?.jobCategory || "")
        .trim()
        .toLowerCase() === "logistics" ||
      stops.length > 2 ||
      stops.some(
        (stop) => Array.isArray(stop?.cargoItems) && stop.cargoItems.length > 0,
      )
    );

    if (logistics) {
      setCreateChoiceOpen(false);
      setSelectedJob(application);
      navigateView(INTERNAL_ROUTES.job, "logisticsJobDetail", { selectedJob: application });
      setActiveMenu("logisticsJobDetail");
      if (contentRef.current) contentRef.current.scrollTop = 0;
      return;
    }

    if (!inspectionMode && stops.length !== 2) {
      window.alert(
        "這是多點工作，請使用多點工作詳情頁。單點工作必須只有起點與終點兩站。",
      );
      return;
    }

    setCreateChoiceOpen(false);
    setSelectedJob(application);
    navigateView(INTERNAL_ROUTES.job, "jobDetail", { selectedJob: application });
    setActiveMenu("jobDetail");
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const handleOpenCargoReport = async (report, reportOrgId) => {
    const applicationId = mapText(report?.applicationId);
    const dateKey = mapText(report?.workDateKey || report?.dateKey);
    if (!applicationId || !dateKey || !reportOrgId) {
      window.alert("缺少工作識別資料，無法開啟工作詳情。");
      return;
    }
    try {
      const snapshot = await getDoc(
        doc(db, "orgs", reportOrgId, "applications", dateKey, "jobs", applicationId),
      );
      if (!snapshot.exists()) {
        window.alert("找不到這筆貨物所屬的工作。");
        return;
      }
      handleOpenJob({
        id: snapshot.id,
        applicationId: snapshot.id,
        dateKey,
        ...snapshot.data(),
        orgId: reportOrgId,
        focusStopId: mapText(report?.stopId),
      });
    } catch (error) {
      console.error("[DriverManagerHome] open cargo report job:", error);
      window.alert("工作詳情開啟失敗，請稍後再試。");
    }
  };

  const handleEditJob = (application) => {
    const stops = Array.isArray(application?.stops)
      ? application.stops
      : Array.isArray(application?.routeSnapshot?.stops)
        ? application.routeSnapshot.stops
        : [];
    const inspectionMode = isInspectionApplication(application);
    const logistics = !inspectionMode && (
      mapText(application?.jobCategory).toLowerCase() === "logistics" ||
      stops.length > 2 ||
      stops.some((stop) => Array.isArray(stop?.cargoItems) && stop.cargoItems.length > 0)
    );
    setSelectedJob(application);
    setCreateChoiceOpen(false);
    const nextView = logistics
      ? "createLogistics"
      : inspectionMode
        ? "createInspection"
        : "createInternal";
    setActiveMenu(nextView);
    navigateView(INTERNAL_ROUTES.job, nextView, { selectedJob: application });
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };
  const handleCopyJob = (application) => {
    const stops = Array.isArray(application?.stops)
      ? application.stops
      : Array.isArray(application?.routeSnapshot?.stops)
        ? application.routeSnapshot.stops
        : [];
    const inspectionMode = isInspectionApplication(application);
    const logistics = !inspectionMode && (
      mapText(application?.jobCategory).toLowerCase() === "logistics" ||
      stops.length > 2 ||
      stops.some((stop) => Array.isArray(stop?.cargoItems) && stop.cargoItems.length > 0)
    );
    const nextView = logistics
      ? "createLogistics"
      : inspectionMode
        ? "createInspection"
        : "createInternal";
    setCargoWorkPlan(null);
    setSelectedJob(application);
    setCreateChoiceOpen(false);
    setActiveMenu(nextView);
    navigateView(INTERNAL_ROUTES.job, nextView, {
      selectedJob: application,
      copyJobMode: true,
    });
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };
  const handleDeleteJob = async (application) => {
    const applicationId = mapText(application?.applicationId || application?.id);
    const dateKey = mapText(application?.dateKey || application?.schedule?.dateKey);
    const orgId = mapText(account?.orgId || application?.orgId);
    if (!applicationId || !dateKey || !orgId) {
      window.alert("缺少工作識別資料，無法刪除。");
      return;
    }
    if (!window.confirm("刪除後會一併清除貨物索引、工作分片、照片、文件與成員執行媒體，確定要刪除嗎？")) return;
    try {
      await deleteJobWithDependencies({ orgId, dateKey, applicationId });
      setSelectedJob(null);
      handleMenuChange("internalJob");
    } catch (error) {
      console.error("[DriverManagerHome] delete job:", error);
      window.alert(`工作刪除失敗：${error?.message || "請稍後再試"}`);
    }
  };
  const renderRightContent = () => {
    switch (activeMenu) {
      case "internalJob":
        return (
          <DriverManagerJobList
            user={sessionUser}
            account={account}
            dateRange={jobDateRange}
            onDateRangeChange={setJobDateRange}
            onCreate={handleCreateWork}
            onOpenJob={handleOpenJob}
            onEditJob={handleEditJob}
            onCopyJob={handleCopyJob}
            onDeleteJob={handleDeleteJob}
          />
        );

      case "jobDetail":
        return (
          <DriverManagerJobDetail
            applicationId={selectedJob?.applicationId || selectedJob?.id}
            orgId={account?.orgId || selectedJob?.orgId}
            dateKey={selectedJob?.dateKey}
            initialApplication={selectedJob}
            initialStopId={selectedJob?.focusStopId}
            onBack={() => {
              setSelectedJob(null);
              handleMenuChange("internalJob");
            }}
            onEdit={(job) => {
              const nextJob = job || selectedJob;
              const nextView = isInspectionApplication(nextJob)
                ? "createInspection"
                : "createInternal";
              setSelectedJob(nextJob);
              setCreateChoiceOpen(false);
              setActiveMenu(nextView);
              navigateView(INTERNAL_ROUTES.job, nextView, { selectedJob: nextJob });
              if (contentRef.current) contentRef.current.scrollTop = 0;
            }}
          />
        );

      case "logisticsJobDetail":
        return (
          <DriverManagerLogisticsJobDetail
            applicationId={selectedJob?.applicationId || selectedJob?.id}
            orgId={account?.orgId || selectedJob?.orgId}
            dateKey={selectedJob?.dateKey}
            initialApplication={selectedJob}
            initialStopId={selectedJob?.focusStopId}
            onBack={() => {
              setSelectedJob(null);
              handleMenuChange("internalJob");
            }}
            onEdit={(job) => {
              const nextJob = job || selectedJob;
              setSelectedJob(nextJob);
              setCreateChoiceOpen(false);
              setActiveMenu("createLogistics");
              navigateView(INTERNAL_ROUTES.job, "createLogistics", { selectedJob: nextJob });
              if (contentRef.current) contentRef.current.scrollTop = 0;
            }}
          />
        );

      case "createInternal":
        return (
          <CreateInternalFleetJob
            user={sessionUser}
            account={effectiveAccount}
            orgId={effectiveOrgId}
            profileLoading={driverProfileLoading}
            initialApplication={selectedJob}
            copyMode={isCopyingJob}
            initialCargoPlan={cargoWorkPlan?.mode === "single" ? cargoWorkPlan : null}
            initialStopId={isCopyingJob ? "" : selectedJob?.focusStopId}
            onBack={() => {
              if (isCopyingJob) {
                handleMenuChange("internalJob");
                return;
              }
              if (selectedJob) {
                setActiveMenu("jobDetail");
                navigateView(INTERNAL_ROUTES.job, "jobDetail", { selectedJob });
                if (contentRef.current) contentRef.current.scrollTop = 0;
                return;
              }
              if (cargoWorkPlan) {
                setCargoWorkPlan(null);
                handleMenuChange("cargoManagement");
                return;
              }
              handleMenuChange("internalJob");
            }}
            onCreated={async (result = {}) => {
              await handleCargoPlanCreated(result);
              if (isCopyingJob) {
                handleMenuChange("internalJob");
                return;
              }
              const { applicationId, dateKey, application, editMode } = result;
              if (selectedJob && editMode) {
                const nextApplication = {
                  ...selectedJob,
                  ...(application || {}),
                  id: applicationId || application?.id || selectedJob.id,
                  applicationId: applicationId || application?.applicationId || selectedJob.applicationId,
                  dateKey: dateKey || application?.dateKey || selectedJob.dateKey,
                };
                setSelectedJob(nextApplication);
                setActiveMenu("jobDetail");
                navigateView(INTERNAL_ROUTES.job, "jobDetail", { selectedJob: nextApplication });
                if (contentRef.current) contentRef.current.scrollTop = 0;
                return;
              }
              if (selectedJob) {
                setActiveMenu("jobDetail");
                navigateView(INTERNAL_ROUTES.job, "jobDetail", { selectedJob });
                if (contentRef.current) contentRef.current.scrollTop = 0;
                return;
              }
              handleMenuChange("internalJob");
            }}
          />
        );

      case "createInspection":
        return (
          <CreateInspectionFleetJob
            user={sessionUser}
            account={account}
            initialApplication={selectedJob}
            copyMode={isCopyingJob}
            initialStopId={isCopyingJob ? "" : selectedJob?.focusStopId}
            onBack={() => {
              if (isCopyingJob) {
                handleMenuChange("internalJob");
                return;
              }
              if (selectedJob) {
                setActiveMenu("jobDetail");
                navigateView(INTERNAL_ROUTES.job, "jobDetail", { selectedJob });
                if (contentRef.current) contentRef.current.scrollTop = 0;
                return;
              }
              handleMenuChange("internalJob");
            }}
            onCreated={({ applicationId, dateKey, application, editMode } = {}) => {
              if (isCopyingJob) {
                handleMenuChange("internalJob");
                return;
              }
              if (selectedJob && editMode) {
                const nextApplication = {
                  ...selectedJob,
                  ...(application || {}),
                  id: applicationId || application?.id || selectedJob.id,
                  applicationId: applicationId || application?.applicationId || selectedJob.applicationId,
                  dateKey: dateKey || application?.dateKey || selectedJob.dateKey,
                };
                setSelectedJob(nextApplication);
                setActiveMenu("jobDetail");
                navigateView(INTERNAL_ROUTES.job, "jobDetail", { selectedJob: nextApplication });
                if (contentRef.current) contentRef.current.scrollTop = 0;
                return;
              }
              if (selectedJob) {
                setActiveMenu("jobDetail");
                navigateView(INTERNAL_ROUTES.job, "jobDetail", { selectedJob });
                if (contentRef.current) contentRef.current.scrollTop = 0;
                return;
              }
              handleMenuChange("internalJob");
            }}
          />
        );

      case "createLogistics":
        return (
          <CreateFleetLogisticsJob
            user={sessionUser}
            account={effectiveAccount}
            orgId={effectiveOrgId}
            profileLoading={driverProfileLoading}
            initialApplication={selectedJob}
            copyMode={isCopyingJob}
            initialCargoPlan={cargoWorkPlan?.mode === "multi" ? cargoWorkPlan : null}
            initialStopId={isCopyingJob ? "" : selectedJob?.focusStopId}
            onBack={() => {
              if (isCopyingJob) {
                handleMenuChange("internalJob");
                return;
              }
              if (!selectedJob) {
                if (cargoWorkPlan) {
                  setCargoWorkPlan(null);
                  handleMenuChange("cargoManagement");
                  return;
                }
                handleMenuChange("internalJob");
                return;
              }
              setActiveMenu("logisticsJobDetail");
              navigateView(INTERNAL_ROUTES.job, "logisticsJobDetail", { selectedJob });
            }}
            onCreated={async (result = {}) => {
              await handleCargoPlanCreated(result);
              if (isCopyingJob) {
                handleMenuChange("internalJob");
                return;
              }
              const { applicationId, dateKey, application, editMode } = result;
              if (!selectedJob) {
                handleMenuChange("internalJob");
                return;
              }
              const nextApplication = {
                ...selectedJob,
                ...(editMode && application ? application : {}),
                id: applicationId || application?.id || selectedJob.id,
                applicationId: applicationId || application?.applicationId || selectedJob.applicationId,
                dateKey: dateKey || application?.dateKey || selectedJob.dateKey,
              };
              setSelectedJob(nextApplication);
              setActiveMenu("logisticsJobDetail");
              navigateView(INTERNAL_ROUTES.job, "logisticsJobDetail", { selectedJob: nextApplication });
              if (contentRef.current) contentRef.current.scrollTop = 0;
            }}
          />
        );

      case "quickLogistics":
        return (
          <QuickCreateFleetLogisticsJob
            user={sessionUser}
            account={account}
            initialData={quickLogisticsData}
            onBack={() => handleMenuChange("internalJob")}
            onNext={(data) => {
              setQuickLogisticsData(data);
              setActiveMenu("quickLogisticsVehicle");
              navigateView(INTERNAL_ROUTES.job, "quickLogisticsVehicle", { quickLogisticsData: data });
              if (contentRef.current) contentRef.current.scrollTop = 0;
            }}
          />
        );

      case "quickLogisticsVehicle":
        return quickLogisticsData ? (
          <QuickCreateFleetVehicleStep
            user={sessionUser}
            account={account}
            initialData={quickLogisticsData}
            jobDate={quickLogisticsData.jobDate}
            rows={quickLogisticsData.rows}
            onBack={() => {
              setActiveMenu("quickLogistics");
              navigateView(INTERNAL_ROUTES.job, "quickLogistics", { quickLogisticsData });
              if (contentRef.current) contentRef.current.scrollTop = 0;
            }}
            onNext={(data) => {
              const nextData = { ...quickLogisticsData, ...data };
              setQuickLogisticsData(nextData);
              setActiveMenu("quickLogisticsClustering");
              navigateView(INTERNAL_ROUTES.job, "quickLogisticsClustering", { quickLogisticsData: nextData });
              if (contentRef.current) contentRef.current.scrollTop = 0;
            }}
          />
        ) : (
          <QuickCreateFleetLogisticsJob
            user={sessionUser}
            account={account}
            initialData={quickLogisticsData}
            onBack={() => handleMenuChange("internalJob")}
            onNext={(data) => {
              setQuickLogisticsData(data);
              setActiveMenu("quickLogisticsVehicle");
            }}
          />
        );

      case "quickLogisticsClustering":
        return quickLogisticsData ? (
          <QuickCreateFleetClusteringStep
            user={sessionUser}
            jobDate={quickLogisticsData.jobDate}
            rows={quickLogisticsData.rows}
            selectedAssets={quickLogisticsData.selectedAssets || []}
            onBack={() => {
              setActiveMenu("quickLogisticsVehicle");
              navigateView(INTERNAL_ROUTES.job, "quickLogisticsVehicle", { quickLogisticsData });
              if (contentRef.current) contentRef.current.scrollTop = 0;
            }}
            onNext={(data) => {
              setQuickLogisticsData((current) => ({ ...current, ...data }));
            }}
          />
        ) : (
          <QuickCreateFleetVehicleStep
            user={sessionUser}
            account={account}
            initialData={quickLogisticsData}
            jobDate={quickLogisticsData?.jobDate}
            rows={quickLogisticsData?.rows || []}
            onBack={() => handleMenuChange("internalJob")}
          />
        );

      case "drivers":
        return <DriverManagerDrivers />;

      case "chats":
        return (
          <DriverManagerChatList
            user={sessionUser}
            account={account}
            onOpenChat={handleOpenChat}
          />
        );

      case "chatRoom":
        return (
          <DriverManagerChatRoom
            user={sessionUser}
            orgId={selectedChat?.orgId || account?.orgId}
            chatId={selectedChat?.chatId}
            chatType={selectedChat?.chatType}
            title={selectedChat?.title}
            onBack={() => {
              setSelectedChat(null);
              handleMenuChange("chats");
            }}
          />
        );

      case "commonInfo":
        return (
          <DriverManagerCommonInfo
            user={sessionUser}
            account={account}
          />
        );

      case "fleet":
        return <DriverManagerCars user={sessionUser} account={account} />;

      case "reports":
        return (
          <DriverManagerReports
            user={sessionUser}
            account={account}
            onOpenCargoReport={handleOpenCargoReport}
          />
        );

      case "cargoManagement":
        return (
          <DriverManagerCargoManagement
            user={sessionUser}
            profile={driverProfile}
            profileLoading={driverProfileLoading}
            onCreateWork={handleCargoWorkPlan}
          />
        );

      case "deliveryDetails":
        return isLocalDeliveryDetailsEnabled()
          ? <DriverManagerDeliveryDetails />
          : null;

      case "companyAccounts":
        return <DriverManagerCompanyAccounts user={sessionUser} account={account} />;

      case "settings":
        return (
          <DriverManagerSetting
            onOpenOwnProfile={() => handleMenuChange("drivers")}
          />
        );

      case "overview":
      default:
        return (
          <>
            <LiveFleetMap
              user={sessionUser}
              account={account}
              onOpenJob={handleOpenJob}
              mapExpanded={fleetMapExpanded}
              onMapExpandedChange={setFleetMapExpanded}
            />
          </>
        );
    }
  };

  const isJobList = ["internalJob", "quickLogistics", "quickLogisticsVehicle", "quickLogisticsClustering"].includes(activeMenu);

  return (
    <div
      style={{
        ...styles.page,
      }}
    >
      {createChoiceOpen && (
        <CreateWorkChoiceModal
          onClose={() => setCreateChoiceOpen(false)}
          onSelect={handleCreateWork}
        />
      )}
      <div
        style={{
          ...styles.layout,
          gridTemplateColumns: isMobile
            ? "1fr"
            : sidebarOpen
              ? `${DRIVER_MANAGER_SIDEBAR_EXPANDED_WIDTH}px minmax(0,1fr)`
              : "88px minmax(0,1fr)",
        }}
      >
        {!isMobile && (
          <DriverManagerSidebar
            activeKey={activeMenu}
            collapsed={!sidebarOpen}
            onSelect={handleMenuChange}
            onToggle={() => setSidebarOpen((open) => !open)}
          />
        )}

        <main
          style={{
            ...styles.mainArea,
          }}
        >
          {activeMenu === "overview" && (
            <header
              style={{
                ...styles.topbar,
                ...(fleetMapExpanded ? styles.topbarMapOverlay : {}),
              }}
            >
              <div style={{ ...styles.topbarLeft, ...(fleetMapExpanded ? styles.topbarMapInteractive : {}) }}>
                {isMobile && (
                  <button type="button" style={styles.mobileMenuBtn} aria-label="開啟側邊欄" onClick={() => setSidebarOpen((value) => !value)}>
                    <PanelIcon />
                  </button>
                )}
              </div>
              <div style={{ ...styles.topbarRight, position: "relative", ...(fleetMapExpanded ? styles.topbarRightMapOverlay : {}) }}>
                <button type="button" style={{ ...styles.notifyWrap, border: 0, background: "transparent", cursor: "pointer" }} aria-label="通知" aria-expanded={notificationOpen} onClick={() => { setNotificationOpen((value) => !value) }}>
                  <BellIcon />
                  <span style={styles.notifyDot} />
                </button>
                <div style={styles.avatarWrap} role="img" aria-label={accountDisplayName}>
                  <span style={styles.avatarCircle}>
                    {accountPhotoUrl ? <img src={accountPhotoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%", display: "block" }} /> : accountInitial}
                  </span>
                </div>
                {notificationOpen && <div style={styles.topbarPopover} role="dialog" aria-label="通知面板"><strong>通知</strong><span>目前沒有新的通知</span></div>}

              </div>
            </header>
          )}

          <section
            ref={contentRef}
            className={activeMenu === "overview" ? "dmh-overview-map-viewport" : undefined}
            style={{
              ...styles.contentViewport,
              ...(activeMenu === "cargoManagement"
                ? { overflow: "hidden auto", scrollbarGutter: "stable" }
                : {}),
              ...(activeMenu === "overview"
                ? {
                    position: "absolute",
                    top: fleetMapExpanded ? 0 : 84,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    zIndex: 10,
                    borderTopLeftRadius: fleetMapExpanded ? 0 : 24,
                    transition: "top 380ms cubic-bezier(.2,.75,.25,1), border-top-left-radius 380ms ease",
                  }
                : {}),
            }}
          >
            <div
              style={{
                ...styles.contentInner,
                ...(isJobList && !isMobile
                  ? styles.jobListContentInner
                  : {}),
                ...(activeMenu === "overview" && !isMobile
                  ? styles.overviewContentInner
                  : {}),
              }}
            >
              {renderRightContent()}
            </div>
          </section>
        </main>
      </div>

      {chatPushToast && (
        <div role="status" aria-live="polite" style={styles.chatPushToast}>
          <div style={styles.chatPushToastHeader}>
            <strong style={styles.chatPushToastTitle}>{chatPushToast.title}</strong>
            <button type="button" aria-label="關閉通知" style={styles.chatPushToastClose} onClick={() => setChatPushToast(null)}>×</button>
          </div>
          <div style={styles.chatPushToastBody}>{chatPushToast.body}</div>
          <button
            type="button"
            style={styles.chatPushToastButton}
            onClick={() => {
              openChatFromPush(chatPushToast).then((opened) => {
                if (!opened) setChatPushToast({ title: "無法開啟聊天室", body: "聊天室可能已不存在，或你沒有存取權限。" });
              }).catch((error) => {
                console.warn("[DriverManagerHome] notification chat could not be opened:", error);
                setChatPushToast({ title: "聊天室暫時無法開啟", body: "請稍後再試。" });
              });
            }}
          >
            開啟聊天室
          </button>
        </div>
      )}

      {isMobile && sidebarOpen && (
        <DriverManagerSidebar
          activeKey={activeMenu}
          mobile
          onSelect={(menuKey) => {
            handleMenuChange(menuKey);
            setSidebarOpen(false);
          }}
          onClose={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}

function CreateWorkChoiceModal({ onClose, onSelect }) {
  return (
    <div style={styles.choiceOverlay} onMouseDown={onClose}>
      <div
        style={styles.choiceModal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-work-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div style={styles.choiceModalHeader}>
          <div>
            <div></div>
            <h2 id="create-work-title" style={styles.choiceModalTitle}>
              建立哪一種工作？
            </h2>
          </div>
          <button
            type="button"
            style={styles.choiceCloseButton}
            onClick={onClose}
            aria-label="關閉"
          >
            ×
          </button>
        </div>
        <div style={styles.choiceCards}>
          <button
            type="button"
            style={styles.choiceCard}
            onClick={() => onSelect("inspection")}
          >
            <span
              style={{
                ...styles.choiceIcon,
                background: TOKENS.orangeSoft,
                color: TOKENS.orangeDeep,
              }}
            >
              <img src={jobInspectionIcon} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            </span>
            <span style={styles.choiceCardText}>
              <strong>巡檢工作</strong>
              <small>記錄勤務、現場影像與巡檢事項</small>
            </span>
            <span style={styles.choiceArrow}>›</span>
          </button>
          <button
            type="button"
            style={styles.choiceCard}
            onClick={() => onSelect("general")}
          >
            <span
              style={{
                ...styles.choiceIcon,
                background: TOKENS.orangeSoft,
                color: TOKENS.blue,
              }}
            >
              <img src={jobSingleIcon} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            </span>
            <span style={styles.choiceCardText}>
              <strong>單點工作</strong>
              <small>一個起點與一個終點的日常派工</small>
            </span>
            <span style={styles.choiceArrow}>›</span>
          </button>
          <button
            type="button"
            style={styles.choiceCard}
            onClick={() => onSelect("logistics")}
          >
            <span
              style={{
                ...styles.choiceIcon,
                background: TOKENS.orangeSoft,
                color: "#6651BB",
              }}
            >
              <img src={jobMultiIcon} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            </span>
            <span style={styles.choiceCardText}>
              <strong>多點工作</strong>
              <small>多個配送站的路線工作</small>
            </span>
            <span style={styles.choiceArrow}>›</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function PlaceholderPanel({ title, desc }) {
  return (
    <div style={styles.placeholderCard}>
      <div style={styles.placeholderTag}>Tallarge Web</div>
      <h2 style={styles.placeholderTitle}>{title}</h2>
      <p style={styles.placeholderDesc}>{desc}</p>
    </div>
  );
}

function StatIcon({ type }) {
  switch (type) {
    case "task":
      return (
        <svg viewBox="0 0 24 24" style={styles.statIcon}>
          <path
            d="M8 6h8M9 4h6a1 1 0 0 1 1 1v2H8V5a1 1 0 0 1 1-1Zm-3 4h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "user":
      return (
        <svg viewBox="0 0 24 24" style={styles.statIcon}>
          <path
            d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 8a7 7 0 0 1 14 0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "clock":
      return (
        <svg viewBox="0 0 24 24" style={styles.statIcon}>
          <path
            d="M12 7v5l3 2m6-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "alert":
      return (
        <svg viewBox="0 0 24 24" style={styles.statIcon}>
          <path
            d="M12 4 20 18a1.4 1.4 0 0 1-1.2 2H5.2A1.4 1.4 0 0 1 4 18L12 4Zm0 5v4m0 3h.01"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    default:
      return null;
  }
}

function PanelIcon() {
  return (
    <svg viewBox="0 0 24 24" style={styles.icon}>
      <path
        d="M4 7h16M4 12h16M4 17h16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" style={styles.topIcon}>
      <path
        d="M6.5 9.5a5.5 5.5 0 1 1 11 0v3.1c0 .7.2 1.3.6 1.8l.9 1.3H5l.9-1.3c.4-.5.6-1.1.6-1.8V9.5Zm3.7 9.2a2.5 2.5 0 0 0 3.6 0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg viewBox="0 0 24 24" style={styles.icon}>
      <path
        d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 20h14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const LIVE_FLEET_STYLES = `
.dmh-fleet-shell{display:flex;flex-direction:column;width:100%;height:100%;min-width:0;min-height:0;color:#0f172a;font-family:Inter,"Noto Sans TC",-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
.dmh-fleet-shell *{box-sizing:border-box}
.dmh-fleet-shell button,.dmh-fleet-shell input{font:inherit}
.dmh-fleet-shell button{cursor:pointer}
.dmh-fleet-heading{display:flex;align-items:flex-end;justify-content:space-between;gap:14px;flex:0 0 auto;margin:2px 0 20px}
.dmh-heading-title{flex:0 0 auto}
.dmh-fleet-heading h1{margin:7px 0 0;font-size:31px;line-height:1.1;letter-spacing:-1px;font-weight:900}
.dmh-fleet-heading p{margin:9px 0 0;color:#6b7280;font-size:13px}
.dmh-live-pill{display:flex;align-items:center;gap:8px;padding:9px 13px;border:1px solid #cfeeda;border-radius:999px;background:#f1fcf5;color:#16845b;font-size:12px;font-weight:900;white-space:nowrap}
.dmh-live-pill i{width:8px;height:8px;border-radius:50%;background:#31b66a;box-shadow:0 0 0 4px rgba(49,182,106,.13)}
.dmh-fleet-grid{display:grid;grid-template-columns:minmax(0,1.75fr) minmax(360px,.9fr);flex:1;min-height:0;gap:16px}
.dmh-map-card,.dmh-fleet-panel{min-width:0;border:1px solid rgba(15,23,42,.07);border-radius:24px;background:#fff;box-shadow:0 10px 24px rgba(15,23,42,.06);overflow:hidden}
.dmh-map-card.is-track{box-shadow:0 14px 32px rgba(36,123,209,.09)}
.dmh-map-card{display:flex;flex-direction:column;height:100%;min-height:0}
.dmh-map-card.is-track,.dmh-map-card.is-live-map{position:relative}
.dmh-map-card.is-track .dmh-map-toolbar,.dmh-map-card.is-live-map .dmh-map-toolbar{position:absolute;top:10px;left:56px;right:10px;z-index:700;isolation:isolate;overflow:hidden;border:0;border-radius:20px!important;background:rgba(255,255,255,.1);background-clip:padding-box;box-shadow:none;backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);clip-path:inset(0 round 20px)}
.dmh-map-card.is-track .dmh-map-stage,.dmh-map-card.is-live-map .dmh-map-stage{height:100%;min-height:0}
.dmh-map-toolbar{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:12px 16px;border-bottom:1px solid rgba(15,23,42,.07)}
.dmh-map-title-block{display:grid!important;gap:7px;min-width:0}
.dmh-map-title-row{display:flex;align-items:center;gap:10px;min-width:0}
.dmh-map-toolbar strong{font-size:18px;font-weight:900}
.dmh-map-toolbar span{color:#94a3b8;font-size:11px;font-weight:800}
.dmh-selected-driver-inline{display:flex;align-items:center;gap:6px;color:#247bd1!important;font-size:11px!important;font-weight:900!important}
.dmh-selected-driver-inline img{width:24px;height:24px;border-radius:50%;object-fit:cover;border:2px solid #dbeafe}
.dmh-map-actions{display:flex;align-items:center;justify-content:flex-end;gap:6px;flex-wrap:wrap}
.dmh-map-toolbar button{border:1px solid #e2e8f0;border-radius:8px;padding:7px 10px;background:#fff;color:#64748b;font-size:11px;font-weight:900}
.dmh-map-toolbar button.is-active{border-color:#f68b2c;background:#fff4e9;color:#e9781e}
.dmh-map-toolbar button:disabled{cursor:not-allowed;opacity:.45}
.dmh-map-toolbar input{height:34px;border:1px solid #e2e8f0;border-radius:9px;padding:0 8px;background:#fff;color:#475569;font-size:11px}
.dmh-track-controls{padding:12px 20px 13px;border-bottom:1px solid rgba(15,23,42,.07);background:#fbfcfe}
.dmh-track-control-top{display:flex;align-items:center;gap:9px;min-width:0}
.dmh-track-control-top strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#334155;font-size:12px}
.dmh-playback-clock{margin-left:auto;color:#334155!important;font-size:13px!important;font-weight:900!important;font-variant-numeric:tabular-nums;white-space:nowrap}
.dmh-play-button{min-width:58px;border:0!important;border-radius:9px!important;padding:8px 11px!important;background:#247bd1!important;color:#fff!important;font-size:11px!important;font-weight:900!important}
.dmh-play-button:disabled{background:#cbd5e1!important;color:#fff!important}
.dmh-seek-button{border:1px solid #dbeafe!important;border-radius:9px!important;padding:8px 9px!important;background:#f8fbff!important;color:#247bd1!important;font-size:10px!important;font-weight:900!important}
.dmh-seek-button:disabled{cursor:not-allowed;opacity:.45}
.dmh-speed-select{height:33px;border:1px solid #e2e8f0;border-radius:9px;padding:0 8px;background:#fff;color:#475569;font-size:11px;font-weight:800}
.dmh-track-range{display:block;width:100%;height:5px;margin:12px 0 6px;accent-color:#247bd1;cursor:pointer}
.dmh-track-time-row{display:flex;justify-content:space-between;color:#94a3b8;font-size:10px;font-variant-numeric:tabular-nums}
.dmh-track-timeline{position:absolute;left:16px;right:16px;bottom:14px;z-index:500;padding:7px 13px 6px;border:1px solid rgba(255,255,255,.94);border-radius:14px;background:rgba(255,255,255,.96);box-shadow:0 7px 22px rgba(15,23,42,.16);backdrop-filter:blur(10px)}
.dmh-track-timeline-top{display:grid;grid-template-columns:minmax(0,1fr) auto minmax(0,1fr);align-items:center;gap:8px;min-height:27px}
.dmh-track-timeline-date-control{display:flex;align-items:center;justify-self:start;gap:2px;min-width:0}
.dmh-track-timeline-date-step{display:grid;place-items:center;width:22px;height:24px;flex:0 0 22px;padding:0;border:0;border-radius:7px;background:transparent;color:#64748b;font-size:20px;font-weight:650;line-height:1;cursor:pointer;transition:background .15s,color .15s}
.dmh-track-timeline-date-step:hover:not(:disabled){background:#f1f3f6;color:#202939}
.dmh-track-timeline-date-step:disabled{cursor:not-allowed;opacity:.32}
.dmh-track-timeline-date{position:relative;display:flex;align-items:center;justify-self:start;gap:7px;min-width:0;color:#344054;font-size:12px;font-weight:650;white-space:nowrap;cursor:pointer}
.dmh-track-timeline-date svg{width:17px;height:17px;flex:0 0 17px;color:#344054}
.dmh-track-timeline-date input{position:absolute;inset:0;width:100%;height:100%;opacity:0;cursor:pointer}
.dmh-track-timeline-clock{justify-self:center;color:#202939;font-size:18px;font-weight:850;font-variant-numeric:tabular-nums;letter-spacing:.01em;white-space:nowrap}
.dmh-track-timeline-top-actions{display:flex;align-items:center;justify-self:end;gap:4px}
.dmh-track-timeline-follow{display:grid;place-items:center;width:28px;height:25px;padding:0;border:1px solid #dbe3ee;border-radius:8px;background:#fff;color:#475569;cursor:pointer;transition:background .16s,border-color .16s,color .16s}
.dmh-track-timeline-follow svg{width:14px;height:14px;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}
.dmh-track-timeline-follow.is-active{border-color:#f58224;background:#fff4e9;color:#dc6a12}
.dmh-track-timeline-follow:disabled{cursor:not-allowed;opacity:.42}
.dmh-track-timeline-more{display:grid;place-items:center;justify-self:end;width:28px;height:25px;padding:0;border:0;border-radius:7px;background:transparent;color:#252b36;font-size:20px;font-weight:900;line-height:1;cursor:pointer}
.dmh-track-timeline-more:hover,.dmh-track-timeline-more[aria-expanded="true"]{background:#f1f3f6}
.dmh-track-timeline-player-row{display:grid;grid-template-columns:auto minmax(90px,1fr) auto;align-items:center;gap:10px;margin-top:2px}
.dmh-track-timeline-transport{display:flex;align-items:center;gap:6px}
.dmh-track-timeline-seek{display:flex;flex-direction:column;align-items:center;justify-content:center;width:34px;height:35px;padding:0;border:0;background:transparent;color:#344054;cursor:pointer}
.dmh-track-timeline-seek span{font-size:19px;font-weight:650;line-height:18px}
.dmh-track-timeline-seek small{font-size:9px;font-weight:750;line-height:10px}
.dmh-track-timeline-play{display:grid;place-items:center;width:40px;height:40px;flex:0 0 40px;padding:0;border:0;border-radius:50%;background:#f58224;color:#fff;cursor:pointer;box-shadow:0 2px 5px rgba(245,130,36,.2)}
.dmh-track-timeline-play svg{width:18px;height:18px}
.dmh-track-timeline-slider{min-width:0;display:flex;flex-direction:column;justify-content:center;gap:1px}
.dmh-track-timeline .dmh-track-range{display:block;width:100%;height:5px;margin:0;box-sizing:border-box;accent-color:#f58224;cursor:pointer}
.dmh-track-timeline-time-labels{display:flex;justify-content:space-between;gap:8px;color:#7b8492;font-size:10px;font-variant-numeric:tabular-nums;line-height:12px}
.dmh-track-timeline-speed{width:64px;height:33px;padding:0 8px;border:0;border-radius:18px;background:#f1f3f6;color:#202939;font-size:12px;font-weight:750;cursor:pointer}
.dmh-track-timeline-player-row button:disabled{cursor:not-allowed;opacity:.45}
.dmh-track-timeline.is-range-menu-open{z-index:1300}
@media(max-width:560px){.dmh-track-timeline{left:10px;right:10px;bottom:10px;padding:7px 10px 6px;border-radius:14px}.dmh-track-timeline-top{gap:5px;min-height:25px}.dmh-track-timeline-date{gap:6px;font-size:12px}.dmh-track-timeline-date svg{width:16px;height:16px;flex-basis:16px}.dmh-track-timeline-date-step{width:20px;height:23px;flex-basis:20px}.dmh-track-timeline-date-control{gap:1px}.dmh-track-timeline-clock{font-size:17px}.dmh-track-timeline-follow{width:25px;height:24px}.dmh-track-timeline-follow svg{width:13px;height:13px}.dmh-track-timeline-top-actions{gap:3px}.dmh-track-timeline-player-row{grid-template-columns:auto minmax(54px,1fr) auto;gap:6px;margin-top:2px}.dmh-track-timeline-transport{gap:4px}.dmh-track-timeline-seek{width:28px;height:34px}.dmh-track-timeline-seek span{font-size:18px}.dmh-track-timeline-play{width:36px;height:36px;flex-basis:36px}.dmh-track-timeline-play svg{width:17px;height:17px}.dmh-track-timeline-speed{width:58px;height:31px;padding:0 6px;font-size:12px}.dmh-track-timeline-time-labels{font-size:9px;line-height:11px}.dmh-track-range-popover-fields{gap:7px}.dmh-track-time-control{gap:3px;padding:4px}.dmh-track-time-control select{height:30px;padding:0 3px;font-size:13px}.dmh-track-time-control small{font-size:9px}}
@media(max-width:380px){.dmh-track-timeline{left:7px;right:7px;padding:6px 8px 5px}.dmh-track-timeline-player-row{grid-template-columns:auto minmax(42px,1fr) auto;gap:4px}.dmh-track-timeline-transport{gap:2px}.dmh-track-timeline-seek{width:26px;height:32px}.dmh-track-timeline-play{width:34px;height:34px;flex-basis:34px}.dmh-track-timeline-speed{width:54px;padding:0 4px}}
.dmh-track-range-popover{position:absolute;right:0;bottom:calc(100% + 9px);z-index:1301;width:min(340px,calc(100vw - 32px));padding:14px;border:1px solid #dbe3ee;border-radius:14px;background:#fff;box-shadow:0 18px 42px rgba(15,23,42,.24);color:#1e293b}
.dmh-track-range-popover-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}
.dmh-track-range-popover-head>div{display:grid;gap:4px;min-width:0}
.dmh-track-range-popover-head strong{color:#1e293b;font-size:13px;font-weight:950}
.dmh-track-range-popover-head span{color:#64748b;font-size:10px;line-height:1.45}
.dmh-track-range-popover-close{display:grid;place-items:center;width:26px;height:26px;flex:0 0 26px;padding:0;border:0;border-radius:8px;background:#f1f5f9;color:#64748b;font-size:19px;line-height:1}
.dmh-track-range-popover-fields{display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-top:13px}
.dmh-track-range-popover-fields label{display:grid;gap:5px;min-width:0;color:#64748b;font-size:10px;font-weight:850}
.dmh-track-time-control{display:flex;align-items:center;gap:5px;min-width:0;padding:5px 7px;border:1px solid #dbe3ee;border-radius:10px;background:#f8fafc}
.dmh-track-time-control select{min-width:0;height:32px;padding:0 5px;border:1px solid #e2e8f0;border-radius:7px;background:#fff;color:#1e293b;font-size:14px;font-weight:750;font-variant-numeric:tabular-nums}
.dmh-track-time-control select:first-child{flex:1}
.dmh-track-time-control select:nth-of-type(2){flex:1}
.dmh-track-time-control small{color:#64748b;font-size:10px;font-weight:750}
.dmh-track-time-control b{color:#64748b;font-size:13px}
.dmh-track-time-control select:disabled{opacity:.55}
.dmh-track-range-popover-actions{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:12px}
.dmh-track-range-popover-actions>small{color:#64748b;font-size:10px;line-height:1.4}
.dmh-track-range-popover-buttons{display:flex;align-items:center;gap:7px;flex:0 0 auto;margin-left:auto}
.dmh-track-range-popover-buttons button{padding:8px 10px;border:0;border-radius:9px;background:#0f5ea8;color:#fff;font-size:10px;font-weight:900;white-space:nowrap}
.dmh-track-range-popover-buttons button.is-secondary{border:1px solid #dbe3ee;background:#fff;color:#475569}
.dmh-track-range-popover-buttons button:disabled{opacity:.5;cursor:not-allowed}
.dmh-track-range-popover small.is-error{display:block;margin-top:9px;color:#b91c1c;font-size:10px;font-weight:800;line-height:1.45}.dmh-track-legend{bottom:78px}
.dmh-map-stage{position:relative;flex:1;min-height:0;background:#dceff4}
.dmh-leaflet-map{position:absolute;inset:0;width:100%;height:100%;z-index:1}
.dmh-map-card .leaflet-top.leaflet-left{top:10px;left:10px;z-index:800}.dmh-map-card .leaflet-control-zoom{margin:0;border:0;border-radius:11px;overflow:hidden;background:rgba(255,255,255,.78);box-shadow:0 6px 16px rgba(15,23,42,.14);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px)}.dmh-map-card .leaflet-control-zoom a{width:32px;height:30px;border:0!important;border-bottom:1px solid rgba(148,163,184,.2)!important;background:rgba(255,255,255,.58);color:#334155;font-size:17px;font-weight:900;line-height:30px;transition:background .16s,color .16s}.dmh-map-card .leaflet-control-zoom a:last-child{border-bottom:0!important}.dmh-map-card .leaflet-control-zoom a:hover{background:rgba(255,255,255,.9);color:#e9781e}
.dmh-map-empty,.dmh-map-error{position:absolute;left:50%;z-index:500;transform:translateX(-50%);padding:10px 14px;border-radius:10px;background:rgba(255,255,255,.94);box-shadow:0 5px 18px rgba(15,23,42,.12);font-size:12px;font-weight:800;white-space:nowrap}
.dmh-map-empty{top:50%;color:#64748b}
.dmh-map-error{bottom:15px;color:#b42318;border:1px solid #fecaca;background:#fff5f5}
.dmh-map-legend{position:absolute;left:18px;bottom:18px;z-index:500;display:flex;gap:13px;padding:9px 12px;border:1px solid rgba(255,255,255,.85);border-radius:10px;background:rgba(255,255,255,.9);box-shadow:0 5px 18px rgba(15,23,42,.12);color:#64748b;font-size:10px;font-weight:800}
.dmh-map-legend span{display:flex;align-items:center;gap:5px}
.dmh-map-legend i{display:inline-block;width:19px;height:4px;border-radius:4px;background:#247bd1}
.dmh-map-legend i.is-full-track{background:#247bd1;opacity:.82}
.dmh-map-legend i.is-played{height:6px;background:#0f5ea8}
.dmh-map-legend i.is-stop{width:9px;height:9px;border-radius:50%;background:#f68b2c}
.dmh-map-legend i.is-rest{width:9px;height:9px;border-radius:50%;background:#8b5cf6}
.dmh-track-endpoint-shell{border:0!important;background:transparent!important}
.dmh-track-endpoint{display:grid;place-items:center;width:38px;height:38px;border:3px solid #fff;border-radius:50%;box-shadow:0 5px 13px rgba(15,23,42,.25);color:#fff;font-size:12px;font-weight:950}
.dmh-track-endpoint.is-start{background:#18b977}
.dmh-track-endpoint.is-end{background:#ef5360}
.dmh-popup{display:grid;gap:5px;min-width:150px;color:#334155;font-family:Inter,"Noto Sans TC",sans-serif}
.dmh-popup strong{font-size:13px;color:#0f172a}
.dmh-popup span{font-size:11px;color:#64748b}
.dmh-popup b{font-size:11px;color:#475569}
.dmh-fleet-panel{display:flex;flex-direction:column;height:100%;min-height:0;padding:18px}
.dmh-fleet-panel.is-track-panel{padding:20px;overflow-x:hidden;overflow-y:auto;overscroll-behavior:contain;scrollbar-width:thin;scrollbar-color:#cbd5e1 transparent}
.dmh-fleet-panel.is-track-panel::-webkit-scrollbar{width:7px}
.dmh-fleet-panel.is-track-panel::-webkit-scrollbar-track{background:transparent}
.dmh-fleet-panel.is-track-panel::-webkit-scrollbar-thumb{border:2px solid transparent;border-radius:999px;background:#cbd5e1;background-clip:padding-box}
.dmh-driver-detail-head{display:grid;gap:14px}
.dmh-driver-detail-back{justify-self:start;border:0;background:transparent;color:#e9781e;font-size:11px;font-weight:900;padding:0}
.dmh-driver-detail-profile{display:flex;align-items:center;gap:12px;padding:2px 0 17px;border-bottom:1px solid #eef2f7}
.dmh-driver-detail-avatar{position:relative;display:grid;place-items:center;width:58px;height:58px;flex:0 0 58px;border-radius:50%;background:#eaf2ff;color:#247bd1;box-shadow:0 4px 12px rgba(15,23,42,.12);font-size:21px;font-weight:950;overflow:visible}
.dmh-driver-detail-avatar img{display:block;width:100%;height:100%;border-radius:50%;object-fit:cover}
.dmh-driver-detail-avatar i{position:absolute;right:-1px;bottom:1px;width:13px;height:13px;border:3px solid #fff;border-radius:50%}
.dmh-driver-detail-profile>div{display:grid;gap:6px;min-width:0}.dmh-driver-detail-profile>div>span{color:#64748b;font-size:11px;font-weight:800}
.dmh-driver-detail-name-row{display:flex;align-items:center;gap:7px;min-width:0}.dmh-driver-detail-name-row strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#1e293b;font-size:19px;font-weight:950}.dmh-driver-detail-name-row em{padding:4px 7px;border-radius:999px;font-size:9px;font-style:normal;font-weight:900;white-space:nowrap}
.dmh-vehicle-detail-profile{display:flex;align-items:center;gap:12px;padding:2px 0 17px;border-bottom:1px solid #eef2f7}.dmh-vehicle-detail-profile>div{display:grid;gap:6px;min-width:0}.dmh-vehicle-detail-profile>div>span{color:#64748b;font-size:11px;font-weight:800}.dmh-vehicle-detail-avatar{display:grid;place-items:center;width:58px;height:58px;flex:0 0 58px;border-radius:14px;background:#f1f5f9;color:#64748b;box-shadow:0 4px 12px rgba(15,23,42,.12);font-size:22px;font-weight:950;overflow:hidden}.dmh-vehicle-detail-avatar img{display:block;width:100%;height:100%;object-fit:cover}.dmh-vehicle-detail-facts{margin-top:17px}.dmh-vehicle-detail-facts strong{max-width:62%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dmh-driver-detail-empty{padding:15px;border-radius:12px;background:#f8fafc;color:#475569;font-size:12px;font-weight:900}
.dmh-driver-detail-facts{display:grid;border-top:1px solid #eef2f7}.dmh-driver-detail-facts>div{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:15px 0;border-bottom:1px solid #eef2f7}.dmh-driver-detail-facts span{color:#94a3b8;font-size:11px;font-weight:800}.dmh-driver-detail-facts strong{color:#334155;font-size:12px;font-weight:950;text-align:right}
.dmh-track-inspector-head{display:flex;align-items:center;gap:12px;padding:2px 0 17px}
.dmh-inspector-avatar{position:relative;display:grid;place-items:center;width:64px;height:64px;flex:0 0 64px;border-radius:50%;background:#eaf2ff;color:#247bd1;font-size:22px;font-weight:900}
.dmh-inspector-avatar img{width:64px;height:64px;border-radius:50%;object-fit:cover}
.dmh-inspector-avatar i{position:absolute;right:1px;bottom:2px;width:13px;height:13px;border:3px solid #fff;border-radius:50%}
.dmh-inspector-copy{display:grid;gap:5px;min-width:0}
.dmh-inspector-name-row{display:flex;align-items:center;gap:7px;min-width:0}
.dmh-inspector-name-row strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#0f172a;font-size:18px;font-weight:950}
.dmh-inspector-name-row em{padding:4px 7px;border-radius:999px;background:#e8f8ee;color:#16845b;font-size:9px;font-style:normal;font-weight:900;white-space:nowrap}
.dmh-inspector-copy>span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#475569;font-size:11px;font-weight:800}
.dmh-inspector-copy small{color:#94a3b8;font-size:10px}
.dmh-track-no-driver{padding:16px;border-radius:14px;background:#f8fafc;color:#64748b;font-size:12px;font-weight:800}
.dmh-route-summary{display:flex;align-items:center;gap:10px;margin-bottom:14px;padding:13px;border:1px solid #dbeafe;border-radius:14px;background:#f5faff}
.dmh-route-summary-icon{display:grid;place-items:center;width:30px;height:30px;border-radius:9px;background:#dbeafe;color:#247bd1;font-size:20px;font-weight:900}
.dmh-route-summary>div{display:grid;gap:4px;min-width:0;flex:1}
.dmh-route-summary strong{color:#24527f;font-size:12px;font-weight:900}
.dmh-route-summary span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#64748b;font-size:10px}
.dmh-route-summary button{border:0;background:transparent;color:#94a3b8;font-size:24px;line-height:1;cursor:pointer}
.dmh-route-summary-clickable{cursor:pointer;transition:border-color .16s,background .16s,box-shadow .16s}.dmh-route-summary-clickable:hover{border-color:#a9cff7;background:#eef7ff;box-shadow:0 5px 14px rgba(36,123,209,.08)}.dmh-route-summary-arrow{color:#94a3b8!important;font-size:24px!important;line-height:1}
.dmh-track-stats{display:grid;grid-template-columns:repeat(3,1fr);margin-bottom:20px;border:1px solid #eef2f7;border-radius:14px;background:#fff}
.dmh-track-stats>div{display:grid;justify-items:center;gap:5px;padding:13px 6px;border-right:1px solid #eef2f7}
.dmh-track-stats>div:last-child{border-right:0}
.dmh-track-stats strong{color:#1e293b;font-size:15px;font-weight:950;white-space:nowrap}
.dmh-track-stats span{color:#94a3b8;font-size:9px;font-weight:800;white-space:nowrap}
.dmh-track-stats-rows{grid-template-columns:1fr;margin-top:17px;background:#fff}.dmh-track-stats-rows>div{position:relative;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:15px 14px;border-right:0;border-bottom:1px solid #eef2f7}.dmh-track-stats-rows>div:last-child{border-bottom:0}.dmh-track-stats-rows span{font-size:11px}.dmh-track-stats-rows strong{font-size:13px}.dmh-track-stats-rows small{margin-left:auto;color:#94a3b8;font-size:10px;font-weight:800;white-space:nowrap}
.dmh-track-stop-marker-host,.dmh-track-rest-marker-host{background:transparent;border:0}.dmh-track-stop-marker,.dmh-track-rest-marker{display:grid;place-items:center;width:30px;height:30px;border:3px solid #fff;border-radius:50%;color:#fff;box-shadow:0 3px 12px rgba(15,23,42,.24);font-size:11px;font-weight:950}.dmh-track-stop-marker{background:#f68b2c;box-shadow:0 3px 12px rgba(246,139,44,.42)}.dmh-track-rest-marker{background:#8b5cf6;box-shadow:0 3px 12px rgba(139,92,246,.38)}.dmh-track-stop-marker.is-selected,.dmh-track-rest-marker.is-selected{width:36px;height:36px;border-width:4px;box-shadow:0 0 0 5px rgba(36,123,209,.2),0 5px 16px rgba(15,23,42,.3)}.dmh-track-stop-popup{min-width:140px}.dmh-track-stop-popup small{color:#94a3b8;font-size:10px}.dmh-track-stop-popup b{color:#247bd1;font-size:10px}
.dmh-track-stop-marker-host{overflow:visible!important;background:transparent!important;border:0!important}
.dmh-track-stop-marker{position:relative;display:flex!important;align-items:center!important;justify-content:flex-start!important;place-items:initial!important;width:max-content!important;height:54px!important;overflow:visible!important;border:0!important;border-radius:0!important;background:transparent!important;box-shadow:none!important;color:#fff!important;font-size:inherit!important}
.dmh-track-stop-marker.is-selected{width:max-content!important;height:54px!important;border:0!important;box-shadow:none!important}
.dmh-track-stop-ring{position:absolute;left:-4px;top:3px;z-index:0;width:48px;height:48px;box-sizing:border-box;border:1.5px solid rgba(242,138,50,.68);border-radius:50%;background:rgba(242,138,50,.035);box-shadow:0 0 0 5px rgba(242,138,50,.11)}
.dmh-track-stop-core{position:relative;z-index:2;display:grid;place-items:center;flex:0 0 38px;width:38px;height:38px;box-sizing:border-box;border:3px solid #fff;border-radius:50%;background:#f28a32;color:#fff;box-shadow:0 3px 12px rgba(216,117,32,.32);font-size:11px;font-weight:900;line-height:1}
.dmh-track-stop-duration{position:relative;z-index:1;display:flex;align-items:center;align-self:center;height:30px!important;box-sizing:border-box;margin-left:-8px;padding:0 10px 0 16px;border-radius:0 999px 999px 0;background:rgba(255,255,255,.97);color:#253447;box-shadow:0 3px 12px rgba(15,23,42,.14);font-size:11px;line-height:1;font-weight:750;white-space:nowrap;letter-spacing:-.15px}
.dmh-track-stop-marker.is-selected .dmh-track-stop-ring{border-width:2px;border-color:rgba(232,126,39,.88);box-shadow:0 0 0 6px rgba(242,138,50,.16),0 2px 8px rgba(216,117,32,.12)}
.dmh-track-stop-marker.is-selected .dmh-track-stop-duration{box-shadow:0 4px 14px rgba(15,23,42,.2)}
.dmh-track-stop-marker-host,.dmh-track-rest-marker-host,.dmh-track-work-marker-host{overflow:visible!important;background:transparent!important;border:0!important}
.dmh-track-stop-marker,.dmh-track-rest-marker{position:relative;display:flex!important;align-items:center!important;justify-content:flex-start!important;place-items:initial!important;width:max-content!important;height:44px!important;overflow:visible!important;border:0!important;border-radius:0!important;background:transparent!important;box-shadow:none!important;color:#fff!important}
.dmh-track-stop-marker.is-selected,.dmh-track-rest-marker.is-selected{width:max-content!important;height:44px!important;border:0!important;box-shadow:none!important}
.dmh-track-stop-ring,.dmh-track-rest-ring{position:absolute;left:-3px;top:4px;z-index:0;width:36px;height:36px;box-sizing:border-box;border:1px solid rgba(204,132,77,.5);border-radius:50%;background:rgba(240,175,120,.025);box-shadow:0 0 0 3px rgba(204,132,77,.07)}
.dmh-track-stop-core,.dmh-track-rest-core{position:relative;z-index:2;display:grid;place-items:center;flex:0 0 28px;width:28px;height:28px;box-sizing:border-box;border:2px solid rgba(255,255,255,.96);border-radius:50%;color:#fff;font-size:9px;font-weight:850;line-height:1}
.dmh-track-stop-core{background:#d9955c;box-shadow:0 2px 6px rgba(153,100,55,.2)}
.dmh-track-rest-ring{border-color:rgba(139,112,181,.52);background:rgba(139,92,246,.02);box-shadow:0 0 0 3px rgba(139,92,246,.065)}
.dmh-track-rest-core{background:#917ab8;box-shadow:0 2px 6px rgba(92,67,137,.2)}
.dmh-track-stop-duration,.dmh-track-rest-duration{position:relative;z-index:1;display:flex;align-items:center;align-self:center;height:23px!important;box-sizing:border-box;margin-left:-6px;padding:0 7px 0 12px;border-radius:0 999px 999px 0;background:rgba(255,255,255,.94);color:#647184;box-shadow:0 2px 6px rgba(15,23,42,.09);font-size:9px;line-height:1;font-weight:700;white-space:nowrap}
.dmh-track-stop-marker.is-selected .dmh-track-stop-ring,.dmh-track-rest-marker.is-selected .dmh-track-rest-ring{border-color:rgba(139,111,166,.7);box-shadow:0 0 0 4px rgba(139,92,246,.1)}
.dmh-track-work-marker-host{overflow:visible!important;background:transparent!important;border:0!important}
.dmh-track-work-marker{position:relative;display:block!important;width:max-content!important;height:36px!important;min-width:0!important;box-sizing:border-box!important;overflow:visible!important;color:#334155}
.dmh-track-work-summary{display:flex;align-items:center;justify-content:center;gap:6px;width:var(--dmh-work-label-width);min-height:30px;box-sizing:border-box;padding:3px 6px;border:1px solid rgba(50,104,137,.18);border-radius:999px;background:linear-gradient(180deg,rgba(255,255,255,.99),rgba(248,250,252,.96));box-shadow:0 2px 4px rgba(15,23,42,.08),0 7px 16px rgba(15,23,42,.16);backdrop-filter:blur(4px);white-space:nowrap}
.dmh-track-work-group{display:flex;align-items:center;gap:3px}
.dmh-track-work-kind{color:#64748b;font-size:9px;font-weight:900;line-height:1}
.dmh-track-work-number{display:grid;place-items:center;width:19px;height:19px;flex:0 0 19px;box-sizing:border-box;border:1.5px solid #fff;border-radius:50%;background:var(--dmh-work-number-color);color:#fff;box-shadow:0 0 0 1px var(--dmh-work-number-ring),0 1px 3px rgba(15,23,42,.16);font-size:8px;font-weight:950;line-height:1}
.dmh-track-work-divider{color:#b0bac6;font-size:10px;font-style:normal;font-weight:800}
.dmh-track-work-marker.is-selected .dmh-track-work-summary{border-color:rgba(50,132,167,.5);box-shadow:0 0 0 3px rgba(50,132,167,.1),0 4px 8px rgba(15,23,42,.12),0 10px 18px rgba(15,23,42,.17)}
.dmh-track-work-popup{min-width:160px;max-width:240px}
.dmh-track-work-popup-item{display:grid;gap:2px;padding-top:5px;border-top:1px solid rgba(15,23,42,.08)}
.dmh-track-work-popup-item span{font-size:11px;color:#64748b}
.dmh-track-aux-marker-host{overflow:visible!important;background:transparent!important;border:0!important}
.dmh-track-aux-marker{position:relative;display:block;width:100%;height:46px;overflow:visible;color:#647184}
.dmh-track-aux-pin{position:absolute;z-index:2;width:28px;height:28px;transform:translate(0,0)}
.dmh-track-aux-ring{position:absolute;left:-4px;top:-4px;width:36px;height:36px;box-sizing:border-box;border:1px solid rgba(170,137,111,.48);border-radius:50%;background:rgba(255,255,255,.18);box-shadow:0 0 0 3px rgba(170,137,111,.06)}
.dmh-track-aux-core{position:relative;z-index:2;display:grid;place-items:center;width:28px;height:28px;box-sizing:border-box;border:2px solid rgba(255,255,255,.96);border-radius:50%;background:#b58a67;color:#fff;box-shadow:0 2px 6px rgba(100,75,54,.2);font-size:8px;font-weight:900;white-space:nowrap}
.dmh-track-aux-label{position:absolute;z-index:1;display:flex;align-items:center;min-height:23px;box-sizing:border-box;padding:4px 8px 4px 12px;border:1px solid rgba(171,148,127,.14);border-radius:999px;background:rgba(255,255,255,.96);color:#697586;box-shadow:0 2px 7px rgba(15,23,42,.1);font-size:9px;line-height:1.15;font-weight:750;white-space:nowrap}
.dmh-track-aux-leader{position:absolute;z-index:0;height:1.5px;background:rgba(171,148,127,.6);transform-origin:0 50%;pointer-events:none}
.dmh-track-aux-marker.is-selected .dmh-track-aux-ring{border-color:rgba(36,123,209,.55);box-shadow:0 0 0 4px rgba(36,123,209,.1)}
.dmh-track-aux-marker.is-selected .dmh-track-aux-label{border-color:rgba(36,123,209,.25);box-shadow:0 3px 9px rgba(15,23,42,.16)}
.dmh-track-stop-popup>div{display:grid;gap:2px;margin-top:5px}
.dmh-track-route-flow{stroke-dasharray:1 22!important;stroke-dashoffset:0;stroke-linecap:round;filter:drop-shadow(0 0 3px rgba(255,255,255,.9));-webkit-animation:dmh-track-route-flow 1.35s linear infinite;animation:dmh-track-route-flow 1.35s linear infinite;pointer-events:none}
@-webkit-keyframes dmh-track-route-flow{to{stroke-dashoffset:-46}}
@keyframes dmh-track-route-flow{to{stroke-dashoffset:-46}}
.dmh-track-section-heading{display:flex;align-items:center;justify-content:space-between;gap:12px;margin:4px 0 9px}.dmh-track-section-heading>div{display:grid;gap:3px}.dmh-track-section-heading strong{color:#1e293b;font-size:16px;font-weight:950}.dmh-track-section-heading span{color:#94a3b8;font-size:10px;font-weight:800}.dmh-track-section-heading>b{display:grid;place-items:center;min-width:27px;height:25px;border-radius:9px;background:#fff4e9;color:#e9781e;font-size:12px;font-weight:950}.dmh-history-section{margin-top:20px}.dmh-history-record-list{display:grid;gap:7px}.dmh-history-record{display:flex;align-items:center;gap:9px;padding:10px;border:1px solid #eef2f7;border-radius:12px;background:#fff}.dmh-history-record-icon{display:grid;place-items:center;width:30px;height:30px;flex:0 0 30px;border-radius:10px;font-size:11px;font-weight:950}.dmh-history-record-icon.is-stop{background:#fff4e9;color:#d97706}.dmh-history-record-icon.is-rest{background:#fff4e9;color:#d97706}.dmh-history-record-icon.is-status{background:#eaf2ff;color:#247bd1;font-size:9px}.dmh-history-record>div{display:grid;gap:3px;min-width:0;flex:1}.dmh-history-record strong{color:#334155;font-size:12px;font-weight:950}.dmh-history-record span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#94a3b8;font-size:10px}.dmh-history-record em{padding:4px 6px;border-radius:999px;background:#f1f5f9;color:#64748b;font-size:9px;font-style:normal;font-weight:900;white-space:nowrap}.dmh-history-record-empty{padding:13px;border:1px dashed #dbe3ec;border-radius:12px;background:#fafbfc;color:#94a3b8;font-size:11px;font-weight:800;text-align:center}
.dmh-inspector-list-head{display:flex;align-items:baseline;justify-content:space-between;gap:8px;margin-bottom:8px}
.dmh-inspector-list-head strong{color:#1e293b;font-size:16px;font-weight:950}
.dmh-inspector-list-head span{color:#94a3b8;font-size:10px;font-weight:800}
.dmh-inspector-search{margin-top:0}
.dmh-track-driver-list{max-height:none}
.dmh-panel-title-row{display:flex;align-items:center;justify-content:space-between;gap:12px}
.dmh-panel-title-row>div{display:flex;align-items:baseline;gap:9px;min-width:0}
.dmh-panel-title-row strong{font-size:20px;font-weight:900}
.dmh-panel-title-row span:not(.dmh-panel-total){color:#8793a1;font-size:13px;font-weight:800}
.dmh-panel-total{display:grid;place-items:center;min-width:40px;height:34px;border-radius:11px;background:#fff4e9;color:#e9781e;font-size:15px;font-weight:900}
.dmh-search-box{display:flex;align-items:center;gap:9px;height:50px;margin-top:15px;padding:0 13px;border:1px solid #e2e8f0;border-radius:12px;background:#fafbfc;color:#94a3b8}
.dmh-search-box>span{font-size:22px;line-height:1}
.dmh-search-box input{flex:1;min-width:0;border:0;outline:0;background:transparent;color:#334155;font-size:14px}
.dmh-search-box button{border:0;background:transparent;color:#94a3b8;font-size:20px;line-height:1}
.dmh-filter-row{display:flex;gap:8px;overflow:auto;padding:14px 0 5px}
.dmh-filter-row button{display:flex;align-items:center;gap:6px;height:38px;flex:0 0 auto;border:1px solid #e2e8f0;border-radius:10px;padding:0 11px;background:#fff;color:#64748b;font-size:12px;font-weight:900;white-space:nowrap}
.dmh-filter-row button b{display:grid;place-items:center;min-width:21px;height:21px;border-radius:11px;background:#f1f5f9;color:#8793a1;font-size:11px}
.dmh-filter-row button.is-active{border-color:#f68b2c;background:#f68b2c;color:#fff}
.dmh-filter-row button.is-active b{background:rgba(255,255,255,.22);color:#fff}
.dmh-driver-list{display:grid;grid-auto-rows:max-content;align-content:start;flex:1;gap:9px;min-height:0;overflow-y:auto;padding:9px 1px 3px;overscroll-behavior:contain}
.dmh-driver-row{display:flex;align-items:center;gap:12px;width:100%;min-width:0;min-height:70px;border:1px solid transparent;border-radius:14px;padding:11px 12px;background:#fff;color:#334155;text-align:left;transition:background .16s,border-color .16s,box-shadow .16s}
.dmh-driver-row:hover,.dmh-driver-row.is-selected{border-color:#ffd7b7;background:#fffaf5;box-shadow:0 5px 14px rgba(246,139,44,.08)}
.dmh-avatar{position:relative;display:grid;place-items:center;width:46px;height:46px;flex:0 0 46px;overflow:visible;border-radius:13px;font-size:17px;font-weight:900}
.dmh-avatar img{width:46px;height:46px;display:block;object-fit:cover;border-radius:13px}
.dmh-avatar i{position:absolute;right:-2px;bottom:-2px;width:11px;height:11px;border:2px solid #fff;border-radius:50%}
.dmh-driver-copy{display:grid;gap:4px;min-width:0;flex:1}
.dmh-driver-name-row{display:flex;align-items:center;gap:7px;min-width:0}
.dmh-driver-name-row strong{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:15px;font-weight:900;color:#1e293b}
.dmh-driver-name-row em{padding:4px 7px;border-radius:7px;font-size:10px;font-style:normal;font-weight:900;white-space:nowrap}
.dmh-driver-work,.dmh-driver-meta,.dmh-driver-updated{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dmh-driver-vehicle-row{display:flex;align-items:center;gap:8px;min-width:0}
.dmh-driver-vehicle-plate{display:inline-flex;align-items:center;justify-content:center;max-width:120px;overflow:hidden;padding:4px 8px;border:1px solid rgba(71,85,105,.22);border-radius:8px;background:linear-gradient(180deg,#fff 0%,#f5f7f8 100%);box-shadow:inset 0 1px 0 rgba(255,255,255,.9),0 1px 2px rgba(15,23,42,.04);color:#475569;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11px;font-weight:900;letter-spacing:.045em;text-overflow:ellipsis;white-space:nowrap}
.dmh-driver-row.is-selected .dmh-driver-vehicle-plate{border-color:rgba(246,139,44,.34);background:#fffaf5}
@media(max-width:560px){.dmh-driver-row{gap:10px;padding:10px}.dmh-avatar{width:43px;height:43px;flex-basis:43px}.dmh-avatar img{width:43px;height:43px}.dmh-driver-name-row strong{font-size:14px}.dmh-driver-vehicle-plate{max-width:100px;font-size:10px}.dmh-driver-updated{font-size:10px}}
@media(max-width:380px){.dmh-driver-row{gap:8px;padding:9px}.dmh-avatar{width:40px;height:40px;flex-basis:40px}.dmh-avatar img{width:40px;height:40px}.dmh-driver-name-row strong{font-size:13px}.dmh-driver-vehicle-row{gap:6px}.dmh-driver-vehicle-plate{max-width:88px;padding:3px 6px;font-size:9px}.dmh-driver-updated{font-size:9px}}
.dmh-driver-meta{font-size:10px;color:#64748b}
.dmh-driver-updated{font-size:11px;color:#8793a1}
.dmh-chevron{color:#b2bcc5;font-size:26px;line-height:1}
.dmh-list-empty{display:grid;justify-items:center;gap:6px;padding:38px 12px;text-align:center;color:#94a3b8}
.dmh-list-empty strong{font-size:13px;color:#475569}
.dmh-list-empty span{font-size:11px}
.dmh-list-empty button{margin-top:5px;border:0;border-radius:8px;padding:7px 10px;background:#fff4e9;color:#e9781e;font-size:10px;font-weight:900}
.dmh-selected-card{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:12px;padding:12px;border:1px solid #ffd7b7;border-radius:13px;background:#fffaf5}
.dmh-selected-card>div{display:grid;gap:4px;min-width:0}
.dmh-selected-card strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:12px}
.dmh-selected-card span{font-size:10px;color:#64748b}
.dmh-selected-card button{border:0;border-radius:8px;padding:8px 9px;background:#f68b2c;color:#fff;font-size:10px;font-weight:900;white-space:nowrap}
.dmh-current-focus-card{display:flex;align-items:center;gap:9px;margin:9px 0;padding:9px 10px;border:1px solid #ffd7b7;border-radius:12px;background:#fffaf5;box-shadow:0 4px 12px rgba(246,139,44,.06)}
.dmh-current-focus-avatar{display:grid;place-items:center;width:34px;height:34px;flex:0 0 34px;border:2px solid #fff;border-radius:50%;background:#eaf2ff;color:#3b82f6;overflow:hidden;font-size:14px;font-weight:950;box-shadow:0 2px 7px rgba(15,23,42,.13)}
.dmh-current-focus-avatar.is-vehicle{border-radius:10px;background:#f1f5f9;color:#64748b}.dmh-current-focus-avatar img{width:100%;height:100%;object-fit:cover}.dmh-current-focus-copy{display:grid;gap:2px;min-width:0;flex:1}.dmh-current-focus-copy small{color:#e9781e;font-size:8px;font-weight:950}.dmh-current-focus-copy strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#1e293b;font-size:12px;font-weight:950}.dmh-current-focus-copy>span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#64748b;font-size:9px}.dmh-current-focus-card button{border:0;border-radius:8px;padding:7px 8px;background:#f68b2c;color:#fff;font-size:9px;font-weight:900;white-space:nowrap}.dmh-current-focus-type{color:#e9781e;font-size:9px;font-style:normal;font-weight:900;white-space:nowrap}
.dmh-map-ready-overlay{position:absolute;z-index:650;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:7px;background:rgba(248,250,252,.84);backdrop-filter:blur(2px);color:#64748b}.dmh-map-ready-overlay strong{color:#334155;font-size:12px}.dmh-map-ready-overlay small{font-size:10px}
.dmh-fleet-loading{display:flex;align-items:center;justify-content:center;gap:10px;min-height:520px;color:#64748b;font-size:13px}
.dmh-spinner{width:24px;height:24px;border:3px solid #e2e8f0;border-top-color:#f68b2c;border-radius:50%;animation:dmh-spin .8s linear infinite}
@keyframes dmh-spin{to{transform:rotate(360deg)}}
.dmh-driver-marker-shell,.dmh-photo-marker-host{border:0!important;background:transparent!important}
.dmh-speed-marker{display:grid;grid-template-columns:minmax(0,1fr);justify-items:center;gap:3px;width:100%;height:100%;margin:0!important;padding:0!important;box-sizing:border-box!important;overflow:visible;pointer-events:auto;cursor:pointer}
.dmh-driver-work-label{display:flex;align-items:center;gap:5px;width:max-content;max-width:none;height:20px;box-sizing:border-box;padding:0 8px;border:1px solid rgba(15,23,42,.1);border-radius:999px;background:rgba(255,255,255,.97);color:#334155;box-shadow:0 3px 9px rgba(15,23,42,.18);font-size:10px;font-weight:850;line-height:18px;white-space:nowrap;pointer-events:auto;cursor:pointer}
.dmh-driver-work-label>i{width:6px;height:6px;flex:0 0 6px;border-radius:50%;background:var(--marker-color,#3b82f6)}
.dmh-driver-work-label>span{max-width:none;overflow:visible;text-overflow:clip;white-space:nowrap}
.dmh-speed-badge{display:grid;place-items:center;min-width:24px;height:24px;box-sizing:border-box;padding:0 5px;border:1px solid rgba(83,112,132,.24);border-radius:999px;background:rgba(247,251,253,.94);color:#527087;box-shadow:0 3px 9px rgba(15,23,42,.16);white-space:nowrap;line-height:1}
.dmh-speed-badge b{font-size:11px;font-weight:900;letter-spacing:-.2px}
.dmh-photo-marker{display:block;width:50px!important;height:50px!important;max-width:none!important;max-height:none!important;margin:0!important;padding:0!important;box-sizing:border-box!important;border:4px solid var(--marker-color,#fff);border-radius:50%;background:#eaf2ff;box-shadow:0 6px 18px rgba(15,23,42,.3);object-fit:cover;object-position:center;}
.dmh-photo-marker.is-selected{width:58px!important;height:58px!important;border-width:4px;box-shadow:0 0 0 5px rgba(246,139,44,.24),0 8px 22px rgba(15,23,42,.3)}
.dmh-driver-marker{position:relative;display:grid;place-items:center;width:46px;height:46px;max-width:none!important;max-height:none!important;margin:0!important;padding:0!important;box-sizing:border-box!important;border:3px solid var(--marker-color);border-radius:50%;background:var(--marker-soft);box-shadow:0 6px 18px rgba(15,23,42,.27);color:var(--marker-color);font-size:14px;font-weight:950;transition:width .16s,height .16s}
.dmh-driver-marker>span{display:grid;place-items:center;width:32px;height:32px;overflow:hidden;border-radius:50%;background:#fff}
.dmh-driver-avatar{position:relative}
.dmh-driver-avatar img{width:100%;height:100%;display:block;object-fit:cover;border-radius:50%}
.dmh-driver-avatar-fallback{display:grid;place-items:center;width:100%;height:100%;color:var(--marker-color);font-size:14px;font-weight:950}
.dmh-driver-marker>i{position:absolute;right:-1px;bottom:-1px;width:11px;height:11px;border:2px solid #fff;border-radius:50%;background:var(--marker-color)}
.dmh-driver-marker.is-selected{width:54px;height:54px;border-color:var(--marker-color);box-shadow:0 0 0 5px rgba(246,139,44,.24),0 8px 22px rgba(15,23,42,.28);font-size:16px}
.dmh-driver-marker.is-selected>span{width:38px;height:38px}

@media(max-width:1180px){.dmh-fleet-grid{grid-template-columns:minmax(0,1.35fr) minmax(330px,.95fr)}}
@media(max-width:920px){.dmh-fleet-shell{height:auto;min-height:0}.dmh-fleet-heading{align-items:flex-start;flex-direction:column}.dmh-fleet-grid{grid-template-columns:1fr;flex:none;min-height:0}.dmh-map-card{height:470px;min-height:470px}.dmh-map-stage{min-height:390px}.dmh-fleet-panel{height:auto;min-height:520px;max-height:680px}.dmh-driver-list{flex:0 1 auto;max-height:420px}}
@media(max-width:560px){.dmh-fleet-heading h1{font-size:25px}.dmh-map-card{height:400px;min-height:400px}.dmh-map-stage{min-height:320px}.dmh-fleet-panel{padding:13px}.dmh-panel-title-row>div{display:grid;gap:3px}.dmh-driver-name-row em{display:none}}

.dmh-fleet-eyebrow{display:block;color:#E9781E;font-size:10px;line-height:1;font-weight:950;letter-spacing:1.7px}
.dmh-mode-switch{display:flex;align-items:center;gap:0;padding:3px;border:1px solid #e2e8f0;border-radius:999px;background:#f8fafc;flex:0 0 auto}
.dmh-mode-switch button{height:34px;border:0;border-radius:999px;padding:0 17px;background:transparent;color:#64748b;font-size:11px;font-weight:900;white-space:nowrap}
.dmh-mode-switch button.is-active{background:#fff;border:1px solid #f68b2c;color:#e9781e;box-shadow:0 3px 9px rgba(246,139,44,.12)}
.dmh-vehicle-list{display:grid;grid-auto-rows:max-content;align-content:start;flex:1;gap:2px;min-height:0;overflow-y:auto;padding:5px 1px 2px;overscroll-behavior:contain}.dmh-vehicle-row{display:flex;align-items:center;gap:10px;width:100%;min-width:0;border:1px solid transparent;border-radius:12px;padding:10px;background:#fff;color:#334155;text-align:left;transition:background .16s,border-color .16s,box-shadow .16s}.dmh-vehicle-row:hover,.dmh-vehicle-row.is-selected{border-color:#ffd7b7;background:#fffaf5;box-shadow:0 5px 14px rgba(246,139,44,.08)}
.dmh-vehicle-thumb{display:grid;place-items:center;width:42px;height:34px;flex:0 0 42px;border-radius:9px;background:#f4f7fb;color:#64748b;overflow:hidden;font-size:20px;font-weight:900}.dmh-vehicle-thumb img{width:100%;height:100%;object-fit:cover}.dmh-vehicle-copy{display:grid;gap:3px;min-width:110px;flex:1}.dmh-vehicle-name-row{display:flex;align-items:center;gap:6px;min-width:0}.dmh-vehicle-name-row strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#1e293b;font-size:12px;font-weight:950}.dmh-vehicle-copy small{color:#64748b;font-size:10px}.dmh-trailer-badge{display:grid;place-items:center;min-width:17px;height:17px;padding:0 5px;border-radius:999px;background:#f68b2c;color:#fff;font-size:9px;font-weight:950}.dmh-vehicle-status{display:grid;gap:4px;min-width:100px;max-width:145px}.dmh-vehicle-status em{display:flex;align-items:center;gap:5px;width:max-content;max-width:100%;padding:4px 7px;border-radius:999px;font-size:9px;font-style:normal;font-weight:900;white-space:nowrap}.dmh-vehicle-status em i{width:6px;height:6px;border-radius:50%}.dmh-vehicle-status small{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#94a3b8;font-size:9px}.dmh-list-loading{display:flex;align-items:center;justify-content:center;gap:9px;min-height:160px;color:#64748b;font-size:11px;font-weight:800}.dmh-list-loading .dmh-spinner{width:18px;height:18px;border-width:2px}
.dmh-map-card .leaflet-control-attribution{box-sizing:border-box;max-width:calc(100vw - 24px);margin:0 10px 10px 0!important;padding:4px 9px!important;border:1px solid rgba(255,255,255,.82);border-radius:999px;background:rgba(255,255,255,.78);box-shadow:0 3px 10px rgba(15,23,42,.12);color:#64748b;font-size:9px;font-weight:700;line-height:1.25;white-space:nowrap;backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px)}.dmh-map-fit-all-button{position:absolute;right:10px;bottom:40px;z-index:1100;display:grid;place-items:center;width:40px;height:40px;box-sizing:border-box;padding:0;border:1px solid rgba(255,255,255,.9);border-radius:12px;background:rgba(255,255,255,.92);color:#334155;box-shadow:0 4px 14px rgba(15,23,42,.2);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);cursor:pointer;transition:background .16s ease,box-shadow .16s ease,transform .16s ease}
.dmh-map-fit-all-button:hover:not(:disabled){transform:translateY(-1px);background:#fff;box-shadow:0 6px 18px rgba(15,23,42,.24)}
.dmh-map-fit-all-button:focus-visible{outline:3px solid rgba(59,130,246,.72);outline-offset:2px}
.dmh-map-fit-all-button:disabled{opacity:.48;cursor:not-allowed}
.dmh-map-fit-all-button svg{display:block;width:20px;height:20px;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}
.dmh-map-card .leaflet-control-attribution a{color:#475569!important;font-weight:800;text-decoration:none}.dmh-map-card .leaflet-control-attribution a:hover{color:#e9781e!important;text-decoration:underline}@media(max-width:560px){.dmh-map-card .leaflet-control-attribution{max-width:calc(100vw - 16px);margin:0 7px 7px 0!important;padding:3px 7px!important;font-size:8px}}.dmh-vehicle-label-marker-host{border:0!important;background:transparent!important}.dmh-vehicle-label-marker{display:grid;justify-items:stretch;gap:2px;width:var(--marker-width);filter:drop-shadow(0 3px 8px rgba(15,23,42,.16))}.dmh-vehicle-marker-main{box-sizing:border-box;display:flex;align-items:center;justify-content:space-between;gap:7px;min-height:22px;padding:3px 7px;border:1px solid var(--marker-color);border-radius:6px;background:#fff;color:#1e293b;white-space:nowrap}.dmh-vehicle-marker-main.is-selected{border-color:#f68b2c;box-shadow:0 0 0 2px rgba(246,139,44,.2)}.dmh-vehicle-marker-duration{color:#64748b;font-size:8px;font-weight:800;line-height:1.1;white-space:nowrap}.dmh-vehicle-plate{display:block;max-width:100%;overflow:hidden;text-overflow:ellipsis;padding:0;border:0;border-radius:0;background:transparent;box-shadow:none;color:#1e293b;font-size:10px;font-weight:950;line-height:1.15;white-space:nowrap}.dmh-vehicle-label-marker .dmh-vehicle-marker-main.is-selected .dmh-vehicle-plate{color:#c75f12}.dmh-vehicle-parking-meta{display:block;box-sizing:border-box;width:max-content;max-width:210px;justify-self:center;padding:2px 6px;border:1px solid rgba(15,23,42,.12);border-radius:5px;background:#fff;color:#64748b;font-size:8px;font-weight:750;line-height:1.15;text-align:center;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.dmh-vehicle-marker-point{box-sizing:border-box;display:grid;place-items:center;justify-self:center;align-self:center;width:24px;height:24px;margin-top:2px;border:2px solid #fff;border-radius:50%;background:#f68b2c;color:#fff;box-shadow:0 0 0 3px rgba(246,139,44,.28),0 3px 8px rgba(15,23,42,.35);font-size:11px;font-weight:950;line-height:1;text-align:center}
.dmh-vehicle-cluster-marker-host{border:0!important;background:transparent!important}.dmh-vehicle-cluster-marker{display:grid;place-items:center;width:48px;height:48px;border:4px solid #fff;border-radius:50%;background:var(--cluster-background);box-shadow:0 6px 18px rgba(15,23,42,.3);color:#fff}.dmh-vehicle-cluster-marker b{display:grid;place-items:center;width:34px;height:34px;border-radius:50%;background:rgba(15,23,42,.58);font-size:15px;font-weight:950}.dmh-vehicle-group-popup{display:grid;gap:7px;min-width:170px}.dmh-vehicle-group-popup>strong{color:#1e293b;font-size:13px;font-weight:950}.dmh-vehicle-group-popup>div{display:grid;gap:5px;max-height:138px;overflow-y:auto;padding-right:3px;overscroll-behavior:contain}.dmh-vehicle-group-popup>div::-webkit-scrollbar{width:5px}.dmh-vehicle-group-popup>div::-webkit-scrollbar-thumb{border-radius:999px;background:#cbd5e1}.dmh-vehicle-group-popup button{display:flex;align-items:center;gap:8px;width:100%;padding:5px;border:1px solid #e5e7eb;border-radius:8px;background:#fff;color:#1e293b;font-size:11px;font-weight:900;text-align:left}.dmh-vehicle-group-popup button:hover{background:#fffaf5;border-color:#f68b2c}
@media(max-width:920px){.dmh-fleet-heading{gap:12px}.dmh-mode-switch{align-self:stretch}.dmh-mode-switch button{flex:1}}
@media(max-width:560px){.dmh-vehicle-status{min-width:80px;max-width:100px}.dmh-fleet-eyebrow{font-size:9px}}
.dmh-current-history-state{display:flex;align-items:center;gap:10px;margin-top:14px;padding:13px;border:1px solid #fed7aa;border-radius:14px;background:#fffaf3;box-shadow:0 5px 15px rgba(246,139,44,.08)}.dmh-current-history-state.is-rest{border-color:#ddd6fe;background:#faf8ff;box-shadow:0 5px 15px rgba(139,92,246,.08)}.dmh-current-history-icon{display:grid;place-items:center;width:34px;height:34px;flex:0 0 34px;border-radius:11px;background:#f68b2c;color:#fff;font-size:12px;font-weight:950}.dmh-current-history-state.is-rest .dmh-current-history-icon{background:#8b5cf6}.dmh-current-history-state>div{display:grid;gap:3px;min-width:0}.dmh-current-history-state strong{color:#9a5315;font-size:11px;font-weight:950}.dmh-current-history-state.is-rest strong{color:#6d4ac0}.dmh-current-history-state b{color:#1e293b;font-size:15px;font-weight:950}.dmh-current-history-state span{color:#94a3b8;font-size:10px;font-weight:800}
.dmh-history-record{display:flex;align-items:center;gap:9px;width:100%;padding:10px;border:1px solid #eef2f7;border-radius:12px;background:#fff;color:#334155;text-align:left;cursor:pointer;transition:border-color .16s,background .16s,box-shadow .16s}.dmh-history-record:hover,.dmh-history-record.is-selected{border-color:#f6c48e;background:#fffaf5;box-shadow:0 5px 14px rgba(246,139,44,.08)}.dmh-history-record.is-selected{border-color:#8bbcf0;background:#f5faff;box-shadow:0 5px 14px rgba(36,123,209,.1)}.dmh-history-record-icon.is-rest{background:#f1eafe;color:#7c3aed}

.dmh-history-range-loader{display:grid;gap:9px;margin:12px 0;padding:12px;border:1px solid #dbeafe;border-radius:13px;background:#f7fbff}
.dmh-history-range-loader>div:first-child{display:grid;gap:4px}.dmh-history-range-loader strong{color:#1e3a5f;font-size:11px;font-weight:950}.dmh-history-range-loader span,.dmh-history-range-loader small{color:#64748b;font-size:10px;line-height:1.45}.dmh-history-range-inputs{display:flex;align-items:center;flex-wrap:wrap;gap:7px}.dmh-history-range-inputs label{display:flex;align-items:center;gap:5px;color:#64748b;font-size:10px;font-weight:850}.dmh-history-range-inputs input{width:96px;padding:7px 8px;border:1px solid #dbe3ee;border-radius:8px;background:#fff;color:#334155;font-size:11px}.dmh-history-range-inputs button{padding:8px 11px;border:0;border-radius:8px;background:#0f5ea8;color:#fff;font-size:10px;font-weight:900}.dmh-history-range-inputs button:disabled{opacity:.5}.dmh-history-range-inputs button.is-secondary{border:1px solid #dbe3ee;background:#fff;color:#475569}.dmh-history-range-loader small.is-error{color:#b91c1c;font-weight:800}.dmh-history-record.is-job-link{cursor:pointer}.dmh-history-record.is-job-link em{flex:0 0 auto;color:#2563eb;font-size:9px;font-style:normal;font-weight:900}

.dmh-history-work-list{display:grid;gap:10px}
.dmh-history-work-group{overflow:hidden;border:1px solid #e2e8f0;border-radius:14px;background:#fff;box-shadow:0 4px 12px rgba(15,23,42,.04)}
.dmh-history-work-header{display:flex;align-items:center;gap:10px;padding:12px;background:linear-gradient(120deg,#f5f9ff,#fff)}
.dmh-history-work-icon{display:grid;place-items:center;width:34px;height:34px;flex:0 0 34px;border-radius:11px;background:#e8f1ff;color:#247bd1;font-size:12px;font-weight:950}
.dmh-history-work-icon.is-event{background:#f1f5f9;color:#64748b;font-size:9px}
.dmh-history-work-title{display:grid;gap:4px;min-width:0;flex:1}
.dmh-history-work-title strong{overflow:hidden;color:#1e293b;font-size:12px;font-weight:950;text-overflow:ellipsis;white-space:nowrap}
.dmh-history-work-title span,.dmh-history-work-title small{overflow:hidden;color:#64748b;font-size:10px;font-weight:800;text-overflow:ellipsis;white-space:nowrap}
.dmh-history-work-title small{color:#94a3b8;font-weight:700}
.dmh-history-work-open{flex:0 0 auto;padding:6px 8px;border:0;border-radius:9px;background:#eff6ff;color:#2563eb;font-size:9px;font-weight:950;white-space:nowrap;cursor:pointer}
.dmh-history-station-list{display:grid;gap:7px;padding:0 10px 10px}
.dmh-history-station{padding:9px 10px;border:1px solid #edf2f7;border-radius:11px;background:#fbfdff}
.dmh-history-station-heading{display:flex;align-items:center;justify-content:space-between;gap:8px}
.dmh-history-station-heading strong{overflow:hidden;color:#334155;font-size:11px;font-weight:950;text-overflow:ellipsis;white-space:nowrap}
.dmh-history-station-heading span{flex:0 0 auto;color:#64748b;font-size:10px;font-weight:850;font-variant-numeric:tabular-nums}
.dmh-history-station-events{display:flex;flex-wrap:wrap;gap:5px;margin-top:7px}
.dmh-history-station-event{display:inline-flex;align-items:center;gap:4px;padding:4px 6px;border-radius:7px;background:#f1f5f9;color:#64748b;font-size:9px;font-weight:850}
.dmh-history-station-event time{color:#334155;font-variant-numeric:tabular-nums}
.dmh-history-work-other-events{display:flex;flex-wrap:wrap;gap:6px;padding:0 12px 11px;color:#64748b;font-size:10px;font-weight:800}
.dmh-history-loose-event{display:flex;align-items:center;gap:9px;padding:10px;border:1px solid #eef2f7;border-radius:12px;background:#fff}
.dmh-history-loose-event>div{display:grid;gap:3px;min-width:0}
.dmh-history-loose-event strong{color:#334155;font-size:11px;font-weight:950}
.dmh-history-loose-event span{overflow:hidden;color:#94a3b8;font-size:10px;text-overflow:ellipsis;white-space:nowrap}
@media(max-width:600px){.dmh-history-work-header{gap:7px;padding:10px}.dmh-history-work-open{padding:6px}.dmh-history-station{padding:8px}}

/* Map-only expand/collapse: the containing workspace clips the map and its controls. */
.dmh-fleet-heading{min-height:0;max-height:120px;overflow:hidden;transform:translateY(0);transition:max-height .34s cubic-bezier(.2,.75,.25,1),margin .34s cubic-bezier(.2,.75,.25,1),opacity .22s ease,transform .3s ease}
.dmh-fleet-grid{position:relative;transition:grid-template-columns .38s cubic-bezier(.2,.75,.25,1),gap .38s ease}
.dmh-map-card{transition:border-radius .34s ease,box-shadow .34s ease}
.dmh-fleet-panel{transition:opacity .2s ease,transform .34s cubic-bezier(.2,.75,.25,1),padding .34s ease,border-width .34s ease,visibility 0s}
.dmh-fleet-shell.is-map-expanded{position:absolute;inset:-22px 0 0 -22px;z-index:50;width:auto;height:auto;min-height:0;padding:0}
.dmh-fleet-shell.is-map-expanded .dmh-fleet-heading{max-height:0;margin:0;opacity:0;transform:translateY(-12px);pointer-events:none}
.dmh-fleet-shell.is-map-expanded .dmh-fleet-grid{grid-template-columns:minmax(0,1fr) minmax(0,0fr);flex:1 1 0%;height:100%;min-height:0;gap:0}
.dmh-fleet-shell.is-map-expanded .dmh-map-card{height:100%;min-height:0;border:0;border-radius:0;box-shadow:none}
.dmh-fleet-shell.is-map-expanded .dmh-fleet-panel{height:100%;min-height:0;max-height:none;padding:0;border-width:0;opacity:0;transform:translateX(24px);visibility:hidden;pointer-events:none;transition:opacity .2s ease,transform .34s cubic-bezier(.2,.75,.25,1),padding .34s ease,border-width .34s ease,visibility 0s linear .36s}
.dmh-fleet-shell.is-map-expanded .dmh-map-card .dmh-map-toolbar{top:30px;left:72px;right:210px;transition:top .38s cubic-bezier(.2,.75,.25,1),left .38s ease,right .38s ease}
.dmh-fleet-shell.is-map-expanded .dmh-map-card .leaflet-top.leaflet-left{top:33px;left:38px;transition:top .38s cubic-bezier(.2,.75,.25,1),left .38s ease}
.dmh-fleet-shell.is-map-expanded .dmh-map-card .leaflet-control-zoom{border:1px solid rgba(255,255,255,.62);border-radius:12px;background:rgba(255,255,255,.62);box-shadow:0 4px 12px rgba(15,23,42,.1);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px)}
.dmh-fleet-shell.is-map-expanded .dmh-map-card .leaflet-control-zoom a{background:rgba(255,255,255,.48)}
.dmh-map-toolbar .dmh-map-expand-toggle{display:grid;place-items:center;flex:0 0 38px;width:38px;height:38px;padding:0;border:0;border-radius:0;background:transparent;color:#334155;box-shadow:none;backdrop-filter:none;-webkit-backdrop-filter:none;cursor:pointer;transition:color .18s ease,transform .18s ease,opacity .18s ease}
.dmh-map-toolbar .dmh-map-expand-toggle:hover{background:transparent;color:#e9781e;transform:scale(1.08)}
.dmh-map-toolbar .dmh-map-expand-toggle:focus-visible{outline:none;color:#e9781e}
.dmh-map-expand-toggle svg{display:block;width:24px;height:24px;fill:none;stroke:currentColor;stroke-width:2.15;stroke-linecap:round;stroke-linejoin:round}
@media(max-width:920px){.dmh-fleet-shell.is-map-expanded .dmh-fleet-grid{grid-template-columns:minmax(0,1fr) minmax(0,0fr);grid-template-rows:minmax(0,1fr);flex:1 1 0%;height:100%;min-height:0}.dmh-fleet-shell.is-map-expanded .dmh-map-card{height:100%;min-height:0}.dmh-fleet-shell.is-map-expanded .dmh-fleet-panel{height:100%;min-height:0;max-height:none}}
@media(max-width:560px){.dmh-fleet-shell.is-map-expanded .dmh-map-card .dmh-map-toolbar{top:96px;left:14px;right:14px}.dmh-fleet-shell.is-map-expanded .dmh-map-title-block{display:none!important}.dmh-fleet-shell.is-map-expanded .dmh-map-actions{justify-content:center;gap:5px}.dmh-fleet-shell.is-map-expanded .dmh-map-card .leaflet-top.leaflet-left{top:210px;left:16px}}
@media(max-width:420px){.dmh-fleet-shell.is-map-expanded .dmh-map-card .leaflet-top.leaflet-left{top:260px}}
@media(prefers-reduced-motion:reduce){.dmh-overview-map-viewport,.dmh-fleet-heading,.dmh-fleet-grid,.dmh-map-card,.dmh-fleet-panel,.dmh-map-toolbar,.dmh-map-card .leaflet-top.leaflet-left{transition:none!important}}

/* 桌面／平板寬螢幕：收窄右側成員／車輛列表，將空間還給地圖 */
@media(min-width:921px){
  .dmh-fleet-grid{grid-template-columns:minmax(0,1fr) minmax(300px,360px);gap:10px}
}
@media(min-width:921px) and (max-width:1180px){
  .dmh-fleet-grid{grid-template-columns:minmax(0,1fr) minmax(280px,32vw);gap:9px}
}

/* 平板／窄視窗放大地圖：填滿可視區，不沿用桌面內距補償造成的負位移。 */
@media(max-width:979px){
  .dmh-overview-map-viewport{overflow-x:hidden!important;overflow-y:auto!important;overscroll-behavior-y:contain;-webkit-overflow-scrolling:touch}
  .dmh-overview-map-viewport:has(.dmh-fleet-shell.is-map-expanded){overflow:hidden!important}
  .dmh-fleet-shell.is-map-expanded{inset:0}
  .dmh-fleet-shell.is-map-expanded .dmh-fleet-grid{grid-template-columns:minmax(0,1fr) minmax(0,0fr);grid-template-rows:minmax(0,1fr);flex:1 1 0%;height:100%;min-height:0}
  .dmh-fleet-shell.is-map-expanded .dmh-map-card{height:100%;min-height:0}
  .dmh-fleet-shell.is-map-expanded .dmh-fleet-panel{height:100%;min-height:0;max-height:none}
}

/* 成員／車輛篩選列：維持可橫滑，滑軌僅在 hover 或鍵盤聚焦時出現 */
.dmh-filter-row{
  display:flex;
  align-items:center;
  gap:8px;
  overflow-x:auto;
  overflow-y:hidden;
  padding:12px 0 7px;
  scrollbar-width:none;
  scrollbar-color:transparent transparent;
  -webkit-overflow-scrolling:touch;
}
.dmh-filter-row::-webkit-scrollbar{height:0}
.dmh-filter-row::-webkit-scrollbar-track{background:transparent}
.dmh-filter-row::-webkit-scrollbar-thumb{border-radius:999px;background:transparent}
.dmh-filter-row:hover,
.dmh-filter-row:focus-within{
  scrollbar-width:thin;
  scrollbar-color:rgba(148,163,184,.6) transparent;
}
.dmh-filter-row:hover::-webkit-scrollbar,
.dmh-filter-row:focus-within::-webkit-scrollbar{height:5px}
.dmh-filter-row:hover::-webkit-scrollbar-thumb,
.dmh-filter-row:focus-within::-webkit-scrollbar-thumb{background:rgba(148,163,184,.6)}
.dmh-filter-row::-webkit-scrollbar-thumb:hover{background:rgba(100,116,139,.75)}
.dmh-filter-row button{
  height:34px;
  gap:7px;
  border-color:#e6ebf2;
  border-radius:999px;
  padding:0 12px;
  background:#f8fafc;
  color:#64748b;
  transition:background .18s ease,border-color .18s ease,color .18s ease,box-shadow .18s ease,transform .18s ease;
}
.dmh-filter-row button:hover{
  border-color:#f7bf8a;
  background:#fff8f1;
  color:#c96516;
  transform:translateY(-1px);
}
.dmh-filter-row button b{
  min-width:19px;
  height:19px;
  padding:0 5px;
  border-radius:999px;
  background:#edf1f6;
  color:#718096;
  font-size:9px;
}
.dmh-filter-row button.is-active{
  border-color:#f68b2c;
  background:linear-gradient(135deg,#f89b49,#ed7a20);
  color:#fff;
  box-shadow:0 3px 8px rgba(246,139,44,.2);
}
.dmh-filter-row button.is-active b{background:rgba(255,255,255,.22);color:#fff}

/* 平板放大地圖：Leaflet 縮放鈕與標題/操作列使用完全相同的垂直邊界。 */
@media(min-width:561px) and (max-width:1279px){
  .dmh-fleet-shell.is-map-expanded{inset:0}
  .dmh-fleet-shell.is-map-expanded .dmh-map-title-block{display:grid!important;flex:0 0 auto;min-width:max-content}
  .dmh-fleet-shell.is-map-expanded .dmh-map-card .dmh-map-toolbar{top:30px;left:56px;right:210px;height:62px;min-height:62px;max-height:62px;justify-content:flex-start;gap:10px;padding:0 16px}
  .dmh-fleet-shell.is-map-expanded .dmh-map-actions{flex:0 1 auto;margin-left:auto;justify-content:flex-start;flex-wrap:nowrap;min-width:0;max-width:calc(100% - 82px);overflow-x:auto;overflow-y:hidden;scrollbar-width:none;-webkit-overflow-scrolling:touch}
  .dmh-fleet-shell.is-map-expanded .dmh-map-actions::-webkit-scrollbar{display:none}
  .dmh-fleet-shell.is-map-expanded .dmh-map-actions>*{flex:0 0 auto}
  .dmh-fleet-shell.is-map-expanded .dmh-map-card .leaflet-top.leaflet-left{top:30px;left:16px}
  .dmh-fleet-shell.is-map-expanded .dmh-map-card .leaflet-control-zoom{display:flex;flex-direction:column;width:34px;height:62px}
  .dmh-fleet-shell.is-map-expanded .dmh-map-card .leaflet-control-zoom a{flex:0 0 30px;width:32px;height:30px}
}

/* 放大即時成員地圖後，頭像欄才滑入；外框與頭像保持緊湊。 */
.dmh-live-driver-rail{position:absolute;top:50%;right:0;z-index:650;display:flex;flex-direction:column;align-items:center;gap:6px;box-sizing:border-box;width:54px;height:264px;max-height:calc(100% - 24px);padding:6px 3px;overflow-x:hidden;overflow-y:auto;border:1px solid rgba(255,255,255,.88);border-radius:16px;background:rgba(255,255,255,.88);box-shadow:0 8px 24px rgba(15,23,42,.18);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);transform:translateY(-50%);animation:dmhLiveDriverRailEnter .46s cubic-bezier(.2,.75,.25,1) both;scrollbar-width:thin;scrollbar-color:rgba(100,116,139,.58) transparent;overscroll-behavior:contain}
@keyframes dmhLiveDriverRailEnter{from{opacity:0;transform:translate(12px,-50%) scale(.96)}to{opacity:1;transform:translate(0,-50%) scale(1)}}
.dmh-live-driver-rail::-webkit-scrollbar{width:4px}
.dmh-live-driver-rail::-webkit-scrollbar-track{background:transparent}
.dmh-live-driver-rail::-webkit-scrollbar-thumb{border-radius:99px;background:rgba(100,116,139,.48)}
.dmh-live-driver-rail:hover::-webkit-scrollbar-thumb{background:rgba(71,85,105,.72)}
.dmh-live-driver-avatar{position:relative;display:grid;place-items:center;flex:0 0 42px;width:42px;height:42px;box-sizing:border-box;padding:2px;border:2px solid transparent;border-radius:50%;background:rgba(255,255,255,.92);color:#475569;box-shadow:0 2px 7px rgba(15,23,42,.12);font-size:14px;font-weight:900;cursor:pointer;transition:transform .16s ease,border-color .16s ease,box-shadow .16s ease}
.dmh-live-driver-avatar:hover{z-index:1;transform:scale(1.08);box-shadow:0 4px 12px rgba(15,23,42,.22)}
.dmh-live-driver-avatar.is-selected{border-color:#f68b2c;box-shadow:0 0 0 2px rgba(246,139,44,.2),0 3px 10px rgba(15,23,42,.18)}
.dmh-live-driver-avatar:focus-visible{outline:3px solid rgba(36,123,209,.72);outline-offset:2px}
.dmh-live-driver-avatar-image{display:grid;place-items:center;width:100%;height:100%;overflow:hidden;border-radius:50%;background:#eef2f6}
.dmh-live-driver-avatar-image img{display:block;width:100%;height:100%;object-fit:cover}
.dmh-live-driver-status{position:absolute;right:-1px;bottom:0;width:10px;height:10px;border:2px solid #fff;border-radius:50%;box-shadow:0 1px 3px rgba(15,23,42,.22)}
@media(max-width:560px){
  .dmh-live-driver-rail{right:0;width:48px;height:232px;gap:5px;padding:6px 2px;border-radius:14px}
  .dmh-live-driver-avatar{flex-basis:36px;width:36px;height:36px;font-size:12px}
  .dmh-live-driver-status{width:9px;height:9px}
}
@media(max-width:380px){
  .dmh-live-driver-rail{right:0;width:46px;padding-right:3px;padding-left:3px}
  .dmh-live-driver-avatar{flex-basis:33px;width:33px;height:33px}
}
@media(prefers-reduced-motion:reduce){.dmh-live-driver-rail{animation:none}}

/* 快捷頭像固定最多五位；完整成員清單在獨立面板中捲動。 */
.dmh-live-driver-rail{z-index:9999;width:62px;height:auto;max-height:calc(100% - 24px);overflow:visible;border-color:transparent;background:transparent;box-shadow:none;backdrop-filter:none;-webkit-backdrop-filter:none;isolation:isolate;pointer-events:auto;scrollbar-width:none;touch-action:pan-y}
.dmh-live-driver-rail-shape{position:absolute;top:-18px;right:0;z-index:-1;width:100%;height:calc(100% + 36px);overflow:visible;pointer-events:none;filter:drop-shadow(0 8px 20px rgba(15,23,42,.14))}
.dmh-live-driver-rail::-webkit-scrollbar{display:none}
.dmh-live-driver-list-toggle{display:grid;place-items:center;flex:0 0 32px;width:32px;height:32px;box-sizing:border-box;padding:0;border:0;border-radius:50%;background:transparent;color:#475569;cursor:pointer;transition:background .16s ease,color .16s ease}
.dmh-live-driver-list-toggle svg{width:19px;height:19px;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round}
.dmh-live-driver-list-toggle:hover,.dmh-live-driver-list-toggle.is-active{background:rgba(37,99,235,.08);color:#1d4ed8}
.dmh-live-driver-picker{position:absolute;top:50%;right:82px;z-index:10000;display:flex;flex-direction:column;width:min(340px,calc(100% - 112px));max-height:min(520px,calc(100% - 24px));box-sizing:border-box;transform:translateY(-50%);overflow:hidden;border:1px solid rgba(255,255,255,.94);border-radius:17px;background:rgba(255,255,255,.97);box-shadow:0 14px 38px rgba(15,23,42,.24);backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px)}
.dmh-live-driver-picker-head{display:flex;align-items:center;justify-content:space-between;gap:14px;flex:0 0 auto;padding:13px 14px;border-bottom:1px solid rgba(15,23,42,.08)}
.dmh-live-driver-picker-head>div{display:grid;gap:3px;min-width:0}
.dmh-live-driver-picker-head strong{color:#1e293b;font-size:14px;font-weight:900}
.dmh-live-driver-picker-head span{color:#64748b;font-size:11px;font-weight:650}
.dmh-live-driver-picker-head>button{display:grid;place-items:center;flex:0 0 30px;width:30px;height:30px;padding:0;border:0;border-radius:9px;background:#f1f5f9;color:#475569;font-size:21px;line-height:1;cursor:pointer}
.dmh-live-driver-picker-head>button:hover{background:#e2e8f0;color:#0f172a}
.dmh-live-driver-picker-list{display:grid;gap:5px;min-height:0;padding:8px;overflow-x:hidden;overflow-y:auto;overscroll-behavior:contain;scrollbar-width:auto;scrollbar-color:#64748b rgba(148,163,184,.2);-webkit-overflow-scrolling:touch}
.dmh-live-driver-picker-list::-webkit-scrollbar{width:8px}
.dmh-live-driver-picker-list::-webkit-scrollbar-track{border-radius:99px;background:rgba(148,163,184,.2)}
.dmh-live-driver-picker-list::-webkit-scrollbar-thumb{border:1px solid rgba(255,255,255,.8);border-radius:99px;background:rgba(71,85,105,.75)}
.dmh-live-driver-picker-row{display:grid;grid-template-columns:38px minmax(0,1fr) auto;align-items:center;gap:10px;min-height:50px;padding:5px 8px;border:1px solid transparent;border-radius:12px;background:rgba(248,250,252,.88);color:#334155;text-align:left;cursor:pointer;transition:background .14s ease,border-color .14s ease}
.dmh-live-driver-picker-row:hover{border-color:#dbeafe;background:#eff6ff}
.dmh-live-driver-picker-row.is-selected{border-color:rgba(246,139,44,.48);background:#fff7ed}
.dmh-live-driver-picker-avatar{display:grid;place-items:center;width:36px;height:36px;overflow:hidden;border-radius:50%;background:#e8eef5;color:#475569;font-size:13px;font-weight:850}
.dmh-live-driver-picker-avatar img{display:block;width:100%;height:100%;object-fit:cover}
.dmh-live-driver-picker-name{display:grid;gap:2px;min-width:0;overflow:hidden;color:#1e293b;text-overflow:ellipsis;white-space:nowrap}
.dmh-live-driver-picker-name strong{overflow:hidden;font-size:12px;font-weight:850;text-overflow:ellipsis;white-space:nowrap}
.dmh-live-driver-picker-name small{color:#64748b;font-size:10px;font-weight:650}
.dmh-live-driver-picker-status{max-width:76px;overflow:hidden;padding:4px 7px;border-radius:999px;font-size:10px;font-weight:850;text-overflow:ellipsis;white-space:nowrap}
@media(max-width:560px){
  .dmh-live-driver-rail{right:0;width:56px;gap:5px;padding:6px 2px;border-radius:14px}
  .dmh-live-driver-picker{right:70px;width:min(300px,calc(100% - 84px));max-height:calc(100% - 24px);border-radius:15px}
  .dmh-live-driver-picker-head{padding:11px 12px}
}
@media(max-width:380px){
  .dmh-live-driver-rail{right:0;width:54px}
  .dmh-live-driver-picker{right:64px;width:calc(100% - 76px)}
  .dmh-live-driver-picker-row{grid-template-columns:34px minmax(0,1fr) auto;gap:7px;padding:5px 6px}
  .dmh-live-driver-picker-avatar{width:32px;height:32px}
  .dmh-live-driver-picker-status{max-width:66px;padding:4px 6px;font-size:9px}
}

`;

const styles = {
  page: {
    height: "100vh",
    background: TOKENS.pageBg,
    fontFamily: '"Inter", "Noto Sans TC", sans-serif',
    color: TOKENS.text,
    overflow: "hidden",
  },

  layout: {
    display: "grid",
    height: "100%",
    width: "100%",
    transition: "grid-template-columns 280ms ease",
  },

  icon: {
    width: 22,
    height: 22,
    display: "block",
  },

  mainArea: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    minWidth: 0,
    minHeight: 0,
    background: TOKENS.white,
    overflow: "hidden",
  },

  topbar: {
    position: "relative",
    zIndex: 20,
    height: 84,
    background: TOKENS.white,
    borderBottom: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 24px",
    flexShrink: 0,
    overflow: "hidden",
    transition:
      "background-color 260ms ease, height 220ms ease, flex-basis 220ms ease, padding 220ms ease, opacity 180ms ease, transform 220ms ease",
  },

  topbarMapOverlay: {
    position: "relative",
    zIndex: 20,
    background: "rgba(255,255,255,0.14)",
    borderBottom: "none",
    overflow: "visible",
    pointerEvents: "none",
  },

  topbarMapInteractive: {
    pointerEvents: "auto",
  },

  topbarRightMapOverlay: {
    pointerEvents: "auto",
    padding: "5px 9px",
    border: "1px solid rgba(255,255,255,0.78)",
    borderRadius: 999,
    background: "rgba(255,255,255,0.72)",
    boxShadow: "0 4px 16px rgba(15,23,42,0.12)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
  },

  topbarHidden: {
    height: 0,
    flex: "0 0 0px",
    minHeight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    opacity: 0,
    transform: "translateY(-100%)",
    pointerEvents: "none",
  },

  topbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    minWidth: 0,
  },

  breadcrumbText: {
    fontSize: 17,
    color: TOKENS.textSoft,
    fontWeight: 700,
  },

  breadcrumbSep: {
    fontSize: 26,
    color: "#9CA3AF",
    lineHeight: 1,
  },

  breadcrumbCurrent: {
    fontSize: 17,
    color: TOKENS.textStrong,
    fontWeight: 900,
  },

  topbarRight: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },

  notifyWrap: {
    position: "relative",
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: TOKENS.textSoft,
  },

  topIcon: {
    width: 24,
    height: 24,
    display: "block",
  },

  notifyDot: {
    position: "absolute",
    top: 6,
    right: 7,
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "#FF4D4F",
    border: `2px solid ${TOKENS.white}`,
  },

  avatarWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  avatarCircle: {
    width: 44,
    height: 44,
    flex: "0 0 44px",
    borderRadius: "50%",
    overflow: "hidden",
    background: "linear-gradient(135deg,#CBD5E1,#94A3B8)",
    color: TOKENS.white,
    fontWeight: 900,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
  },

  topbarPopover: {
    position: "absolute",
    top: "calc(100% + 10px)",
    right: 0,
    minWidth: 190,
    display: "grid",
    gap: 4,
    padding: 10,
    border: `1px solid ${TOKENS.line}`,
    borderRadius: 14,
    background: TOKENS.white,
    boxShadow: "0 14px 34px rgba(15,23,42,0.14)",
    zIndex: 30,
  },

  chatPushToast: {
    position: "fixed",
    top: 16,
    right: 16,
    width: "min(480px, calc(100vw - 32px))",
    boxSizing: "border-box",
    display: "grid",
    gap: 12,
    padding: 18,
    border: `1px solid ${TOKENS.line}`,
    borderRadius: 18,
    background: TOKENS.white,
    boxShadow: "0 14px 38px rgba(15,23,42,0.18)",
    zIndex: 3000,
  },
  chatPushToastHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  chatPushToastTitle: {
    minWidth: 0,
    color: TOKENS.textStrong,
    fontSize: 16,
    fontWeight: 900,
  },
  chatPushToastClose: {
    width: 28,
    height: 28,
    flex: "0 0 28px",
    border: 0,
    borderRadius: 8,
    background: "transparent",
    color: TOKENS.textSoft,
    fontSize: 22,
    cursor: "pointer",
  },
  chatPushToastBody: {
    color: TOKENS.textSoft,
    fontSize: 14,
    lineHeight: 1.55,
    overflowWrap: "anywhere",
  },
  chatPushToastButton: {
    justifySelf: "start",
    minHeight: 40,
    padding: "0 16px",
    border: 0,
    borderRadius: 10,
    background: TOKENS.orange,
    color: TOKENS.white,
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
  },

  mobileMenuBtn: {
    width: 42,
    height: 42,
    borderRadius: 12,
    border: `1px solid ${TOKENS.line}`,
    background: TOKENS.white,
    color: TOKENS.text,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },

  contentViewport: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    background: TOKENS.pageBg,
    borderTopLeftRadius: 24,
  },

  contentInner: {
    padding: 22,
  },

  jobListContentInner: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
    padding: 0,
  },

  overviewContentInner: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    boxSizing: "border-box",
  },

  statsGrid: {
    display: "grid",
    gap: 16,
    marginBottom: 16,
  },

  statCard: {
    background: TOKENS.white,
    borderRadius: 24,
    border: `1px solid ${TOKENS.lineSoft}`,
    boxShadow: TOKENS.shadowCard,
    padding: "18px 18px",
    display: "flex",
    alignItems: "center",
    gap: 14,
    minHeight: 108,
  },

  statIconWrap: {
    width: 66,
    height: 66,
    borderRadius: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  statIcon: {
    width: 32,
    height: 32,
    display: "block",
  },

  statContent: {
    minWidth: 0,
  },

  statLabel: {
    fontSize: 14,
    fontWeight: 900,
    color: TOKENS.textStrong,
    marginBottom: 4,
  },

  statValue: {
    fontSize: 42,
    fontWeight: 900,
    color: "#0A1032",
    lineHeight: 1,
    marginBottom: 5,
  },

  statSub: {
    fontSize: 13,
    color: TOKENS.textSoft,
    fontWeight: 600,
    lineHeight: 1.45,
  },

  overviewGrid: {
    display: "grid",
    gap: 16,
    alignItems: "stretch",
  },

  cardPanel: {
    background: TOKENS.white,
    borderRadius: 28,
    border: `1px solid ${TOKENS.lineSoft}`,
    boxShadow: TOKENS.shadowSoft,
    padding: 18,
  },

  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 14,
  },

  cardTitleWrap: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },

  cardTitle: {
    fontSize: 22,
    fontWeight: 900,
    color: TOKENS.textStrong,
  },

  liveBadge: {
    height: 30,
    padding: "0 12px",
    borderRadius: 12,
    background: TOKENS.greenSoft,
    color: TOKENS.green,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    fontWeight: 800,
  },

  liveDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: TOKENS.green,
  },

  tabGroup: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },

  tabBtn: {
    height: 38,
    padding: "0 14px",
    borderRadius: 12,
    border: "none",
    background: "transparent",
    color: TOKENS.textSoft,
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
  },

  tabBtnActive: {
    color: TOKENS.textStrong,
    boxShadow: `inset 0 -3px 0 ${TOKENS.orange}`,
  },

  mapWrap: {
    width: "100%",
    height: "calc(100vh - 330px)",
    minHeight: 360,
    maxHeight: 540,
    borderRadius: 24,
    overflow: "hidden",
    background: "#D9EEF7",
    border: `1px solid ${TOKENS.lineSoft}`,
  },

  mapFrame: {
    width: "100%",
    height: "100%",
    border: "none",
  },

  rightColumnFullHeight: {
    display: "grid",
    gridTemplateRows: "1fr 1fr",
    gap: 16,
    height: "100%",
    minHeight: "calc(100vh - 330px)",
  },

  sideTopCard: {
    display: "flex",
    flexDirection: "column",
  },

  sideBottomCard: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },

  sideTitle: {
    fontSize: 20,
    fontWeight: 900,
    color: TOKENS.textStrong,
    marginBottom: 14,
  },

  orderList: {
    display: "grid",
    gap: 8,
  },

  orderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 0",
    borderBottom: `1px solid ${TOKENS.line}`,
  },

  orderLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
  },

  orderTruck: {
    fontSize: 24,
    lineHeight: 1,
  },

  orderName: {
    fontSize: 16,
    fontWeight: 900,
    color: TOKENS.textStrong,
  },

  orderRoute: {
    fontSize: 14,
    color: TOKENS.textSoft,
    fontWeight: 700,
  },

  orderRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },

  statusPill: {
    minWidth: 78,
    height: 32,
    padding: "0 12px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 900,
  },

  etaText: {
    fontSize: 13,
    color: TOKENS.textStrong,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },

  distributionWrap: {
    display: "flex",
    gap: 20,
  },

  donut: {
    width: 146,
    height: 146,
    borderRadius: "50%",
    position: "relative",
    flexShrink: 0,
  },

  donutInner: {
    position: "absolute",
    inset: 38,
    background: TOKENS.white,
    borderRadius: "50%",
  },

  legendList: {
    flex: 1,
    display: "grid",
    gap: 12,
    width: "100%",
  },

  legendRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
  },

  legendLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  legendDot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    flexShrink: 0,
  },

  legendLabel: {
    fontSize: 15,
    color: TOKENS.textStrong,
    fontWeight: 800,
  },

  legendValue: {
    fontSize: 18,
    color: TOKENS.textStrong,
    fontWeight: 900,
  },

  placeholderCard: {
    background: TOKENS.white,
    borderRadius: 28,
    padding: 28,
    border: `1px solid ${TOKENS.lineSoft}`,
    boxShadow: TOKENS.shadow,
  },

  placeholderTag: {
    fontSize: 12,
    fontWeight: 800,
    color: TOKENS.orange,
    marginBottom: 8,
  },

  placeholderTitle: {
    margin: 0,
    fontSize: 28,
    fontWeight: 900,
    color: TOKENS.textStrong,
  },

  placeholderDesc: {
    marginTop: 10,
    fontSize: 14,
    color: TOKENS.textSoft,
    lineHeight: 1.8,
  },

  choiceOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    background: "rgba(15,23,42,0.42)",
  },

  choiceModal: {
    width: "min(520px, 100%)",
    borderRadius: 24,
    padding: 24,
    background: TOKENS.white,
    boxShadow: "0 24px 70px rgba(15,23,42,0.24)",
  },

  choiceModalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
  },
  choiceModalEyebrow: {
    color: TOKENS.orange,
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1.5,
  },
  choiceModalTitle: {
    margin: "8px 0 0",
    color: TOKENS.textStrong,
    fontSize: 24,
    fontWeight: 900,
  },
  choiceModalDesc: {
    margin: "8px 0 0",
    color: TOKENS.textSoft,
    fontSize: 13,
    lineHeight: 1.7,
  },
  choiceCloseButton: {
    width: 36,
    height: 36,
    border: `1px solid ${TOKENS.line}`,
    borderRadius: 10,
    background: TOKENS.white,
    color: TOKENS.textSoft,
    fontSize: 24,
    lineHeight: 1,
    cursor: "pointer",
  },
  choiceCards: { display: "grid", gap: 10, marginTop: 22 },
  choiceCard: {
    width: "100%",
    minHeight: 78,
    display: "flex",
    alignItems: "center",
    gap: 13,
    border: `1px solid ${TOKENS.line}`,
    borderRadius: 15,
    padding: "12px 14px",
    background: TOKENS.white,
    color: TOKENS.text,
    textAlign: "left",
    cursor: "pointer",
  },
  choiceIcon: {
    width: 46,
    height: 46,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    borderRadius: 13,
  },
  choiceCardText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  choiceArrow: { color: TOKENS.textMuted, fontSize: 28, lineHeight: 1 },

};
